import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { getApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInAnonymously,
} from "firebase/auth";
import {
  arrayUnion,
  collection,
  collectionGroup,
  documentId,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
} from "firebase/firestore";
import {
  getStorage,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCamera,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCloudUploadAlt,
  faHistory,
  faUsersGear,
  faImage,
  faInfoCircle,
  faPlus,
  faRedo,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { DoorScanPdfButton, useDoorSnapshotPdf } from "./DoorScanPdf";
import "./DoorScan.css";

const PREDICT_URL =
  "https://firechecked-model-server-813451531907.europe-west1.run.app/predict-door-parts";
const HINGE_PREDICT_PATH = "/predict-hinge-screw";
const EXTRACT_TEXT_PATH = "/extract-text";
const SCAN_COLLECTION = "z-scan-door";
const SCHEMA_VERSION = 6;
const MAX_EXTRAS = 20;
const MAX_EVIDENCE_BYTES = 120 * 1024 * 1024;
const MAX_RESULT_BYTES = 4 * 1024 * 1024;
const modelClient = axios.create({ timeout: 120000, withCredentials: false });
const MAX_INPUT_BYTES = 15 * 1024 * 1024;
const MAX_IMAGE_EDGE = 2560;
const SIDES = ["front", "back"];
const HINGES = ["hinge_top", "hinge_middle", "hinge_bottom"];
const PHOTO_KEYS = [...SIDES, ...HINGES, "certificate"];
const PHOTO_LABELS = {
  front: "Front of door",
  back: "Back of door",
  hinge_top: "Top hinge",
  hinge_middle: "Middle hinge",
  hinge_bottom: "Bottom hinge",
  certificate: "Certificate",
};
const labelFor = (side) => PHOTO_LABELS[side] || "Additional evidence";
const CERTIFICATE_REASONS = {
  not_found: "I can't find a label",
  not_accessible: "I can't reach it safely",
  deferred_to_firechecked: "Leave this for FireChecked",
};
const hingePhotoKeys = (count) =>
  count === 2 ? ["hinge_top", "hinge_bottom"] : HINGES;
export const requiredPhotoKeys = () => SIDES;
const certificationAnswered = (photos, decision) =>
  Boolean(
    photos.certificate ||
      (decision &&
        Object.prototype.hasOwnProperty.call(
          CERTIFICATE_REASONS,
          decision.status
        ))
  );
const nextCaptureKey = (photos, hingeCount, decision) =>
  [...SIDES, ...hingePhotoKeys(hingeCount)].find((key) => !photos[key]) ||
  (!certificationAnswered(photos, decision) ? "certificate" : null);
const captureGroup = (key) =>
  HINGES.includes(key) ? "hinges" : key === "certificate" ? "certificate" : key;
const GUIDE_COPY = {
  front: {
    title: "Front of the door",
    text: "Step back and include the top, both sides and threshold.",
    detail:
      "Keep the complete door and frame visible. Hold the phone upright and avoid glare.",
  },
  back: {
    title: "Back of the door",
    text: "Move to the other side and keep the complete door and frame visible.",
    detail:
      "Include the closer and any fitted locks. Do not put yourself at risk of being locked out.",
  },
  hinge_top: {
    title: "Top hinge",
    text: "One hinge only. A partly open door is fine.",
    detail:
      "Include the full hinge, its screws and any visible markings. Only photograph what you can reach safely.",
  },
  hinge_middle: {
    title: "Middle hinge",
    text: "Move to the middle hinge. Include the whole hinge and screws.",
    detail:
      "Do not count the screws as hinges. Choose the two-hinge route only if two is the total fitted.",
  },
  hinge_bottom: {
    title: "Bottom hinge",
    text: "Photograph the bottom hinge clearly, including its screws.",
    detail:
      "A hinge you cannot see or reach is not the same as a missing hinge. Ask FireChecked for help rather than guessing.",
  },
  certificate: {
    title: "Certification label",
    text: "Photograph the certification label. Keep every number readable. You can add it later.",
    detail:
      "Only check places you can already see and reach safely. No climbing, peeling labels or removing fittings. No label is not an automatic failure.",
  },
};
export const classNames = {
  hinge: "Hinge",
  keyless_exit: "Keyless Exit",
  yale_lock: "Yale Lock",
  night_latch: "Night Latch",
  mortice_lock: "Mortice Lock",
  external_lock: "External Lock",
  mortice_cover: "Mortice Cover",
  code_lock: "Code Lock",
  emergency_lock: "Emergency Lock",
  padlock: "Pad Lock",
  keep_shut: "Keep Shut",
  keep_locked: "Keep Locked",
  bolt_latch: "Bolt Latch",
  door_closer: "Door Closer",
  glass: "Glass",
  safety_glass: "Safety Glass",
  door_handle: "Door Handle",
  pull_handle: "Pull Handle",
  door_knob: "Door Knob",
  push_pad: "Push Pad",
  door_number: "Door Number",
  door_bell: "Door Bell",
  knocker: "Knocker",
  spyhole: "Spy Hole",
  letter_box: "Letter Box",
  signage: "Signage",
  fire_exit: "Fire Exit",
  keep_clear: "Keep Clear",
  drop_down_seal: "Drop Down Seal",
  ventilation: "Ventilation",
  door: "Door",
  door_damage: "Door Damage",
  frame_damage: "Frame Damage",
  door_latch: "Door Latch",
  normal: "Normal",
  missing: "Missing",
  reflection: "Reflection",
  loose: "Loose",
  screw: "Screw",
  missing_screw: "Missing screw",
  loose_screw: "Loose screw",
};
export const categoryMap = {
  structure: {
    name: "Door & frame",
    classes: ["door", "door_damage", "frame_damage"],
  },
  hinges: { name: "Hinges", classes: ["hinge"] },
  locks: {
    name: "Locks & latches",
    classes: [
      "keyless_exit",
      "yale_lock",
      "night_latch",
      "mortice_lock",
      "external_lock",
      "mortice_cover",
      "code_lock",
      "emergency_lock",
      "padlock",
      "bolt_latch",
      "door_latch",
    ],
  },
  closers: { name: "Door closers", classes: ["door_closer"] },
  glazing: { name: "Glazing", classes: ["glass", "safety_glass"] },
  handles: {
    name: "Handles & exit hardware",
    classes: ["door_handle", "pull_handle", "door_knob", "push_pad"],
  },
  signs: {
    name: "Signs & instructions",
    classes: ["keep_shut", "keep_locked", "signage", "fire_exit", "keep_clear"],
  },
  fittings: {
    name: "Other fittings",
    classes: [
      "door_number",
      "door_bell",
      "knocker",
      "spyhole",
      "letter_box",
      "ventilation",
    ],
  },
  seals: { name: "Seals", classes: ["drop_down_seal"] },
  observations: {
    name: "Model observations",
    classes: ["normal", "missing", "reflection", "loose"],
  },
};
export const getCategoryForClass = (cls) => {
  for (const cat of Object.values(categoryMap)) {
    if (cat.classes.includes(cls)) return cat;
  }
  return null;
};
const classLabel = (cls) =>
  Object.prototype.hasOwnProperty.call(classNames, cls)
    ? classNames[cls]
    : String(cls || "Unlabelled part")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
const validConfidence = (value) =>
  Number.isFinite(value) && value >= 0 && value <= 1;
const CONFIDENCE_STYLES = {
  high: {
    band: "high",
    colour: "#15803d",
    text: "#ffffff",
    range: "Above 75%",
  },
  medium: {
    band: "medium",
    colour: "#facc15",
    text: "#422006",
    range: "50% to 75%",
  },
  low: { band: "low", colour: "#dc2626", text: "#ffffff", range: "Below 50%" },
  unknown: {
    band: "unknown",
    colour: "#94a3b8",
    text: "#0f172a",
    range: "Not supplied",
  },
};
export function getConfidenceStyle(confidence) {
  if (!validConfidence(confidence)) return CONFIDENCE_STYLES.unknown;
  if (confidence > 0.75) return CONFIDENCE_STYLES.high;
  if (confidence >= 0.5) return CONFIDENCE_STYLES.medium;
  return CONFIDENCE_STYLES.low;
}
function confidenceCounts(steps) {
  return steps.reduce(
    (counts, step) => {
      const confidence = step?.region?.confidence;
      if (!validConfidence(confidence)) {
        counts.review += 1;
      } else if (confidence > 0.75) {
        counts.fine += 1;
      } else if (confidence >= 0.5) {
        counts.attention += 1;
      } else {
        counts.review += 1;
      }
      return counts;
    },
    { fine: 0, attention: 0, review: 0 }
  );
}
function NfcArt() {
  return (
    <div className="nfc-art" aria-hidden="true">
      <div className="nfc-card">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.65"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M8 8a6 6 0 0 1 0 8M12 5a10 10 0 0 1 0 14M16 2a14 14 0 0 1 0 20" />
          <circle cx="4" cy="12" r="1" />
        </svg>
      </div>
    </div>
  );
}
function confidenceSummary(regions) {
  const summary = Object.create(null);
  for (const region of regions) {
    if (
      !region ||
      typeof region.cls !== "string" ||
      !validConfidence(region.confidence)
    )
      continue;
    const previous = summary[region.cls];
    summary[region.cls] = {
      maximum: previous
        ? Math.max(previous.maximum, region.confidence)
        : region.confidence,
      minimum: previous
        ? Math.min(previous.minimum, region.confidence)
        : region.confidence,
      detections: (previous?.detections || 0) + 1,
    };
  }
  return Object.fromEntries(Object.entries(summary));
}
function cameraScore(device) {
  const text = String(device.label || "").toLowerCase();
  if (/front|user|selfie|facetime/.test(text)) return -100;
  if (/ultra[\s_-]*wide|ultrawide|macro|telephoto|tele\b|depth/.test(text))
    return -60;
  if (/triple|dual|fusion|virtual/.test(text)) return -20;
  let score = /back|rear|environment/.test(text) ? 40 : 0;
  if (/\bwide\b/.test(text)) score += 30;
  if (/main|primary|standard|1[.,]0?x|1x/.test(text)) score += 50;
  return score;
}
async function resetCameraZoom(track) {
  let capabilities = {};
  try {
    capabilities = track?.getCapabilities?.() || {};
  } catch (_) {}
  const range = capabilities.zoom;
  const supported = Boolean(range && range.min <= 1 && range.max >= 1);
  let applied = false;
  if (supported && track.applyConstraints) {
    let existing = {};
    try {
      existing = track.getConstraints?.() || {};
    } catch (_) {}
    try {
      await track.applyConstraints({
        ...existing,
        zoom: { exact: 1 },
        advanced: [{ zoom: 1 }],
      });
      applied = true;
    } catch (_) {
      try {
        await track.applyConstraints({ ...existing, advanced: [{ zoom: 1 }] });
        applied = true;
      } catch (_) {}
    }
  }
  let settings = {};
  try {
    settings = track?.getSettings?.() || {};
  } catch (_) {}
  return {
    supported,
    applied,
    reported: Number.isFinite(settings.zoom) ? settings.zoom : null,
  };
}
function usePreview(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);
  return url;
}
async function createJpeg(source, side) {
  const width = source.videoWidth || source.naturalWidth;
  const height = source.videoHeight || source.naturalHeight;
  if (!width || !height) throw new Error("The camera is not ready yet.");
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  try {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to process this photo.");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      source,
      0,
      0,
      width,
      height,
      0,
      0,
      canvas.width,
      canvas.height
    );
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (value) =>
          value
            ? resolve(value)
            : reject(new Error("Photo capture failed. Please retry.")),
        "image/jpeg",
        0.92
      );
    });
    return new File([blob], `${side}-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}
async function prepareSelectedPhoto(file, side) {
  if (!file.size || file.size > MAX_INPUT_BYTES) {
    throw new Error("Choose a photo smaller than 15 MB.");
  }
  if (file.type && !/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type)) {
    throw new Error("Choose a JPEG, PNG, WebP, HEIC or HEIF photo.");
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  let timer;
  try {
    await new Promise((resolve, reject) => {
      timer = window.setTimeout(
        () => reject(new Error("This photo took too long to open.")),
        15000
      );
      image.onload = resolve;
      image.onerror = () =>
        reject(
          new Error(
            "This phone cannot open that format here. Try a JPEG or take a new photo."
          )
        );
      image.src = url;
    });
    if (image.naturalWidth * image.naturalHeight > 60000000) {
      throw new Error(
        "This photo is too large. Choose a lower-resolution photo."
      );
    }
    return await createJpeg(image, side);
  } finally {
    window.clearTimeout(timer);
    image.onload = null;
    image.onerror = null;
    image.src = "";
    URL.revokeObjectURL(url);
  }
}
function cameraErrorMessage(error) {
  switch (error?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Camera access is not allowed. Upload a photo from your device instead, or allow access in settings.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No suitable camera was found. You can still upload a photo from your device.";
    case "NotReadableError":
      return "The camera could not start. Upload a photo from your device, or close other apps using the camera.";
    default:
      return error?.message || "Unable to open the camera. Please try again.";
  }
}
function CameraWindow({
  side,
  nextSide,
  initialPhoto = null,
  initialMetadata = null,
  onAccept,
  onClose,
  photos,
  hingeCount,
  certificateDecision,
  onSelectSide,
  onHingeCount,
  onCertificateChoice,
  entryRect,
  getReturnRect,
}) {
  const dialogRef = useRef(null);
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const nativeInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const stopRef = useRef(() => {});
  const aliveRef = useRef(false);
  const operationRef = useRef(0);
  const busyRef = useRef(false);
  const acceptedRef = useRef(false);
  const motionRef = useRef(null),
    closingRef = useRef(false);
  const [cameraPhase, setCameraPhase] = useState("entering");
  const returnRectRef = useRef(getReturnRect);
  returnRectRef.current = getReturnRect;
  const [candidate, setCandidate] = useState(initialPhoto);
  const [candidateSource, setCandidateSource] = useState(
    initialPhoto ? "device" : "camera"
  );
  const [status, setStatus] = useState(initialPhoto ? "review" : "opening");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const [showTips, setShowTips] = useState(false);
  const [portrait, setPortrait] = useState(true);
  const [zoom, setZoom] = useState({
    supported: false,
    applied: false,
    reported: null,
  });
  const [frameBounds, setFrameBounds] = useState(null);
  const candidateMetadata = useRef(initialMetadata);
  const candidateUrl = usePreview(candidate);
  const label = labelFor(side);
  const guide = GUIDE_COPY[side] || {
    title: label,
    text: "Keep the relevant detail clear and in focus.",
    detail: "Only collect evidence you can reach safely.",
  };
  useEffect(() => {
    operationRef.current += 1;
    acceptedRef.current = false;
    busyRef.current = false;
    setBusy(false);
    candidateMetadata.current = initialMetadata;
    setCandidate(initialPhoto);
    setCandidateSource(initialPhoto ? "device" : "camera");
    setShowTips(false);
    setMessage("");
  }, [side, initialPhoto, initialMetadata]);
  useEffect(() => {
    aliveRef.current = true;
    const dialog = dialogRef.current;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    Object.assign(body.style, {
      position: "fixed",
      top: `-${scrollY}px`,
      width: "100%",
      overflow: "hidden",
    });
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    const orientation = window.matchMedia(
      "(pointer: coarse) and (orientation: landscape) and (max-height: 600px)"
    );
    const updateOrientation = () => setPortrait(!orientation.matches);
    updateOrientation();
    if (orientation.addEventListener)
      orientation.addEventListener("change", updateOrientation);
    else orientation.addListener(updateOrientation);
    return () => {
      if (orientation.removeEventListener)
        orientation.removeEventListener("change", updateOrientation);
      else orientation.removeListener(updateOrientation);
      aliveRef.current = false;
      operationRef.current += 1;
      stopRef.current();
      if (dialog.open && typeof dialog.close === "function") dialog.close();
      Object.assign(body.style, previous);
      window.scrollTo(0, scrollY);
    };
  }, []);
  useEffect(() => {
    let frame = 0,
      cancelled = false;
    frame = window.requestAnimationFrame(() => {
      const target = dialogRef.current?.querySelector(".ds-solid-gear");
      if (!target) {
        setCameraPhase("live");
        return;
      }
      const to = target.getBoundingClientRect();
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      if (!entryRect?.width || !to.width || reduced || !target.animate) {
        setCameraPhase("live");
        return;
      }
      const animation = target.animate(
        [
          {
            transform: `translate(${entryRect.left - to.left}px, ${
              entryRect.top - to.top
            }px) scale(${entryRect.width / to.width})`,
          },
          { transform: "translate(0,0) scale(1)" },
        ],
        { duration: 560, easing: "cubic-bezier(.4,0,.2,1)", fill: "both" }
      );
      motionRef.current = animation;
      animation.finished
        .catch(() => {})
        .then(() => {
          if (!cancelled && !closingRef.current) {
            animation.cancel();
            setCameraPhase("live");
          }
        });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      motionRef.current?.cancel();
    };
  }, []);
  useEffect(() => {
    if (candidate) return undefined;
    let cancelled = false;
    let stream = null;
    let timer;
    const video = videoRef.current;
    const stop = () => {
      cancelled = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
      if (video?.srcObject === stream) video.srcObject = null;
    };
    stopRef.current = stop;
    const fail = (text, nextStatus = "error") => {
      if (cancelled) return;
      stop();
      operationRef.current += 1;
      busyRef.current = false;
      setBusy(false);
      setStatus(nextStatus);
      setMessage(text);
    };
    const pause = () =>
      fail("Camera paused. Tap Resume camera to continue.", "paused");
    const onVisibility = () => {
      if (document.hidden) pause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", pause);
    setStatus("opening");
    setMessage("");
    setZoom({ supported: false, applied: false, reported: null });
    async function start() {
      try {
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            "Live camera is unavailable here. Upload a photo from your device instead."
          );
        }
        timer = window.setTimeout(() => {
          fail(
            "Camera access is still pending. Check permissions, then try again."
          );
        }, 20000);
        const request = (deviceId) =>
          navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              ...(deviceId
                ? { deviceId: { exact: deviceId } }
                : { facingMode: { ideal: "environment" } }),
              width: { ideal: 1920 },
              resizeMode: { ideal: "none" },
            },
          });
        let incoming = await request("");
        if (cancelled) {
          incoming.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = incoming;
        let cameras = [];
        try {
          const all = await navigator.mediaDevices.enumerateDevices();
          cameras = all.filter(
            (device) => device.kind === "videoinput" && device.deviceId
          );
        } catch (_) {}
        if (cancelled) {
          incoming.getTracks().forEach((track) => track.stop());
          return;
        }
        const current =
          stream.getVideoTracks()[0]?.getSettings?.().deviceId || "";
        const best = [...cameras].sort(
          (a, b) => cameraScore(b) - cameraScore(a)
        )[0];
        if (best && cameraScore(best) >= 40 && best.deviceId !== current) {
          stream.getTracks().forEach((track) => track.stop());
          try {
            incoming = await request(best.deviceId);
          } catch (failure) {
            if (cancelled) return;
            if (
              ![
                "NotFoundError",
                "OverconstrainedError",
                "NotReadableError",
              ].includes(failure.name)
            )
              throw failure;
            incoming = await request("");
          }
          if (cancelled) {
            incoming.getTracks().forEach((track) => track.stop());
            return;
          }
          stream = incoming;
        }
        const track = stream.getVideoTracks()[0];
        const zoomResult = await resetCameraZoom(track);
        if (cancelled) {
          stream.getTracks().forEach((item) => item.stop());
          return;
        }
        setZoom(zoomResult);
        track.onended = () =>
          fail("Camera access was interrupted. Please try again.");
        video.srcObject = stream;
        await video.play();
        if (cancelled) return;
        if (!video.videoWidth || !video.videoHeight) {
          throw new Error("No camera image was received. Please try again.");
        }
        window.clearTimeout(timer);
        setStatus("live");
      } catch (error) {
        if (!cancelled) fail(cameraErrorMessage(error));
      }
    }
    start();
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", pause);
    };
  }, [candidate, retry]);
  useEffect(() => {
    if (candidate) return undefined;
    const stage = stageRef.current;
    const video = videoRef.current;
    const measure = () => {
      if (!stage || !video?.videoWidth || !video.videoHeight) {
        setFrameBounds(null);
        return;
      }
      const scale = Math.min(
        stage.clientWidth / video.videoWidth,
        stage.clientHeight / video.videoHeight
      );
      const width = video.videoWidth * scale;
      const height = video.videoHeight * scale;
      setFrameBounds({
        left: (stage.clientWidth - width) / 2,
        top: (stage.clientHeight - height) / 2,
        width,
        height,
      });
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (stage) observer?.observe(stage);
    video?.addEventListener("resize", measure);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      video?.removeEventListener("resize", measure);
      window.removeEventListener("resize", measure);
    };
  }, [status, candidate]);
  const close = async () => {
    if (busyRef.current || closingRef.current) return;
    if (candidate && !window.confirm("Discard this unconfirmed photo?")) return;
    closingRef.current = true;
    operationRef.current += 1;
    setCameraPhase("leaving");
    motionRef.current?.cancel();
    const target = dialogRef.current?.querySelector(".ds-solid-gear");
    const from = target?.getBoundingClientRect(),
      to = returnRectRef.current?.();
    if (
      target?.animate &&
      from?.width &&
      to?.width &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      const animation = target.animate(
        [
          { transform: "translate(0,0) scale(1)" },
          {
            transform: `translate(${to.left - from.left}px, ${
              to.top - from.top
            }px) scale(${to.width / from.width})`,
          },
        ],
        { duration: 460, easing: "cubic-bezier(.4,0,.2,1)", fill: "forwards" }
      );
      motionRef.current = animation;
      await animation.finished.catch(() => {});
    }
    stopRef.current();
    if (aliveRef.current) onClose();
  };
  const processPhoto = async (makeFile, source = "camera", metadata = null) => {
    if (busyRef.current || closingRef.current) return;
    busyRef.current = true;
    const operation = ++operationRef.current;
    setBusy(true);
    setMessage("");
    try {
      const file = await makeFile();
      if (aliveRef.current && operation === operationRef.current) {
        candidateMetadata.current = metadata;
        setCandidateSource(source);
        setCandidate(file);
      }
    } catch (error) {
      if (aliveRef.current && operation === operationRef.current) {
        setMessage(
          error.message || "Unable to process the photo. Please try again."
        );
      }
    } finally {
      if (aliveRef.current && operation === operationRef.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  const capture = () => {
    if (
      status !== "live" ||
      !portrait ||
      candidate ||
      busyRef.current ||
      closingRef.current ||
      cameraPhase !== "live"
    )
      return;
    const video = videoRef.current;
    const track = video?.srcObject?.getVideoTracks()[0];
    const settings = track?.getSettings?.() || {};
    processPhoto(() => createJpeg(video, side), "camera", {
      source: "camera",
      fullFrame: true,
      appCrop: false,
      requestedZoom: 1,
      reportedZoom: Number.isFinite(settings.zoom) ? settings.zoom : null,
      cameraLabel: track?.label || "",
      sourceWidth: video.videoWidth,
      sourceHeight: video.videoHeight,
    });
  };
  const openPicker = (inputRef) => {
    if (busyRef.current) return;
    stopRef.current();
    setStatus("paused");
    setMessage(
      candidate ? "" : "Choose a photo from your device, or resume the camera."
    );
    inputRef.current.value = "";
    inputRef.current.click();
  };
  const selectFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file)
      processPhoto(() => prepareSelectedPhoto(file, side), "device", {
        source: "device",
        fullFrame: true,
        appCrop: false,
        originalFile: file,
      });
  };
  const retake = () => {
    if (candidateSource === "device") {
      openPicker(libraryInputRef);
      return;
    }
    setMessage("");
    setCandidate(null);
  };
  const zoomConfirmed =
    zoom.reported !== null && Math.abs(zoom.reported - 1) < 0.01;
  const zoomDescription = zoomConfirmed
    ? "The camera reports 1x zoom. The complete image is shown."
    : "1x is requested automatically. This camera has not confirmed that setting; the app does not crop or digitally zoom.";
  return createPortal(
    <dialog
      ref={dialogRef}
      className="door-scan-camera"
      data-review={Boolean(candidate)}
      data-motion={cameraPhase}
      data-guide={
        HINGES.includes(side)
          ? "hinge"
          : side === "certificate"
          ? "certificate"
          : "door"
      }
      aria-label={`${label} camera`}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <header className="ds-camera-header">
        <button
          type="button"
          className="ds-icon-button"
          onClick={close}
          aria-label="Close camera"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="ds-camera-heading">
          <h2>{label}</h2>
          <div className="ds-camera-subtitle">
            <span>
              {candidate
                ? "REVIEW YOUR PHOTO"
                : HINGES.includes(side)
                ? "ONE HINGE AT A TIME"
                : side === "certificate"
                ? "CERTIFICATION"
                : "FULL FRAME"}
            </span>
            {!candidate && status === "live" && (
              <span
                className="ds-camera-default-zoom"
                title={zoomDescription}
                aria-label={zoomDescription}
              >
                {zoomConfirmed ? "1\u00d7" : "1\u00d7 requested"}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="ds-icon-button"
          disabled={Boolean(candidate)}
          aria-label="Photo instructions"
          aria-expanded={showTips}
          aria-controls="ds-camera-tips"
          onClick={() => setShowTips((value) => !value)}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </button>
      </header>

      <div ref={stageRef} className="ds-camera-stage">
        <video
          ref={videoRef}
          className="ds-live-video"
          hidden={Boolean(candidate)}
          autoPlay
          muted
          playsInline
          disablePictureInPicture
          aria-label={`Live view for the ${side} door photo`}
        />
        {candidate && candidateUrl && (
          <img
            className="ds-camera-review"
            src={candidateUrl}
            alt={`${label} photo for review`}
          />
        )}
        {!candidate && (
          <>
            {status === "live" && (
              <CameraGuideOverlay key={side} side={side} bounds={frameBounds} />
            )}
            {status !== "live" && (
              <div
                className="ds-camera-state"
                role={status === "opening" ? "status" : "alert"}
              >
                <FontAwesomeIcon
                  icon={status === "opening" ? faSpinner : faInfoCircle}
                  spin={status === "opening"}
                />
                <strong>
                  {status === "opening"
                    ? "Opening your camera"
                    : status === "paused"
                    ? "Camera paused"
                    : "Camera unavailable"}
                </strong>
                <p>
                  {status === "opening"
                    ? "Allow camera access, or upload a photo from your device."
                    : message}
                </p>
                <button
                  type="button"
                  className="ds-button ds-button-primary"
                  disabled={busy}
                  onClick={() => openPicker(libraryInputRef)}
                >
                  <FontAwesomeIcon icon={faImage} /> Upload from device
                </button>
                {status !== "opening" && (
                  <button
                    type="button"
                    className="ds-button ds-button-dark"
                    disabled={busy}
                    onClick={() => setRetry((value) => value + 1)}
                  >
                    {status === "paused" ? "Resume camera" : "Try camera again"}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {busy && (
          <div className="ds-processing" role="status">
            <FontAwesomeIcon icon={faSpinner} spin /> Preparing photo...
          </div>
        )}
      </div>

      <footer className="ds-camera-dock">
        <div className="ds-camera-wheel-persistent">
          <CaptureWheel
            compact
            activeKey={side}
            photos={photos || {}}
            hingeCount={hingeCount}
            certificateDecision={certificateDecision}
            disabled={busy || Boolean(candidate) || cameraPhase !== "live"}
            cameraMode
            shutterDisabled={status !== "live" || !portrait || busy}
            onSelect={onSelectSide}
            onAction={capture}
            actionLabel={`Take ${labelFor(side).toLowerCase()} photo`}
          />
        </div>
        {candidate ? (
          <section
            className="ds-final-card"
            aria-label={`${label} photo confirmation`}
          >
            <div className="ds-final-card-heading">
              <span className="ds-final-card-check" aria-hidden="true">
                <FontAwesomeIcon icon={faCheck} />
              </span>
              <div>
                <strong>{label} photo ready</strong>
                <p className="ds-review-caption">
                  {SIDES.includes(side)
                    ? "Check the whole door and frame before using this photo."
                    : side === "certificate"
                    ? "Check that the label and every number are readable."
                    : "Check that the full hinge and its screws are in focus."}
                </p>
              </div>
            </div>
            <div className="ds-review-actions">
              <button
                type="button"
                className="ds-button ds-button-dark"
                disabled={busy}
                onClick={retake}
              >
                <FontAwesomeIcon icon={faRedo} />{" "}
                {candidateSource === "device" ? "Choose another" : "Retake"}
              </button>
              <button
                type="button"
                className="ds-button ds-button-primary"
                disabled={busy}
                onClick={() => {
                  if (acceptedRef.current || busyRef.current) return;
                  acceptedRef.current = true;
                  try {
                    onAccept(
                      candidate,
                      side,
                      candidateSource,
                      candidateMetadata.current
                    );
                  } catch (failure) {
                    acceptedRef.current = false;
                    setMessage(friendlyError(failure));
                  }
                }}
              >
                <FontAwesomeIcon icon={faCheck} /> Use photo
              </button>
            </div>
            <p className="ds-dock-caption">
              {nextSide
                ? `Next: ${labelFor(nextSide).toLowerCase()}`
                : "Next: analyse and save your evidence"}
            </p>
            {message && (
              <p className="ds-camera-inline-error" role="alert">
                {message}
              </p>
            )}
          </section>
        ) : (
          <>
            <div
              id="ds-camera-tips"
              className="ds-live-instructions"
              hidden={!showTips}
            >
              <strong>PHOTO GUIDE</strong>
              <p>{guide.text}</p>
              <p>{guide.detail}</p>
            </div>
            <div className="ds-live-actions">
              <button
                type="button"
                className="ds-source-button"
                disabled={busy}
                onClick={() => openPicker(libraryInputRef)}
              >
                <FontAwesomeIcon icon={faImage} />
                <span>Upload from device</span>
              </button>
              <div className="ds-camera-wheel-space" aria-hidden="true" />
              <button
                type="button"
                className="ds-source-button"
                disabled={busy}
                onClick={() => openPicker(nativeInputRef)}
              >
                <FontAwesomeIcon icon={faCamera} />
                <span>Phone camera</span>
              </button>
            </div>
            <div className="ds-camera-extras">
              {photos?.front && photos?.back && (
                <button type="button" disabled={busy} onClick={close}>
                  Finish capture &amp; save
                </button>
              )}
              {HINGES.includes(side) && (
                <button type="button" disabled={busy} onClick={onHingeCount}>
                  {hingeCount === 2
                    ? "2 hinges selected - change"
                    : "Only two hinges on this door?"}
                </button>
              )}
              {side === "certificate" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onCertificateChoice}
                >
                  Can't photograph the label?
                </button>
              )}
            </div>
            <p className="ds-dock-caption">
              {busy
                ? "Preparing your photo..."
                : !portrait
                ? "Hold your phone upright to take this photo."
                : `Tap the shutter for the ${side} photo`}
            </p>
            {status === "live" && message && (
              <p className="ds-camera-inline-error" role="alert">
                {message}
              </p>
            )}
          </>
        )}
      </footer>
      <input
        ref={nativeInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={selectFile}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={selectFile}
      />
    </dialog>,
    document.body
  );
}
function newId() {
  const browserCrypto = typeof window !== "undefined" ? window.crypto : null;
  if (browserCrypto && typeof browserCrypto.randomUUID === "function") {
    return browserCrypto.randomUUID();
  }
  if (!browserCrypto || typeof browserCrypto.getRandomValues !== "function") {
    throw new Error(
      "Secure random number generation is unavailable on this device."
    );
  }
  const bytes = new Uint8Array(16);
  browserCrypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
}
const guestRequests = new WeakMap();
async function ensureScanOwner(auth, allowGuestScans) {
  if (auth.currentUser) {
    if (auth.currentUser.isAnonymous && !allowGuestScans)
      throw new Error(
        "Sign in to a registered account to save scans and view results."
      );
    return auth.currentUser;
  }
  if (!allowGuestScans)
    throw new Error("Sign in to save scans and view results.");
  if (guestRequests.has(auth)) return guestRequests.get(auth);
  const request = (async () => {
    if (auth.currentUser) return auth.currentUser;
    await setPersistence(auth, browserLocalPersistence);
    if (auth.currentUser) return auth.currentUser;
    const credential = await signInAnonymously(auth);
    return credential.user;
  })();
  guestRequests.set(auth, request);
  try {
    return await request;
  } finally {
    if (guestRequests.get(auth) === request) guestRequests.delete(auth);
  }
}
function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Unable to read this photo."));
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.readAsDataURL(file);
  });
}
async function predictPhoto(file, url, signal, transformResponse, context) {
  const image = await readBase64(file);
  if (signal.aborted) throw new Error("Request cancelled.");
  const response = await modelClient.post(
    url,
    { image },
    {
      signal,
      headers: { "Content-Type": "application/json", Authorization: null },
    }
  );
  const data = transformResponse
    ? await transformResponse(response.data, context)
    : response.data;
  if (data?.error) throw new Error(String(data.error));
  if (
    !data ||
    !Array.isArray(data.regions) ||
    !data.counts ||
    typeof data.counts !== "object" ||
    Array.isArray(data.counts)
  ) {
    throw new Error("The model response must include counts and regions.");
  }
  const counts = Object.fromEntries(
    Object.entries(data.counts).map(([name, count]) => {
      if (!Number.isInteger(count) || count < 0)
        throw new Error("The model returned invalid part counts.");
      return [normaliseClassId(name), count];
    })
  );
  const normalisedRegions = data.regions.map((region) => ({
    ...region,
    cls: normaliseClassId(region.cls),
  }));
  const regionsJson = JSON.stringify(normalisedRegions);
  if (
    new Blob([regionsJson]).size > MAX_RESULT_BYTES ||
    new Blob([JSON.stringify(counts)]).size > 64000
  ) {
    throw new Error(
      "The result is too large to save safely. No existing records were changed."
    );
  }
  const imageSize =
    data.image_size &&
    Number.isInteger(data.image_size.width) &&
    Number.isInteger(data.image_size.height) &&
    data.image_size.width > 0 &&
    data.image_size.height > 0
      ? data.image_size
      : null;
  if (data.coordinate_space && data.coordinate_space !== "normalized") {
    throw new Error(
      "Expected normalized annotation coordinates. Check the model response."
    );
  }
  return {
    counts,
    regions: normalisedRegions,
    regionsJson,
    confidenceByClass: confidenceSummary(normalisedRegions),
    imageSize,
    annotationFormat: "normalized-v1",
  };
}
function resultChunks(text) {
  const chunks = [];
  for (let start = 0; start < text.length; ) {
    let end = Math.min(start + 100000, text.length);
    const code = text.charCodeAt(end - 1);
    if (end < text.length && code >= 0xd800 && code <= 0xdbff) end -= 1;
    chunks.push(text.slice(start, end));
    start = end;
  }
  return chunks;
}
function friendlyError(error) {
  const code = String(error?.code || "");
  if (
    code === "auth/operation-not-allowed" ||
    code === "auth/admin-restricted-operation"
  )
    return "Enable Anonymous sign-in in Firebase Authentication, or sign in to your account.";
  if (code === "auth/web-storage-unsupported")
    return "Persistent browser storage is unavailable. Sign in to an account to keep your results.";
  if (code === "auth/too-many-requests")
    return "Too many guest sessions were requested. Please wait or sign in to your account.";
  if (code.includes("permission-denied") || code.includes("unauthorized"))
    return "Access is not permitted. Check sign-in and the Firebase rules for schema version 6 and the evidence paths.";
  if (code.includes("failed-precondition"))
    return "The scan could not be saved. Check the Firestore configuration and try again.";
  if (
    code.includes("storage/bucket-not-found") ||
    code.includes("storage/no-default-bucket")
  )
    return "Configure a Firebase Storage bucket for the existing app.";
  if (code.includes("unavailable"))
    return "The connection is unavailable. Your accepted photos are still here; try again.";
  if (error?.response?.data?.error) return String(error.response.data.error);
  if (error?.code === "ERR_NETWORK")
    return "The model could not be reached. Check the connection and API CORS settings.";
  if (error?.code === "ECONNABORTED")
    return "The model timed out. Try again when the connection is available.";
  return error?.message || "Something went wrong. Please try again.";
}
function displayDate(value) {
  if (!value) return "Not saved yet";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime())
    ? "Saved scan"
    : new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        year: "numeric",
      }).format(date);
}
function Sheet({ title, children, onClose, initialScrollTop = 0 }) {
  const dialog = useRef(null);
  const scrollRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    if (typeof element.showModal === "function") element.showModal();
    else element.setAttribute("open", "");
    const restoreFrame = window.requestAnimationFrame(() => {
      if (scrollRef.current && Number.isFinite(initialScrollTop)) {
        scrollRef.current.scrollTop = Math.max(0, initialScrollTop);
      }
    });
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const controls = [
        ...element.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]'
        ),
      ].filter((node) => node.getClientRects().length);
      const first = controls[0],
        last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    element.addEventListener("keydown", onKey);
    element.querySelector("button")?.focus();
    return () => {
      window.cancelAnimationFrame(restoreFrame);
      element.removeEventListener("keydown", onKey);
      if (element.open && typeof element.close === "function") element.close();
      previous?.focus?.();
    };
  }, [initialScrollTop]);
  return createPortal(
    <dialog
      ref={dialog}
      className="ds-sheet"
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header>
        <h2>{title}</h2>
        <button
          type="button"
          className="ds-icon-button"
          aria-label="Close information"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </header>
      <div ref={scrollRef} className="ds-sheet-scroll">
        {children}
      </div>
    </dialog>,
    document.body
  );
}
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
function fitImage(container, image) {
  if (!container.width || !container.height || !image.width || !image.height)
    return null;
  const scale = Math.min(
    container.width / image.width,
    container.height / image.height
  );
  return { width: image.width * scale, height: image.height * scale, scale };
}
function limitPhotoTransform(value, fit, container) {
  const zoom = clamp(Number.isFinite(value.zoom) ? value.zoom : 1, 1, 8);
  const edgeX = zoom > 1.001 ? container.width * 0.42 : 0;
  const edgeY = zoom > 1.001 ? container.height * 0.42 : 0;
  const maxX = fit
    ? Math.max(0, (fit.width * zoom - container.width) / 2) + edgeX
    : 0;
  const maxY = fit
    ? Math.max(0, (fit.height * zoom - container.height) / 2) + edgeY
    : 0;
  return {
    zoom,
    x: clamp(Number.isFinite(value.x) ? value.x : 0, -maxX, maxX),
    y: clamp(Number.isFinite(value.y) ? value.y : 0, -maxY, maxY),
  };
}
function regionGeometry(region, width, height) {
  if (!region || typeof region !== "object" || !width || !height) return null;
  if (
    region.type === "polygon" &&
    Array.isArray(region.points) &&
    region.points.length >= 3
  ) {
    if (
      !region.points.every(
        (p) =>
          Array.isArray(p) &&
          p.length >= 2 &&
          Number.isFinite(p[0]) &&
          Number.isFinite(p[1])
      )
    )
      return null;
    const points = region.points.map(([x, y]) => [
      clamp(x, 0, 1) * width,
      clamp(y, 0, 1) * height,
    ]);
    let left = width,
      top = height,
      right = 0,
      bottom = 0;
    for (const [x, y] of points) {
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x);
      bottom = Math.max(bottom, y);
    }
    if (right <= left || bottom <= top) return null;
    return {
      type: "polygon",
      points: points.map((p) => p.join(",")).join(" "),
      left,
      top,
      right,
      bottom,
    };
  }
  if (
    region.type === "box" &&
    [region.x, region.y, region.w, region.h].every(Number.isFinite) &&
    region.w > 0 &&
    region.h > 0
  ) {
    const left = clamp(region.x, 0, 1) * width;
    const top = clamp(region.y, 0, 1) * height;
    const right = clamp(region.x + region.w, 0, 1) * width;
    const bottom = clamp(region.y + region.h, 0, 1) * height;
    return right > left && bottom > top
      ? { type: "box", left, top, right, bottom }
      : null;
  }
  return null;
}
const PHOTO_MIN_ZOOM = 1;
const PHOTO_MAX_ZOOM = 5;
const TOUR_MAX_ZOOM = 5;
const TOUR_PART_TIME = 5000;
const NO_REGIONS = Object.freeze([]);
function annotationInsets(container, overlayTools) {
  const top = overlayTools ? 58 : 12;
  const bottom = overlayTools ? 70 : 12;
  return {
    left: 12,
    right: 12,
    top: Math.min(top, container.height * 0.24),
    bottom: Math.min(bottom, container.height * 0.29),
  };
}

function combinedRegionGeometry(regions, width, height) {
  let left = Infinity,
    top = Infinity,
    right = -Infinity,
    bottom = -Infinity;
  for (const region of Array.isArray(regions) ? regions : []) {
    const shape = regionGeometry(region, width, height);
    if (!shape) continue;
    left = Math.min(left, shape.left);
    top = Math.min(top, shape.top);
    right = Math.max(right, shape.right);
    bottom = Math.max(bottom, shape.bottom);
  }
  return right > left && bottom > top ? { left, top, right, bottom } : null;
}
function focusTransformForBox(box, image, container, overlayTools) {
  const fit = fitImage(container, image);
  if (!fit || !box) return { zoom: 1, x: 0, y: 0 };
  const inset = annotationInsets(container, overlayTools);
  const availableWidth = Math.max(
    1,
    container.width - inset.left - inset.right
  );
  const availableHeight = Math.max(
    1,
    container.height - inset.top - inset.bottom
  );
  const zoom = clamp(
    Math.min(
      (availableWidth * 0.7) / Math.max(1, (box.right - box.left) * fit.scale),
      (availableHeight * 0.72) / Math.max(1, (box.bottom - box.top) * fit.scale)
    ),
    PHOTO_MIN_ZOOM,
    TOUR_MAX_ZOOM
  );
  return limitPhotoTransform(
    {
      zoom,
      x:
        (inset.left - inset.right) / 2 -
        ((box.left + box.right - image.width) / 2) * fit.scale * zoom,
      y:
        (inset.top - inset.bottom) / 2 -
        ((box.top + box.bottom - image.height) / 2) * fit.scale * zoom,
    },
    fit,
    container
  );
}
export function annotationFocusTransform(
  region,
  image,
  container,
  overlayTools = false
) {
  return focusTransformForBox(
    regionGeometry(region, image.width, image.height),
    image,
    container,
    overlayTools
  );
}
export function groupFocusTransform(
  regions,
  image,
  container,
  overlayTools = false
) {
  return focusTransformForBox(
    combinedRegionGeometry(regions, image.width, image.height),
    image,
    container,
    overlayTools
  );
}
function AnnotationLayer({
  regions,
  image,
  fit,
  container,
  transform,
  selected,
  focused,
  overlayTools,
  onSelect,
}) {
  const geometry = useMemo(
    () =>
      regions
        .map((region, index) => ({
          region,
          index,
          shape: regionGeometry(region, image.width, image.height),
        }))
        .filter((entry) => entry.shape),
    [regions, image.width, image.height]
  );
  const scale = fit.scale * transform.zoom;
  const offsetX = container.width / 2 + transform.x - (image.width * scale) / 2;
  const offsetY =
    container.height / 2 + transform.y - (image.height * scale) / 2;
  const inset = annotationInsets(container, overlayTools);
  const projected = geometry
    .map((entry) => {
      const { shape, region } = entry;
      const box = {
        left: offsetX + shape.left * scale,
        top: offsetY + shape.top * scale,
        right: offsetX + shape.right * scale,
        bottom: offsetY + shape.bottom * scale,
      };
      const path =
        shape.type === "polygon"
          ? region.points
              .map(
                ([x, y], i) =>
                  `${i ? "L" : "M"}${
                    offsetX + clamp(x, 0, 1) * image.width * scale
                  },${offsetY + clamp(y, 0, 1) * image.height * scale}`
              )
              .join(" ") + " Z"
          : `M${box.left},${box.top}H${box.right}V${box.bottom}H${box.left}Z`;
      return {
        ...entry,
        box,
        path,
        label: region.displayLabel || classLabel(region.cls),
      };
    })
    .filter(
      ({ box }) =>
        box.right > 0 &&
        box.bottom > 0 &&
        box.left < container.width &&
        box.top < container.height
    );
  const active = projected.find((entry) => entry.index === selected);
  const visible = focused ? (active ? [active] : []) : projected;
  const overlap = (a, b) =>
    Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
    Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  const labels = [];
  const labelLimit = focused ? 1 : Math.min(6, visible.length);
  const candidates = [...visible].sort(
    (a, b) => Number(b.index === selected) - Number(a.index === selected)
  );
  for (const entry of candidates) {
    if (labels.length >= labelLimit) break;
    const { box, label, index } = entry;
    const isActive = index === selected;
    const fontSize = clamp(
      Math.min(container.width / 30, 9 + (box.right - box.left) / 24),
      11,
      13
    );
    const width = Math.min(
      isActive ? 154 : 132,
      container.width * (isActive ? 0.45 : 0.38),
      Math.max(56, label.length * fontSize * 0.64 + 24)
    );
    const height = fontSize + 13;
    const left = inset.left,
      right = Math.max(left, container.width - inset.right - width);
    const top = inset.top,
      bottom = Math.max(top, container.height - inset.bottom - height);
    const cx = (box.left + box.right) / 2,
      cy = (box.top + box.bottom) / 2;
    const gap = 3;
    const proposals = [
      {
        x: box.right + gap,
        y: cy - height / 2,
        side: "right",
      },
      {
        x: box.left - width - gap,
        y: cy - height / 2,
        side: "left",
      },
      {
        x: cx - width / 2,
        y: box.top - height - gap,
        side: "top",
      },
      {
        x: cx - width / 2,
        y: box.bottom + gap,
        side: "bottom",
      },
    ].map((p) => ({
      ...p,
      x: clamp(p.x, left, right),
      y: clamp(p.y, top, bottom),
    }));
    let best = null;
    for (const p of proposals) {
      const rect = {
        left: p.x,
        top: p.y,
        right: p.x + width,
        bottom: p.y + height,
      };
      const labelOverlap = labels.reduce(
        (total, placed) =>
          total +
          overlap(rect, {
            left: placed.x - 4,
            top: placed.y - 4,
            right: placed.x + placed.width + 4,
            bottom: placed.y + placed.height + 4,
          }),
        0
      );
      const partOverlap = overlap(rect, box);
      const labelCx = p.x + width / 2;
      const labelCy = p.y + height / 2;
      const edgeDistanceX =
        labelCx < box.left
          ? box.left - (p.x + width)
          : labelCx > box.right
          ? p.x - box.right
          : 0;
      const edgeDistanceY =
        labelCy < box.top
          ? box.top - (p.y + height)
          : labelCy > box.bottom
          ? p.y - box.bottom
          : 0;
      const edgeDistance = Math.hypot(
        Math.max(0, edgeDistanceX),
        Math.max(0, edgeDistanceY)
      );
      const score = partOverlap * 1000 + labelOverlap * 100 + edgeDistance;
      if (!best || score < best.score) {
        best = {
          ...p,
          score,
        };
      }
    }
    if (best) {
      labels.push({
        ...entry,
        ...best,
        width,
        height,
        fontSize,
      });
    }
  }
  return (
    <div
      className="ds-screen-annotations"
      data-focused={Boolean(focused && active)}
    >
      <svg
        className="ds-screen-geometry"
        viewBox={`0 0 ${container.width} ${container.height}`}
        preserveAspectRatio="none"
        aria-label="Detected door parts"
      >
        {focused && active && (
          <path
            className="ds-focus-shade"
            d={`M0,0H${container.width}V${container.height}H0Z ${active.path}`}
            fill="#10271d"
            fillOpacity=".40"
            fillRule="evenodd"
            pointerEvents="none"
          />
        )}
        {[...visible]
          .sort(
            (a, b) =>
              (b.box.right - b.box.left) * (b.box.bottom - b.box.top) -
              (a.box.right - a.box.left) * (a.box.bottom - a.box.top)
          )
          .map((entry) => {
            const isActive = entry.index === selected;
            const hasLabel = labels.some(
              (label) => label.index === entry.index
            );
            const confidenceStyle = { colour: "#287548" };
            return (
              <g
                key={entry.index}
                data-region-index={entry.index}
                className={`ds-screen-region ${isActive ? "is-selected" : ""}`}
                role="button"
                tabIndex={hasLabel ? -1 : 0}
                aria-label={entry.label}
                aria-pressed={isActive}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelect(entry.index);
                  }
                }}
              >
                <title>{entry.label}</title>

                {isActive && (
                  <path
                    d={entry.path}
                    className="ds-focus-halo"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="4.5"
                    pointerEvents="none"
                  />
                )}

                <path
                  d={entry.path}
                  className="ds-screen-part"
                  fill={confidenceStyle.colour}
                  fillOpacity="0.08"
                  stroke={confidenceStyle.colour}
                  strokeWidth={isActive ? 3 : 2}
                />

                {!hasLabel && !focused && (
                  <circle
                    cx={clamp(
                      (entry.box.left + entry.box.right) / 2,
                      5,
                      container.width - 5
                    )}
                    cy={clamp(
                      (entry.box.top + entry.box.bottom) / 2,
                      5,
                      container.height - 5
                    )}
                    r="4"
                    fill={confidenceStyle.colour}
                    stroke="#fff"
                    strokeWidth="1.5"
                  />
                )}
              </g>
            );
          })}
        {labels.map((entry) => {
          const confidenceStyle = { colour: "#287548" };
          return (
            <line
              key={entry.index}
              x1={entry.x + entry.width / 2}
              y1={entry.y + entry.height / 2}
              x2={clamp(
                entry.x + entry.width / 2,
                entry.box.left,
                entry.box.right
              )}
              y2={clamp(
                entry.y + entry.height / 2,
                entry.box.top,
                entry.box.bottom
              )}
              stroke={confidenceStyle.colour}
              strokeWidth="1"
              pointerEvents="none"
            />
          );
        })}
      </svg>
      {labels.map((entry) => (
        <button
          type="button"
          key={entry.index}
          data-region-index={entry.index}
          className={`ds-screen-label ${
            entry.index === selected ? "is-selected" : ""
          }`}
          style={{
            left: entry.x,
            top: entry.y,
            width: entry.width,
            height: entry.height,
            fontSize: entry.fontSize,
          }}
          aria-label={entry.label}
          aria-pressed={entry.index === selected}
          title={entry.label}
          onClick={(event) => {
            if (event.detail === 0) onSelect(entry.index);
          }}
        >
          <span className="ds-screen-label-dot" aria-hidden="true" />
          <span className="ds-screen-label-text">{entry.label}</span>
        </button>
      ))}
    </div>
  );
}
let scanZoomLockUsers = 0;
let releaseScanZoomLock = null;
function useScanPageZoomLock() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    scanZoomLockUsers += 1;
    if (scanZoomLockUsers === 1) {
      let viewport = document.querySelector('meta[name="viewport"]');
      const created = !viewport;
      if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
      }
      const previousContent = viewport.getAttribute("content");
      const settings = (previousContent || "width=device-width")
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(
          (part) =>
            part &&
            !/^(?:initial-scale|minimum-scale|maximum-scale|user-scalable)\s*=/i.test(
              part
            )
        );
      const lockedContent = [
        ...settings,
        "initial-scale=1",
        "minimum-scale=1",
        "maximum-scale=1",
        "user-scalable=no",
      ].join(", ");
      viewport.setAttribute("content", lockedContent);
      const preventZoom = (event) => {
        if (event.cancelable) event.preventDefault();
      };
      const preventPinch = (event) => {
        if (event.touches?.length > 1) preventZoom(event);
      };
      const preventWheelZoom = (event) => {
        if (event.ctrlKey || event.metaKey) preventZoom(event);
      };
      const preventKeyZoom = (event) => {
        if (
          (event.ctrlKey || event.metaKey) &&
          ["+", "=", "-", "0"].includes(event.key)
        )
          preventZoom(event);
      };
      let touchStart = null;
      let lastTap = null;
      const rememberTouch = (event) => {
        if (event.touches.length !== 1) {
          touchStart = null;
          lastTap = null;
          return;
        }
        const touch = event.touches[0];
        touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      };
      const preventDoubleTapZoom = (event) => {
        if (event.touches.length || !touchStart || !event.changedTouches.length)
          return;
        const touch = event.changedTouches[0];
        const now = Date.now();
        const tap =
          now - touchStart.time < 350 &&
          Math.hypot(
            touch.clientX - touchStart.x,
            touch.clientY - touchStart.y
          ) < 12;
        if (
          tap &&
          lastTap &&
          now - lastTap.time < 350 &&
          Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < 24
        ) {
          preventZoom(event);
          lastTap = null;
        } else
          lastTap = tap
            ? { x: touch.clientX, y: touch.clientY, time: now }
            : null;
        touchStart = null;
      };
      const listeners = [
        ["touchstart", rememberTouch],
        ["touchstart", preventPinch],
        ["touchmove", preventPinch],
        ["touchend", preventDoubleTapZoom],
        ["gesturestart", preventZoom],
        ["gesturechange", preventZoom],
        ["gestureend", preventZoom],
        ["wheel", preventWheelZoom],
        ["keydown", preventKeyZoom],
      ];
      const options = { passive: false, capture: false };
      listeners.forEach(([name, handler]) =>
        document.addEventListener(name, handler, options)
      );
      releaseScanZoomLock = () => {
        listeners.forEach(([name, handler]) =>
          document.removeEventListener(name, handler, options)
        );
        if (viewport.getAttribute("content") === lockedContent) {
          if (created) viewport.remove();
          else if (previousContent === null)
            viewport.removeAttribute("content");
          else viewport.setAttribute("content", previousContent);
        }
      };
    }
    return () => {
      scanZoomLockUsers = Math.max(0, scanZoomLockUsers - 1);
      if (!scanZoomLockUsers) {
        releaseScanZoomLock?.();
        releaseScanZoomLock = null;
      }
    };
  }, []);
}

function photoScrollParent(node) {
  for (
    let parent = node?.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    const overflow = window.getComputedStyle(parent).overflowY;
    if (
      /(auto|scroll|overlay)/.test(overflow) &&
      parent.scrollHeight > parent.clientHeight + 1
    )
      return parent;
  }
  return document.scrollingElement;
}

function InlinePhotoViewer({
  src,
  side,
  regions = NO_REGIONS,
  showAnnotations = false,
  selected = -1,
  onSelect,
  focusSelected = false,
  focusToken = 0,
  focusGroup = false,
  showTools = true,
  overlayTools = false,
  onReady,
  onInteract,
  onMotionChange,
  onZoomChange,
  onGestureChange,
  onActivate,
  resetToken = 0,
  initialZoom = 1,
}) {
  const viewportRef = useRef(null),
    imageRef = useRef(null);
  const pointers = useRef(new Map()),
    gesture = useRef(null),
    lastTap = useRef(null);
  const transformRef = useRef({ zoom: initialZoom, x: 0, y: 0 });
  const appliedResetToken = useRef(resetToken);
  const appliedFocusToken = useRef(-1);
  const animationRef = useRef(0),
    animationGeneration = useRef(0),
    reducedMotion = useRef(false);
  const [transform, setTransform] = useState(transformRef.current);
  const [container, setContainer] = useState({ width: 0, height: 0 });
  const [loaded, setLoaded] = useState({
    src: "",
    width: 0,
    height: 0,
    failed: false,
  });
  const [dragging, setDragging] = useState(false),
    [moving, setMoving] = useState(false),
    [reload, setReload] = useState(0);
  const image = useMemo(
    () =>
      loaded.src === src
        ? { width: loaded.width, height: loaded.height }
        : { width: 0, height: 0 },
    [loaded, src]
  );
  const failed = loaded.src === src && loaded.failed;
  const fit = useMemo(() => fitImage(container, image), [container, image]);
  const ready = Boolean(src && !failed && fit);
  const latest = useRef({});
  latest.current = {
    fit,
    container,
    ready,
    onInteract,
    onSelect,
    onGestureChange,
    onActivate,
  };
  const stopMotion = useCallback(() => {
    animationGeneration.current += 1;
    if (animationRef.current) window.cancelAnimationFrame(animationRef.current);
    animationRef.current = 0;
    setMoving(false);
  }, []);
  const applyTransform = useCallback((value) => {
    const current = latest.current;
    const next = limitPhotoTransform(value, current.fit, current.container);
    transformRef.current = next;
    setTransform(next);
  }, []);
  const animateTo = useCallback(
    (value, duration = 420) => {
      stopMotion();
      const target = limitPhotoTransform(
        value,
        latest.current.fit,
        latest.current.container
      );
      const start = { ...transformRef.current };
      if (
        reducedMotion.current ||
        duration === 0 ||
        (Math.abs(start.zoom - target.zoom) < 0.001 &&
          Math.abs(start.x - target.x) < 0.1 &&
          Math.abs(start.y - target.y) < 0.1)
      ) {
        applyTransform(target);
        return;
      }
      const generation = animationGeneration.current;
      let started = null;
      setMoving(true);
      const frame = (time) => {
        if (generation !== animationGeneration.current) return;
        if (started === null) started = time;
        const t = clamp((time - started) / duration, 0, 1);
        const ease = 1 - Math.pow(1 - t, 3);
        applyTransform({
          zoom: start.zoom + (target.zoom - start.zoom) * ease,
          x: start.x + (target.x - start.x) * ease,
          y: start.y + (target.y - start.y) * ease,
        });
        if (t < 1) animationRef.current = window.requestAnimationFrame(frame);
        else {
          animationRef.current = 0;
          setMoving(false);
        }
      };
      animationRef.current = window.requestAnimationFrame(frame);
    },
    [applyTransform, stopMotion]
  );
  const clearGesture = useCallback(() => {
    const node = viewportRef.current;
    const ids = [...pointers.current.keys()];
    pointers.current.clear();
    gesture.current = null;
    lastTap.current = null;
    setDragging(false);
    latest.current.onGestureChange?.(false);
    ids.forEach((id) => {
      try {
        if (node?.hasPointerCapture?.(id)) node.releasePointerCapture(id);
      } catch (_) {}
    });
  }, []);
  const interact = useCallback(() => {
    stopMotion();
    latest.current.onInteract?.();
  }, [stopMotion]);
  const reset = useCallback(() => {
    interact();
    clearGesture();
    animateTo({ zoom: 1, x: 0, y: 0 }, 320);
  }, [interact, clearGesture, animateTo]);
  const zoomAt = useCallback(
    (nextZoom, point = { x: 0, y: 0 }, smooth = true) => {
      if (!latest.current.ready) return;
      interact();
      const current = transformRef.current;
      const zoom = clamp(nextZoom, PHOTO_MIN_ZOOM, PHOTO_MAX_ZOOM),
        ratio = zoom / current.zoom;
      const value = {
        zoom,
        x: point.x - (point.x - current.x) * ratio,
        y: point.y - (point.y - current.y) * ratio,
      };
      if (smooth) animateTo(value, 200);
      else applyTransform(value);
    },
    [interact, animateTo, applyTransform]
  );
  useEffect(() => {
    const node = viewportRef.current;
    const measure = () => {
      if (!node) return;
      const width = node.clientWidth,
        height = node.clientHeight;
      setContainer((old) =>
        old.width === width && old.height === height ? old : { width, height }
      );
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (node) observer?.observe(node);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      reducedMotion.current = media.matches;
    };
    update();
    if (media.addEventListener) media.addEventListener("change", update);
    else media.addListener(update);
    return () => {
      if (media.removeEventListener)
        media.removeEventListener("change", update);
      else media.removeListener(update);
    };
  }, []);
  useEffect(() => {
    stopMotion();
    clearGesture();
    transformRef.current = { zoom: initialZoom, x: 0, y: 0 };
    setTransform(transformRef.current);
    const node = imageRef.current;
    if (node?.complete && node.naturalWidth && node.getAttribute("src") === src)
      setLoaded({
        src,
        width: node.naturalWidth,
        height: node.naturalHeight,
        failed: false,
      });
  }, [src, reload, stopMotion, clearGesture]);
  useEffect(() => {
    stopMotion();
    clearGesture();
    applyTransform(transformRef.current);
  }, [fit, container, stopMotion, clearGesture, applyTransform]);
  useEffect(() => {
    if (
      !ready ||
      !focusSelected ||
      selected < 0 ||
      !regionGeometry(regions[selected], image.width, image.height)
    )
      return;
    clearGesture();
    animateTo(
      annotationFocusTransform(
        regions[selected],
        image,
        container,
        overlayTools
      ),
      620
    );
  }, [
    ready,
    focusSelected,
    focusToken,
    selected,
    regions,
    image,
    container,
    overlayTools,
    clearGesture,
    animateTo,
  ]);
  useEffect(() => {
    if (appliedResetToken.current === resetToken) return;
    appliedResetToken.current = resetToken;
    clearGesture();
    animateTo({ zoom: 1, x: 0, y: 0 }, 0);
  }, [resetToken, clearGesture, animateTo]);

  useEffect(() => {
    if (!focusGroup || !ready || !regions.length) return;
    if (appliedFocusToken.current === resetToken) return;
    appliedFocusToken.current = resetToken;
    animateTo(
      groupFocusTransform(regions, image, container, overlayTools),
      620
    );
  }, [
    focusGroup,
    ready,
    regions,
    image,
    container,
    overlayTools,
    resetToken,
    animateTo,
  ]);
  useEffect(() => {
    onReady?.(ready, failed);
  }, [onReady, ready, failed]);
  useEffect(() => {
    onMotionChange?.(moving);
  }, [onMotionChange, moving]);
  useEffect(() => {
    onZoomChange?.(transform.zoom > 1.001);
  }, [onZoomChange, transform.zoom]);
  useEffect(() => {
    if (appliedResetToken.current === resetToken) return;
    appliedResetToken.current = resetToken;
    clearGesture();
    animateTo({ zoom: 1, x: 0, y: 0 }, 0);
  }, [resetToken, clearGesture, animateTo]);
  useEffect(
    () => () => {
      animationGeneration.current += 1;
      if (animationRef.current)
        window.cancelAnimationFrame(animationRef.current);
      pointers.current.clear();
      latest.current.onGestureChange?.(false);
    },
    []
  );
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    const wheel = (event) => {
      if (!latest.current.ready || !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const delta =
        event.deltaY *
        (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1);
      zoomAt(
        transformRef.current.zoom * Math.exp(-clamp(delta, -120, 120) * 0.007),
        {
          x: event.clientX - rect.left - rect.width / 2,
          y: event.clientY - rect.top - rect.height / 2,
        },
        false
      );
    };
    node.addEventListener("wheel", wheel, { passive: false });
    return () => node.removeEventListener("wheel", wheel);
  }, [zoomAt]);
  const pointerPoints = () => {
    const rect = viewportRef.current.getBoundingClientRect();
    return [...pointers.current.values()].map((point) => ({
      ...point,
      x: point.clientX - rect.left - rect.width / 2,
      y: point.clientY - rect.top - rect.height / 2,
    }));
  };
  const beginGesture = (regionIndex = null) => {
    const points = pointerPoints(),
      initial = { ...transformRef.current };
    if (points.length >= 2) {
      const a = points[0],
        b = points[1];
      gesture.current = {
        type: "pinch",
        initial,
        distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
        centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        moved: true,
      };
      lastTap.current = null;
      setDragging(true);
    } else if (points.length === 1) {
      const scrollElement =
        initial.zoom <= 1.001 ? photoScrollParent(viewportRef.current) : null;
      gesture.current = {
        type: "pan",
        initial,
        point: points[0],
        regionIndex,
        moved: false,
        scrollElement,
        scrollTop: scrollElement?.scrollTop || 0,
      };
    } else {
      gesture.current = null;
      setDragging(false);
    }
  };
  const pointFor = (event) => {
    const rect = viewportRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2,
    };
  };
  const pointerDown = (event) => {
    if (!ready || (event.pointerType === "mouse" && event.button !== 0)) return;
    const hit = event.target.closest?.("[data-region-index]");
    interact();
    latest.current.onGestureChange?.(true);
    event.preventDefault();
    try {
      viewportRef.current.focus({ preventScroll: true });
    } catch (_) {}
    pointers.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch (_) {}
    beginGesture(hit ? Number(hit.getAttribute("data-region-index")) : null);
  };
  const pointerMove = (event) => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) return;
    event.preventDefault();
    pointers.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });
    const current = gesture.current,
      points = pointerPoints();
    if (current.type === "pinch" && points.length >= 2) {
      const a = points[0],
        b = points[1],
        centre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const zoom = clamp(
        (current.initial.zoom * Math.hypot(b.x - a.x, b.y - a.y)) /
          current.distance,
        PHOTO_MIN_ZOOM,
        PHOTO_MAX_ZOOM
      );
      const ratio = zoom / current.initial.zoom;
      applyTransform({
        zoom,
        x: centre.x - (current.centre.x - current.initial.x) * ratio,
        y: centre.y - (current.centre.y - current.initial.y) * ratio,
      });
    } else if (points.length === 1 && current.point) {
      const dx = points[0].clientX - current.point.clientX,
        dy = points[0].clientY - current.point.clientY;
      if (Math.hypot(dx, dy) > 5) current.moved = true;
      if (current.initial.zoom > 1) {
        setDragging(current.moved);
        applyTransform({
          ...current.initial,
          x: current.initial.x + dx,
          y: current.initial.y + dy,
        });
      } else if (
        event.pointerType === "touch" &&
        current.moved &&
        current.scrollElement
      ) {
        current.scrollElement.scrollTop = current.scrollTop - dy;
      }
    }
  };
  const pointerEnd = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    const current = gesture.current;
    if (
      event.type === "pointerup" &&
      pointers.current.size === 1 &&
      current &&
      !current.moved
    ) {
      if (latest.current.onActivate) {
        latest.current.onActivate();
        lastTap.current = null;
      } else if (Number.isInteger(current.regionIndex))
        latest.current.onSelect?.(current.regionIndex);
      else if (event.pointerType === "touch") {
        const point = pointFor(event),
          previous = lastTap.current,
          now = Date.now();
        if (
          previous &&
          now - previous.time < 320 &&
          Math.hypot(previous.point.x - point.x, previous.point.y - point.y) <
            24
        ) {
          zoomAt(transformRef.current.zoom > 1.05 ? 1 : 3, point);
          lastTap.current = null;
        } else lastTap.current = { time: now, point };
      }
    }
    pointers.current.delete(event.pointerId);
    try {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_) {}
    beginGesture();
    if (gesture.current) gesture.current.moved = true;
    if (!pointers.current.size) latest.current.onGestureChange?.(false);
  };
  const keyDown = (event) => {
    if (
      ready &&
      event.target === event.currentTarget &&
      (event.key === "Enter" || event.key === " ") &&
      latest.current.onActivate
    ) {
      event.preventDefault();
      latest.current.onActivate();
      return;
    }
    if (
      event.target !== event.currentTarget ||
      !ready ||
      ![
        "+",
        "=",
        "-",
        "0",
        "Home",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(event.key)
    )
      return;
    event.preventDefault();
    const current = transformRef.current;
    if (event.key === "+" || event.key === "=") zoomAt(current.zoom * 1.3);
    else if (event.key === "-") zoomAt(current.zoom / 1.3);
    else if (event.key === "0" || event.key === "Home") reset();
    else {
      interact();
      const offsets = {
        ArrowLeft: [40, 0],
        ArrowRight: [-40, 0],
        ArrowUp: [0, 40],
        ArrowDown: [0, -40],
      };
      if (current.zoom > 1)
        applyTransform({
          ...current,
          x: current.x + offsets[event.key][0],
          y: current.y + offsets[event.key][1],
        });
    }
  };
  return (
    <div
      className={`ds-inline-viewer ds-zoom-viewer ${
        overlayTools ? "has-overlay-tools" : ""
      }`}
    >
      <div
        ref={viewportRef}
        className="ds-media-viewport"
        tabIndex={src ? 0 : -1}
        aria-label={`${labelFor(
          side
        )} photo. Pinch or use plus and minus to zoom. Drag or use arrow keys to pan. Zero fits the photo. Control and mouse wheel also zoom.${
          onActivate ? " Tap or press Enter to enlarge." : ""
        }`}
        data-zoom={transform.zoom.toFixed(3)}
        data-pan-x={transform.x.toFixed(2)}
        data-pan-y={transform.y.toFixed(2)}
        data-dragging={dragging}
        data-moving={moving}
        style={{
          cursor:
            transform.zoom > 1 ? (dragging ? "grabbing" : "grab") : "default",
          touchAction: "none",
        }}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerEnd}
        onPointerCancel={pointerEnd}
        onLostPointerCapture={pointerEnd}
        onKeyDown={keyDown}
        onClick={(event) => {
          if (
            ready &&
            event.detail === 0 &&
            event.target === event.currentTarget
          )
            latest.current.onActivate?.();
        }}
        onDoubleClick={(event) => {
          if (ready && !event.target.closest?.("[data-region-index]")) {
            event.preventDefault();
            zoomAt(transformRef.current.zoom > 1.05 ? 1 : 3, pointFor(event));
          }
        }}
      >
        {src && !failed && (
          <div
            className="ds-media-fit"
            style={
              fit
                ? { width: fit.width, height: fit.height }
                : { width: "100%", height: "100%" }
            }
          >
            <div
              className="ds-media-layer"
              style={{
                transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
                transition: "none",
              }}
            >
              <img
                ref={imageRef}
                key={`${src}-${reload}`}
                src={src}
                alt={labelFor(side)}
                draggable={false}
                onLoad={(event) =>
                  setLoaded({
                    src,
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                    failed: false,
                  })
                }
                onError={() => {
                  stopMotion();
                  setLoaded({ src, width: 0, height: 0, failed: true });
                }}
              />
            </div>
          </div>
        )}
        {ready && showAnnotations && (
          <AnnotationLayer
            regions={regions}
            image={image}
            fit={fit}
            container={container}
            transform={transform}
            selected={selected}
            focused={focusSelected}
            overlayTools={overlayTools}
            onSelect={(index) => latest.current.onSelect?.(index)}
          />
        )}
        {(!src || failed) && (
          <div className="ds-empty">
            <FontAwesomeIcon icon={faImage} />
            <strong>{failed ? "Photo could not load" : "No photo yet"}</strong>
            <p>
              {failed
                ? "Check the connection, then retry."
                : `Take or upload the ${labelFor(side).toLowerCase()}.`}
            </p>
            {failed && (
              <button
                type="button"
                className="ds-text-button"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => {
                  setLoaded({ src: "", width: 0, height: 0, failed: false });
                  setReload((value) => value + 1);
                }}
              >
                Retry photo
              </button>
            )}
          </div>
        )}
      </div>
      {showTools && (
        <div
          className={`ds-viewer-tools ${overlayTools ? "ds-zoom-overlay" : ""}`}
          role="group"
          aria-label="Photo zoom controls"
        >
          {!overlayTools && (
            <span className="ds-viewer-mode">
              {showAnnotations ? "Detected parts" : "Original photo"}
            </span>
          )}
          <button
            type="button"
            disabled={!ready || transform.zoom <= PHOTO_MIN_ZOOM + 0.001}
            aria-label="Zoom out photo"
            onClick={() => zoomAt(transformRef.current.zoom / 1.3)}
          >
            <span aria-hidden="true">&minus;</span>
          </button>
          <output aria-label="Photo magnification">
            {transform.zoom.toFixed(1)}&times;
          </output>
          <button
            type="button"
            disabled={!ready || transform.zoom >= PHOTO_MAX_ZOOM - 0.001}
            aria-label="Zoom in photo"
            onClick={() => zoomAt(transformRef.current.zoom * 1.3)}
          >
            <span aria-hidden="true">+</span>
          </button>
          <button
            type="button"
            className="ds-fit-photo"
            disabled={!ready}
            aria-label="Reset photo zoom"
            title="Fit the whole photo"
            onClick={reset}
          >
            Fit
          </button>
        </div>
      )}
    </div>
  );
}
const REPORT_PARTS = [
  {
    id: "door",
    label: "Door",
    source: "front",
    classes: [
      "door",
      "door_damage",
      "door_number",
      "door_bell",
      "knocker",
      "spyhole",
      "signage",
      "keep_shut",
      "keep_locked",
      "keep_clear",
      "fire_exit",
      "ventilation",
    ],
  },
  { id: "closer", label: "Closer", source: "back", classes: ["door_closer"] },
  {
    id: "hinges",
    label: "Hinges",
    source: "hinge_top",
    classes: [
      "hinge",
      "screw",
      "missing_screw",
      "loose_screw",
      "normal",
      "missing",
      "loose",
      "reflection",
    ],
  },
  {
    id: "glass",
    label: "Glass",
    source: "front",
    classes: ["glass", "safety_glass"],
  },
  {
    id: "locks",
    label: "Locks",
    source: "back",
    classes: [
      "keyless_exit",
      "yale_lock",
      "night_latch",
      "mortice_lock",
      "external_lock",
      "mortice_cover",
      "code_lock",
      "emergency_lock",
      "padlock",
      "bolt_latch",
      "door_latch",
      "door_handle",
      "pull_handle",
      "door_knob",
      "push_pad",
    ],
  },
  {
    id: "letterbox",
    label: "Letterbox",
    source: "front",
    classes: ["letter_box"],
  },
  { id: "frame", label: "Frame", source: "front", classes: ["frame_damage"] },
  {
    id: "smoke_control",
    label: "Smoke Control",
    source: "back",
    classes: ["drop_down_seal"],
  },
  { id: "gaps", label: "Gaps", source: "front", classes: [] },
];
const REVIEW_NOTES = {
  door: "Identification from a photo does not establish the door rating. The certification label and supporting records need review.",
  closer:
    "A still photo cannot show the door closing fully and overcoming latch resistance. Operation has not been tested.",
  hinges:
    "The hinge model, markings, screws and compatibility with the door need review. A missing photo is not a missing hinge.",
  frame:
    "Visible faces only. Concealed fixings and the frame-to-wall junction have not been examined.",
  glass:
    "Do not infer fire resistance from appearance or a generic safety-glass mark. Supporting glazing-system evidence needs checking.",
  smoke_control:
    "The correct seal specification and compatibility with the door need checking. Identification alone does not confirm performance.",
  locks:
    "One visible keyless fitting does not establish how every fitted lock operates. Do not remove locks or risk being locked out.",
  letterbox:
    "Absence from a photograph alone does not establish that a letterbox is not fitted.",
  gaps: "Gaps have not been measured. A ruler photo is an unvalidated reference, not an automatic pass.",
};
export function buildNativeReport(
  predictions,
  hingeCount,
  decision,
  observations = []
) {
  const groups = REPORT_PARTS.map((part) => ({
    ...part,
    detections: [],
    status: "review",
    note: REVIEW_NOTES[part.id],
  }));
  const unclassified = [];
  Object.entries(predictions || {}).forEach(([side, result]) => {
    const prediction = result?.prediction || result;
    (Array.isArray(prediction?.regions) ? prediction.regions : []).forEach(
      (region, index) => {
        if (!region || typeof region.cls !== "string") return;
        const entry = { region, side, index };
        const group = HINGES.includes(side)
          ? groups.find((part) => part.id === "hinges")
          : groups.find((part) => part.classes.includes(region.cls));
        if (group) group.detections.push(entry);
        else unclassified.push(entry);
      }
    );
  });
  if (unclassified.length)
    groups.push({
      id: "other",
      label: "Other observations",
      source: unclassified[0].side,
      detections: unclassified,
      status: "review",
      note: "Model observations require review. They are not a completed assessment.",
    });
  return groups.map((part) => ({
    ...part,
    observations: observations.filter((entry) => entry.group === part.id),
    summary:
      part.id === "gaps"
        ? "Add a gauge photo or let FireChecked measure."
        : part.id === "hinges" && hingeCount === 2
        ? "Only two hinges reported - flagged for FireChecked review."
        : part.detections.length
        ? `${part.detections.length} model ${
            part.detections.length === 1 ? "identification" : "identifications"
          }. Suitability and condition need review.`
        : "Not assessed by the part-identification model.",
    certification: part.id === "door" ? decision : null,
  }));
}
function usePhotoPreviews(photos) {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    const next = {};
    Object.entries(photos).forEach(([key, file]) => {
      if (file) next[key] = URL.createObjectURL(file);
    });
    setUrls(next);
    return () => Object.values(next).forEach((url) => URL.revokeObjectURL(url));
  }, [photos]);
  return urls;
}
function safeMediaUrl(value) {
  return typeof value === "string" &&
    /^(https?:\/\/|blob:|data:image\/(?:jpeg|png|webp);)/i.test(value)
    ? value
    : "";
}
function savedImageUrl(photo) {
  return safeMediaUrl(
    photo?.displayImage?.url ||
      photo?.originalImage?.url ||
      photo?.imageUrl ||
      photo?.originalUrl ||
      ""
  );
}
function plainJSON(value) {
  return JSON.parse(
    JSON.stringify(value, (_key, item) => (item === undefined ? null : item))
  );
}
function photoCaptureMetadata(job) {
  const metadata = { ...(job.capture || {}) };
  delete metadata.originalFile;
  return plainJSON(metadata);
}
function uploadEvidence(
  storage,
  uid,
  scanId,
  key,
  file,
  kind,
  signal,
  onProgress
) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error("Save cancelled."));
      return;
    }
    const extensions = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/webp": "webp",
      "image/heic": "heic",
      "image/heif": "heif",
      "application/pdf": "pdf",
      "video/mp4": "mp4",
      "video/quicktime": "mov",
      "video/webm": "webm",
    };
    const path = `${SCAN_COLLECTION}/${uid}/scans/${scanId}/${key}/${newId()}-${kind}.${
      extensions[file.type] || "bin"
    }`;
    const task = uploadBytesResumable(ref(storage, path), file, {
      contentType: file.type || "application/octet-stream",
      cacheControl: "private, max-age=3600",
      customMetadata: { ownerId: uid, scanId, side: key, kind },
    });
    const cancel = () => task.cancel();
    signal.addEventListener("abort", cancel, { once: true });
    if (signal.aborted) cancel();
    const cleanup = () => signal.removeEventListener("abort", cancel);
    task.on(
      "state_changed",
      (snapshot) =>
        onProgress(
          snapshot.totalBytes
            ? snapshot.bytesTransferred / snapshot.totalBytes
            : 0
        ),
      (failure) => {
        cleanup();
        reject(failure);
      },
      async () => {
        try {
          if (signal.aborted) throw new Error("Save cancelled.");
          const url = await getDownloadURL(task.snapshot.ref);
          if (signal.aborted) throw new Error("Save cancelled.");
          resolve({
            path,
            url,
            contentType: file.type || "application/octet-stream",
            bytes: file.size,
          });
        } catch (failure) {
          reject(failure);
        } finally {
          cleanup();
        }
      }
    );
  });
}
function UiIcon({ name, ...props }) {
  const paths = {
    camera: (
      <>
        <path d="M8 5l1.5-2h5L16 5h3a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
        <circle cx="12" cy="12" r="4" />
      </>
    ),
    results: (
      <>
        <path d="M5 4h14v16H5zM8 8h8M8 12h3" />
        <path d="M11 15l2 2 5-6" />
      </>
    ),
    file: (
      <>
        <path d="M6 3h8l4 4v14H6zM14 3v5h4M12 10v7M9 14l3 3 3-3" />
      </>
    ),
    play: <path d="M8 4l12 8-12 8z" />,
    pause: (
      <>
        <path d="M8 4v16M16 4v16" />
      </>
    ),
    phone: (
      <path d="M4 3h4l2 5-3 2a15 15 0 007 7l2-3 5 2v4c0 1-1 2-2 2C10 22 2 14 2 5c0-1 1-2 2-2z" />
    ),
    hinge: (
      <>
        <rect x="4" y="3" width="7" height="18" rx="1" />
        <rect x="13" y="3" width="7" height="18" rx="1" />
        <path d="M12 2v20M7 7h1M7 12h1M7 17h1M16 7h1M16 12h1M16 17h1" />
      </>
    ),
    check: <path d="M5 12l4 4L19 6" />,
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {paths[name] || paths.results}
    </svg>
  );
}
function GuideArt({ side }) {
  const hinge = HINGES.includes(side);
  return (
    <div className="ds-guide-art" aria-hidden="true">
      {hinge ? (
        <UiIcon name="hinge" />
      ) : side === "certificate" ? (
        <svg viewBox="0 0 160 110">
          <rect x="20" y="20" width="120" height="70" rx="6" />
          <path d="M34 39h55M34 49h83M34 60h45M34 72h10m5 0h3m7 0h4m6 0h3m6 0h10m4 0h3" />
          <circle cx="120" cy="39" r="7" />
        </svg>
      ) : (
        <svg viewBox="0 0 140 160">
          <path d="M28 150V10h84v140M35 150V17h70v133M20 150h100" />
          <path
            d={
              side === "back"
                ? "M46 25h47v6H46zM48 31l20 15h29"
                : "M61 39h18v8H61z"
            }
          />
          <circle cx="94" cy="86" r="2" />
          <path d="M90 94h9" />
        </svg>
      )}
    </div>
  );
}
function CaptureGuide({ side, onContinue }) {
  const guide = GUIDE_COPY[side] || GUIDE_COPY.front;
  return (
    <div className="ds-info-body">
      <GuideArt side={side} />
      <h3>{guide.title}</h3>
      <p>{guide.text}</p>
      <p>{guide.detail}</p>
      <button
        type="button"
        className="ds-button ds-button-primary"
        onClick={onContinue}
      >
        <FontAwesomeIcon icon={faCamera} /> Take this photo
      </button>
    </div>
  );
}
function siblingEndpoint(baseEndpoint, path) {
  const base =
    typeof window !== "undefined" ? window.location.href : "https://localhost/";
  const url = new URL(baseEndpoint, base);
  url.pathname = url.pathname.replace(/\/[^/]*$/, path);
  url.search = "";
  url.hash = "";
  return url.href;
}
function hingeEndpoint(doorEndpoint) {
  return siblingEndpoint(doorEndpoint, HINGE_PREDICT_PATH);
}
function extractTextEndpoint(doorEndpoint) {
  return siblingEndpoint(doorEndpoint, EXTRACT_TEXT_PATH);
}
function applyHingeTextResult(text) {
  if (!text) return { certified: false, confidence: 0, matches: [] };
  const upper = text.toUpperCase();
  const matches = [];
  const checks = [
    { pattern: /\b[CG][E3]\b/, label: "CE" },
    { pattern: /EURO/, label: "Euro" },
    { pattern: /CERTIF[Il1]ED/, label: "Certified" },
    { pattern: /BRIT[Il1]SH/, label: "British" },
    { pattern: /CERT[Il1]F[Il1]RE|CERTIF[Il1]ER/, label: "Certifier" },
    { pattern: /GRADE\s*1[3]/, label: "Grade 13" },
    { pattern: /GRADE\s*1[1]/, label: "Grade 11" },
    { pattern: /JEDO/, label: "JEDO" },
    { pattern: /UK/, label: "UK" },
  ];
  for (const { pattern, label } of checks) {
    if (pattern.test(upper)) matches.push(label);
  }
  const certified = matches.length > 0;
  const confidence = Math.round((matches.length / checks.length) * 100);
  return { certified, confidence, matches };
}
function applyCertTextResult(text) {
  if (!text) return { rating: "No", confidence: 0, matches: [] };
  const upper = text.toUpperCase();
  const matches = [];
  const checks = [
    {
      pattern: /\bFD\s*-?\s*60\b/,
      label: "FD60",
      rating: "FD60",
      explicit: true,
    },
    {
      pattern: /\b60\s*MIN(UTE)?S?\b/,
      label: "60 min",
      rating: "FD60",
      explicit: true,
    },
    {
      pattern: /\bDOOR\s*-?\s*60\b/,
      label: "Door 60",
      rating: "FD60",
      explicit: true,
    },
    {
      pattern: /\bFD\s*-?\s*30\b/,
      label: "FD30",
      rating: "FD30",
      explicit: true,
    },
    {
      pattern: /\b30\s*MIN(UTE)?S?\b/,
      label: "30 min",
      rating: "FD30",
      explicit: true,
    },
    {
      pattern: /\bDOOR\s*-?\s*30\b/,
      label: "Door 30",
      rating: "FD30",
      explicit: true,
    },
    { pattern: /\bFIRE\s*DOOR\b/, label: "Fire Door", explicit: false },
    { pattern: /\bFD\b/, label: "FD", explicit: false },
    { pattern: /\bJELD\s*-?\s*WEN\b/, label: "Jeld-Wen", explicit: false },
    { pattern: /\bCERTIF[I1L]CATE\b/, label: "Certificate", explicit: false },
    { pattern: /\bCERTIF[I1L]ED\b/, label: "Certified", explicit: false },
    { pattern: /\bCERTIF[I1L]RE\b/, label: "Certifire", explicit: false },
    { pattern: /\bBS\s*476\b/, label: "BS476", explicit: false },
    { pattern: /\bBM\s*TRADA\b/, label: "BM TRADA", explicit: false },
    { pattern: /\bCE\b/, label: "CE", explicit: false },
    {
      pattern: /\bBRIT[I1L]SH\s*STANDARD\b/,
      label: "British Standard",
      explicit: false,
    },
    { pattern: /\bUK\s*CERT\b/, label: "UK Cert", explicit: false },
  ];
  let explicitRating = null;
  for (const { pattern, label, rating, explicit } of checks) {
    if (!pattern.test(upper)) continue;
    matches.push(label);
    if (explicit && (!explicitRating || rating === "FD60"))
      explicitRating = rating;
  }
  const rating = explicitRating || (matches.length ? "Assumed" : "No");
  const confidence = matches.length
    ? Math.round((matches.length / checks.length) * 100)
    : 0;
  return { rating, confidence, matches };
}
async function extractTextFromPhoto(file, url, signal) {
  const image = await readBase64(file);
  if (signal.aborted) throw new Error("Request cancelled.");
  const response = await modelClient.post(
    url,
    { image },
    {
      signal,
      headers: { "Content-Type": "application/json", Authorization: null },
    }
  );
  const result = response.data?.result ?? response.data;
  if (result?.error) throw new Error(String(result.error));
  return typeof result?.text === "string" ? result.text.trim() : "";
}
function endpointFor(key, job, predictionUrl, hingePredictionUrl) {
  if (
    key === "certificate" ||
    job.group === "certificate" ||
    job.type === "document" ||
    job.type === "video"
  )
    return null;
  if (HINGES.includes(key) || (job.type === "photo" && job.group === "hinges"))
    return hingePredictionUrl;
  return SIDES.includes(key) ? predictionUrl : null;
}
function allScanEntries(jobs, extras) {
  return [
    ...PHOTO_KEYS.filter((key) => jobs[key]).map((key) => [key, jobs[key]]),
    ...extras.map((entry) => [entry.id, entry]),
  ];
}
function storedPhoto(record, key) {
  return (
    record?.photos?.[key] ||
    record?.supplemental?.find((entry) => entry.id === key) ||
    null
  );
}
function assertOwner(services, uid, signal) {
  if (signal?.aborted || services.auth.currentUser?.uid !== uid)
    throw new Error("Your session changed. The operation was stopped.");
}
async function remoteAnalysisFile(job, signal) {
  if (job.file) return job.file;
  const image = job.stored?.displayImage || job.stored?.originalImage;
  if (!image?.url || !/^https?:\/\//i.test(image.url))
    throw new Error(
      "The saved photo is unavailable. Replace this photo to retry."
    );
  const response = await fetch(image.url, { signal, credentials: "omit" });
  if (!response.ok)
    throw new Error("The saved photo could not be downloaded for retry.");
  const length = Number(response.headers.get("content-length"));
  if (length > MAX_INPUT_BYTES)
    throw new Error("The saved photo is too large to analyse.");
  const blob = await response.blob();
  if (!blob.size || blob.size > MAX_INPUT_BYTES)
    throw new Error("The saved photo is too large to analyse.");
  return blob;
}
async function saveScan({
  jobs,
  documentRef,
  services,
  owner,
  doorId,
  doorName,
  sessionId,
  visitSessionId,
  signal,
  report,
  hingeConfiguration,
  certificateDecision,
  extras,
  observations,
  revision,
  baseRevision,
  mutationId,
  onPhotoState,
  checklistPolicy = DEFAULT_CHECKLIST_POLICY,
}) {
  const uid = owner.uid,
    scanId = documentRef.id;
  const authorized = () => assertOwner(services, uid, signal);
  if (
    SIDES.some(
      (key) =>
        !jobs[key] ||
        (!jobs[key].prediction &&
          (jobs[key].stored?.analysisStatus ||
            (jobs[key].stored ? "complete" : "pending")) !== "complete")
    )
  ) {
    throw new Error(
      "The front and back photos must be analysed before this scan can be saved."
    );
  }
  if (extras.length > MAX_EXTRAS)
    throw new Error(`Use up to ${MAX_EXTRAS} additional files per scan.`);
  const entries = allScanEntries(jobs, extras);
  const totalBytes = entries.reduce(
    (sum, [, job]) =>
      sum +
      (job.originalFile?.size ||
        job.capture?.originalFile?.size ||
        job.file?.size ||
        job.stored?.originalImage?.bytes ||
        0),
    0
  );
  if (totalBytes > MAX_EVIDENCE_BYTES)
    throw new Error(
      "This scan exceeds the 120 MB evidence limit. Use smaller files."
    );
  let resultBytes = 0;
  entries.forEach(([key, job]) => {
    if (!/^[a-zA-Z0-9_-]{1,100}$/.test(key))
      throw new Error("Invalid evidence identifier.");
    if (job.ownerId && job.ownerId !== uid)
      throw new Error("The scan owner changed. Open the scan again.");
    if (!job.file && !job.stored)
      throw new Error("An evidence file is missing.");
    if (job.file && (!job.file.size || job.file.size > 50 * 1024 * 1024))
      throw new Error("An evidence file is empty or too large.");
    if (job.prediction)
      resultBytes += new Blob([job.prediction.regionsJson]).size;
  });
  if (resultBytes > 6 * 1024 * 1024)
    throw new Error("The combined annotations exceed the safe save limit.");
  const photos = {},
    supplemental = [],
    chunks = [];
  for (let index = 0; index < entries.length; index += 1) {
    const [key, job] = entries[index];
    authorized();
    job.ownerId = uid;
    if (job.uploadScanId !== scanId) {
      job.uploads = {};
      job.uploadScanId = scanId;
    }
    const put = async (file, kind) => {
      if (!job.uploads[kind])
        job.uploads[kind] = await uploadEvidence(
          services.storage,
          uid,
          scanId,
          key,
          file,
          kind,
          signal,
          (fraction) => {
            if (signal.aborted || services.auth.currentUser?.uid !== uid)
              return;
            onPhotoState(key, {
              phase: "uploading",
              percent: Math.round(fraction * 100),
            });
            report(
              `Uploading ${PHOTO_LABELS[key] || job.label || "evidence"}`,
              48 + ((index + fraction) / entries.length) * 44
            );
          }
        );
      authorized();
      return job.uploads[kind];
    };
    let displayImage, originalImage;
    if (job.stored && !job.file) {
      displayImage = job.stored.displayImage || job.stored.originalImage;
      originalImage = job.stored.originalImage || displayImage;
    } else {
      displayImage = await put(job.file, "display");
      const original = job.originalFile || job.capture?.originalFile;
      originalImage =
        original && original !== job.file
          ? await put(original, "original")
          : displayImage;
    }
    const prediction = job.prediction;
    const unanalysed =
      key === "certificate" ||
      (!prediction && job.analysisStatus === "not-requested");
    let resultChunkIds = job.stored?.resultChunkIds || null;
    if (job.predictionChanged || (!job.stored && prediction)) {
      resultChunkIds = resultChunks(prediction.regionsJson).map(
        (regionsJson, chunkIndex) => {
          const id = `${key}-${job.captureId}-r${revision}-${String(
            chunkIndex
          ).padStart(4, "0")}`;
          chunks.push({ id, side: key, index: chunkIndex, regionsJson });
          return id;
        }
      );
    }
    const analysisStatus = unanalysed
      ? "not-requested"
      : job.analysisStatus ||
        (prediction ? "complete" : job.stored?.analysisStatus || "failed");
    const entry = {
      ...(job.stored || {}),
      side: key,
      captureId: job.captureId,
      imageId: job.captureId,
      capturedAt:
        job.stored?.capturedAt || new Date(job.capturedAt || Date.now()),
      displayImage,
      originalImage,
      capture: job.stored?.capture || photoCaptureMetadata(job),
      counts: unanalysed ? {} : prediction?.counts || job.stored?.counts || {},
      confidenceByClass: unanalysed
        ? {}
        : prediction?.confidenceByClass || job.stored?.confidenceByClass || {},
      imageSize: prediction?.imageSize || job.stored?.imageSize || null,
      annotationFormat: unanalysed
        ? null
        : prediction
        ? "normalized-v1"
        : job.stored?.annotationFormat || null,
      regionCount: unanalysed
        ? 0
        : prediction?.regions?.length ?? job.stored?.regionCount ?? 0,
      resultChunkCount: unanalysed
        ? 0
        : resultChunkIds?.length ?? job.stored?.resultChunkCount ?? 0,
      resultChunkIds: unanalysed ? [] : resultChunkIds,
      endpoint: unanalysed
        ? null
        : job.endpoint || job.stored?.endpoint || null,
      analysisStatus,
      analysisError: job.analysisError || "",
      extractedText: job.extractedText ?? job.stored?.extractedText ?? "",
      textAnalysisError:
        job.textAnalysisError ?? job.stored?.textAnalysisError ?? "",
      hingeTextResult:
        job.hingeTextResult ?? job.stored?.hingeTextResult ?? null,
      certTextResult: job.certTextResult ?? job.stored?.certTextResult ?? null,
      uploadStatus: "uploaded",
    };
    if (PHOTO_KEYS.includes(key)) photos[key] = entry;
    else
      supplemental.push({
        ...entry,
        id: key,
        type: job.type,
        group: job.group,
        checkId: job.checkId || job.stored?.checkId || null,
        questionId: job.questionId || job.stored?.questionId || null,
        label: job.label,
        fileName: String(
          job.originalFile?.name ||
            job.stored?.fileName ||
            job.file?.name ||
            job.label
        ).slice(0, 250),
        note: String(job.note || "").slice(0, 2000),
      });
    onPhotoState(key, { phase: "saving", percent: 100 });
  }
  const record = {
    schemaVersion: SCHEMA_VERSION,
    ownerId: uid,
    ownerType: owner.isAnonymous ? "guest" : "account",
    scanId,
    sessionId,
    ...(visitSessionId ? { visitSessionId } : {}),
    revision,
    mutationId,
    doorId: doorId == null ? null : String(doorId),
    doorName: String(doorName || "Door scan").slice(0, 120),
    photos,
    supplemental,
    certification: photos.certificate
      ? plainJSON(
          certificateDecision || {
            status: "photo_captured",
            analysed: false,
            rating: "No",
            confidence: 0,
            matches: [],
          }
        )
      : plainJSON(certificateDecision || { status: "not-provided" }),
    hinges: plainJSON(hingeConfiguration),
    observations: plainJSON(observations),
    regionCount: [...Object.values(photos), ...supplemental].reduce(
      (sum, entry) => sum + entry.regionCount,
      0
    ),
    status: "complete",
    assessment: "preliminary-evidence-checklist",
    checklist: (() => {
      const sections = buildSectionChecklist({
        predictions: Object.fromEntries(
          entries.map(([key, job]) => [key, job.prediction])
        ),
        sources: Object.fromEntries(entries.map(([key]) => [key, true])),
        extras: supplemental,
        observations,
        hingeConfiguration,
        certificateDecision,
        policy: checklistPolicy,
      });
      return {
        version: SECTION_CHECKLIST_VERSION,
        scope:
          "Preliminary photo and resident-reported checks only. Not a fire-safety certificate.",
        policy: plainJSON(checklistPolicy),
        sections,
        summary: summariseChecklist(sections),
        requiresProfessionalReview: true,
      };
    })(),
  };
  if (new Blob([JSON.stringify(record)]).size > 700000 || chunks.length > 450)
    throw new Error("The scan is too large to save safely.");
  report("Confirming your uploaded evidence", 96);
  authorized();
  const saved = await runTransaction(services.db, async (transaction) => {
    authorized();
    const snapshot = await transaction.get(documentRef);
    const previous = snapshot.exists() ? snapshot.data() : null;
    if (previous && previous.ownerId !== uid)
      throw new Error("You do not own this scan.");
    if (previous?.mutationId === mutationId)
      return { ...previous, id: scanId, documentPath: documentRef.path };
    if (
      previous ? (previous.revision || 0) !== baseRevision : baseRevision !== -1
    ) {
      throw new Error(
        "This scan changed on another device. Open its saved version before making further changes; your pending photos remain here."
      );
    }
    const createdAt = previous?.createdAt || serverTimestamp();
    const payload = { ...record, createdAt, updatedAt: serverTimestamp() };
    transaction.set(documentRef, payload, { merge: true });
    chunks.forEach(({ id, ...chunk }) =>
      transaction.set(doc(documentRef, "resultChunks", id), chunk)
    );
    return {
      ...payload,
      createdAt: previous?.createdAt || new Date(),
      updatedAt: new Date(),
      id: scanId,
      documentPath: documentRef.path,
    };
  });
  authorized();
  entries.forEach(([key, job]) => {
    job.stored = storedPhoto(saved, key);
    job.predictionChanged = false;
    onPhotoState(key, {
      phase: "uploaded",
      percent: 100,
      error: job.analysisError || "",
    });
  });
  report("All evidence saved to this scan", 100);
  return saved;
}
async function loadScanPredictions(services, record, signal) {
  assertOwner(services, record.ownerId, signal);
  const all = await getDocs(
    query(
      collection(doc(services.db, record.documentPath), "resultChunks"),
      orderBy("index", "asc")
    )
  );
  assertOwner(services, record.ownerId, signal);
  return decodeScanPredictions(
    record,
    all.docs.map((item) => ({ ...item.data(), id: item.id }))
  );
}
function decodeScanPredictions(record, chunks) {
  const entries = [
    ...Object.entries(record.photos || {}),
    ...(record.supplemental || []).map((extra) => [extra.id, extra]),
  ];
  const predictions = {},
    errors = {};
  entries.forEach(([key, photo]) => {
    if (key === "certificate" || photo.analysisStatus === "not-requested")
      return;
    if (
      photo.analysisStatus === "failed" ||
      photo.analysisStatus === "pending"
    ) {
      errors[key] =
        photo.analysisError ||
        "Saved photo is waiting for successful analysis.";
      return;
    }
    try {
      const selected = Array.isArray(photo.resultChunkIds)
        ? photo.resultChunkIds.map((id) =>
            chunks.find((chunk) => chunk.id === id)
          )
        : chunks.filter((chunk) => chunk.side === key);
      if (
        selected.length !== (photo.resultChunkCount || 0) ||
        selected.some(
          (chunk, index) =>
            !chunk ||
            chunk.index !== index ||
            typeof chunk.regionsJson !== "string"
        )
      )
        throw new Error("Some saved annotations are unavailable.");
      const value = selected.map((chunk) => chunk.regionsJson).join("") || "[]";
      if (new Blob([value]).size > MAX_RESULT_BYTES)
        throw new Error("Saved annotations exceed the safe load limit.");
      const regions = JSON.parse(value);
      if (!Array.isArray(regions))
        throw new Error("Saved annotations have an invalid format.");
      predictions[key] = {
        counts: photo.counts || {},
        regions,
        regionsJson: value,
        confidenceByClass: photo.confidenceByClass || {},
        imageSize: photo.imageSize || null,
      };
    } catch (failure) {
      errors[key] = friendlyError(failure);
    }
  });
  return { predictions, errors };
}
function CaptureWheel({
  activeKey,
  photos,
  hingeCount,
  disabled,
  onSelect,
  onAction,
  actionLabel,
  ready = false,
  compact = false,
  cameraMode = false,
  shutterDisabled = false,
}) {
  const wheelRef = useRef(null);
  const [diameter, setDiameter] = useState(compact ? 168 : 244);
  useEffect(() => {
    const wheel = wheelRef.current;
    const measure = () => {
      if (wheel?.clientWidth) setDiameter(wheel.clientWidth);
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (wheel) observer?.observe(wheel);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  const group = captureGroup(activeKey);
  const order = ["front", "back", "hinges", "certificate"];
  const activeIndex = Math.max(0, order.indexOf(group));
  const [angle, setAngle] = useState(-activeIndex * 90);
  useEffect(() => {
    setAngle((previous) => {
      const target = -activeIndex * 90;
      const delta = ((((target - previous + 540) % 360) + 360) % 360) - 180;
      return previous + delta;
    });
  }, [activeIndex]);
  const hingeKeys = hingePhotoKeys(hingeCount);
  const options = [
    {
      id: "front",
      label: "Front",
      key: "front",
      x: 50,
      y: 19,
      done: Boolean(photos.front),
    },
    {
      id: "back",
      label: "Back",
      key: "back",
      x: 81,
      y: 50,
      done: Boolean(photos.back),
    },
    {
      id: "hinges",
      label: "Hinges",
      key: hingeKeys.find((key) => !photos[key]) || "hinge_top",
      x: 50,
      y: 81,
      done: hingeKeys.every((key) => photos[key]),
    },
    {
      id: "certificate",
      label: "Cert.",
      key: "certificate",
      x: 19,
      y: 50,
      done: Boolean(photos.certificate),
    },
  ];
  return (
    <div
      className={`ds-wheel ds-native-wheel ds-rotating-wheel ds-solid-gear ${
        compact ? "is-compact" : ""
      } ${cameraMode ? "is-camera" : ""}`}
      ref={wheelRef}
      style={{ "--ds-wheel-diameter": `${diameter}px` }}
      role="group"
      aria-label="Capture steps"
      data-active={group}
      data-angle={angle}
    >
      <span className="ds-gear-pointer" aria-hidden="true" />
      <div
        className="ds-gear-rotor"
        style={{ transform: `rotate(${angle}deg)` }}
      >
        <svg className="ds-gear-disc" viewBox="0 0 240 240" aria-hidden="true">
          <circle
            cx="120"
            cy="120"
            r="118"
            fill="#23392c"
            stroke="#799183"
            strokeWidth="2"
          />
          <circle
            cx="120"
            cy="120"
            r="112"
            fill="none"
            stroke="#72917c"
            strokeWidth="3"
            strokeDasharray="2 7.8"
          />
          {options.map((option, i) => (
            <path
              key={option.id}
              d="M 120 120 L 39 39 A 114 114 0 0 1 201 39 Z"
              transform={`rotate(${i * 90} 120 120)`}
              fill={group === option.id ? "#268842" : "transparent"}
            />
          ))}
          <path
            d="M39 39L201 201M201 39L39 201"
            fill="none"
            stroke="#829689"
            strokeOpacity=".25"
          />
          <circle
            cx="120"
            cy="120"
            r="52"
            fill="#203327"
            stroke="#69806f"
            strokeWidth="1"
          />
        </svg>
        {options.map((option) => (
          <div
            className="ds-gear-anchor"
            key={option.id}
            style={{ left: `${option.x}%`, top: `${option.y}%` }}
          >
            <button
              type="button"
              className={`ds-wheel-option ds-gear-label ${
                group === option.id ? "is-active" : ""
              }`}
              style={{
                transform: `translate(-50%, -50%) rotate(${-angle}deg)`,
              }}
              disabled={disabled}
              aria-pressed={group === option.id}
              data-capture-step={option.id}
              aria-label={`${option.label}${
                option.done ? ", photo captured" : ""
              }`}
              onClick={() => onSelect(option.key)}
            >
              <span className="ds-gear-content">
                <span className="ds-gear-word">{option.label}</span>
                <span
                  className="ds-wheel-tick"
                  aria-hidden="true"
                  data-complete={option.done}
                >
                  <UiIcon name="check" />
                </span>
              </span>
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="ds-wheel-camera"
        disabled={disabled || shutterDisabled}
        aria-label={actionLabel}
        onClick={onAction}
      >
        {cameraMode ? (
          <span className="ds-native-shutter" />
        ) : ready ? (
          <UiIcon name="check" />
        ) : (
          <FontAwesomeIcon icon={faCamera} />
        )}
      </button>
    </div>
  );
}
function EvidenceThumbnail({ src, label, fileType = "photo" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed || fileType !== "photo")
    return (
      <span className="ds-thumbnail-placeholder">
        <FontAwesomeIcon icon={fileType === "photo" ? faCamera : faImage} />
      </span>
    );
  return (
    <img src={src} alt={label} loading="lazy" onError={() => setFailed(true)} />
  );
}
function EvidenceGallery({
  sources,
  photos,
  extras,
  statuses,
  savedRecord,
  hingeCount,
  activeKey,
  busy,
  onView,
  onCapture,
  onAdd,
  onRemove,
}) {
  const keys = [
    ...SIDES,
    ...hingePhotoKeys(hingeCount),
    ...(photos.hinge_middle && hingeCount === 2 ? ["hinge_middle"] : []),
    "certificate",
  ];
  const entries = [
    ...keys.map((key) => ({
      key,
      label: labelFor(key),
      exists: Boolean(photos[key]),
      type: "photo",
    })),
    ...extras.map((entry) => ({
      key: entry.id,
      label: entry.label,
      exists: true,
      type: entry.type,
      extra: true,
    })),
  ].filter((entry) => entry.exists);
  const captured = entries.filter((entry) => entry.exists).length;
  const uploaded = entries.filter(
    (entry) => entry.exists && statuses[entry.key]?.phase === "uploaded"
  ).length;
  return (
    <section className="ds-evidence-section" aria-label="Photo upload status">
      <div className="ds-section-title">
        <div>
          <h2>Your evidence</h2>
          <p>
            {captured} collected <span aria-hidden="true">/</span> {uploaded}{" "}
            uploaded
          </p>
        </div>
        <button
          type="button"
          className="ds-text-button"
          disabled={busy}
          onClick={onAdd}
        >
          <FontAwesomeIcon icon={faPlus} /> Add more
        </button>
      </div>
      <div className="ds-evidence-grid">
        {!entries.length && (
          <p className="ds-muted">
            No photos collected yet. Start with the front and back.
          </p>
        )}
        {entries.map((entry) => {
          const state = statuses[entry.key];
          const stored = storedPhoto(savedRecord, entry.key);
          const failed =
            state?.error ||
            (state?.phase === "uploaded" &&
              stored?.analysisStatus === "failed");
          const status = !entry.exists
            ? SIDES.includes(entry.key)
              ? "Required"
              : "Add later"
            : state?.phase === "uploading"
            ? "Uploading..."
            : state?.phase === "analysing"
            ? "Analysing..."
            : state?.phase === "saving"
            ? "Confirming save..."
            : state?.phase === "uploaded"
            ? failed
              ? "Uploaded / retry AI"
              : "Uploaded"
            : "On device / not uploaded";
          return (
            <div
              className={`ds-evidence-tile ${entry.exists ? "has-photo" : ""} ${
                state?.phase === "uploaded" ? "is-uploaded" : ""
              } ${failed ? "has-warning" : ""}`}
              key={entry.key}
            >
              <button
                type="button"
                disabled={busy}
                aria-label={`${entry.label}: ${status}. ${
                  entry.exists ? "View" : "Take photo"
                }`}
                aria-pressed={activeKey === entry.key}
                onClick={() =>
                  entry.exists ? onView(entry.key) : onCapture(entry.key)
                }
              >
                <EvidenceThumbnail
                  src={sources[entry.key]}
                  label={entry.label}
                  fileType={entry.type}
                />
                <strong>{entry.label}</strong>
                <span className="ds-file-status">
                  {state?.phase === "uploaded" && (
                    <FontAwesomeIcon icon={faCheck} />
                  )}{" "}
                  {status}
                </span>
              </button>
              {entry.extra && onRemove && (
                <button
                  type="button"
                  className="ds-remove-extra"
                  aria-label={`Remove ${entry.label}`}
                  disabled={busy}
                  onClick={() => onRemove(entry.key)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="ds-evidence-explainer">
        A tick means uploaded, not inspected. Front and back are required.
        Hinges, certification and extra evidence can be added later.
      </p>
    </section>
  );
}
function PhotoDetail({
  side,
  sources,
  originalSources,
  predictions,
  statuses,
  extras,
  busy,
  onRetake,
  onUpload,
  onClose,
}) {
  const [labels, setLabels] = useState(side !== "certificate");
  const [selected, setSelected] = useState(0);
  const [showOriginal, setShowOriginal] = useState(false);
  const entry = extras.find((item) => item.id === side);
  const regions = predictions[side]?.regions || [];
  const originalOnly = side === "certificate" || !predictions[side];
  const label = entry?.label || labelFor(side);
  const displaySource =
    side === "certificate"
      ? originalSources[side] || sources[side]
      : sources[side];
  const originalSource = originalSources[side] || displaySource;
  const source = showOriginal ? originalSource : displaySource;
  return (
    <Sheet title={label} onClose={onClose}>
      <div className="ds-photo-detail">
        {entry?.type === "video" ? (
          <video controls playsInline src={source} className="ds-extra-video" />
        ) : entry?.type === "document" ? (
          <a
            className="ds-button ds-button-secondary"
            href={originalSources[side] || source}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open original PDF
          </a>
        ) : (
          <InlinePhotoViewer
            src={source}
            side={side}
            regions={showOriginal || originalOnly ? [] : regions}
            showAnnotations={!showOriginal && !originalOnly && labels}
            selected={selected}
            onSelect={setSelected}
          />
        )}
        {!originalOnly && !showOriginal && (
          <div className="ds-segment">
            <button
              type="button"
              aria-pressed={!labels}
              onClick={() => setLabels(false)}
            >
              Photo
            </button>

            <button
              type="button"
              aria-pressed={labels}
              onClick={() => setLabels(true)}
            >
              AI labels
            </button>
          </div>
        )}
        {side === "certificate" && (
          <p className="ds-original-note">
            Original certification photo. Not sent to AI and not verified.
          </p>
        )}
        {side === "certificate" &&
          originalSources[side] &&
          originalSources[side] !== sources[side] && (
            <details>
              <summary>Original format not displaying?</summary>
              <p className="ds-muted">
                Some original formats need a device viewer. A full-frame JPEG
                preview is available below; the original file is retained.
              </p>
              <img
                className="ds-original-fallback"
                src={sources[side]}
                alt="Certification compatibility preview"
              />
            </details>
          )}
        {originalSources[side] && originalSources[side] !== sources[side] && (
          <button
            type="button"
            className="ds-text-button"
            onClick={() => {
              setShowOriginal((current) => !current);
              setLabels(false);
              setSelected(0);
            }}
          >
            {showOriginal ? "Back to scan image" : "View original image"}
          </button>
        )}

        {!originalOnly && (
          <>
            <p className="ds-muted">
              Labels identify visible parts. They do not certify fire safety.
            </p>
            <div className="ds-detection-chips">
              {Object.entries(predictions[side]?.counts || {}).map(
                ([cls, count]) => (
                  <span key={cls}>
                    {classLabel(cls)} <b>{count}</b>
                  </span>
                )
              )}
            </div>
            {regions[selected] && (
              <p className="ds-muted">{classLabel(regions[selected].cls)}</p>
            )}
          </>
        )}
        {statuses[side]?.error && (
          <p className="ds-inline-error" role="alert">
            {statuses[side].error}
          </p>
        )}
        {PHOTO_KEYS.includes(side) && (
          <div className="ds-review-actions">
            <button
              type="button"
              className="ds-button ds-button-primary"
              disabled={busy}
              onClick={() => onRetake(side)}
            >
              Retake photo
            </button>
            <button
              type="button"
              className="ds-button ds-button-secondary"
              disabled={busy}
              onClick={() => onUpload(side)}
            >
              Choose another
            </button>
          </div>
        )}
        {entry?.note && <p>{entry.note}</p>}
      </div>
    </Sheet>
  );
}
function EvidenceMediaPreview({
  evidenceKey,
  sources = {},
  extras = [],
  type = "photo",
  label,
}) {
  const item = extras.find((entry) => entry.id === evidenceKey);
  const file =
    typeof Blob !== "undefined" && item?.file instanceof Blob
      ? item.file
      : null;
  const localUrl = usePreview(file);
  const src =
    localUrl ||
    safeMediaUrl(sources[evidenceKey]) ||
    savedImageUrl(item) ||
    savedImageUrl(item?.stored);
  if (!src) return null;
  return type === "video" ? (
    <video
      className="ds-evidence-video"
      src={src}
      controls
      playsInline
      preload="metadata"
      aria-label={label || "Attached closing video"}
    />
  ) : (
    <img
      className="ds-selected-preview"
      src={src}
      alt={
        label ||
        PHOTO_LABELS[evidenceKey] ||
        item?.label ||
        "Attached evidence photo"
      }
    />
  );
}
function EvidenceForm({
  group,
  initialType = "photo",
  busy,
  onSave,
  onClose,
  onRecord,
  check = null,
  observations = [],
  onAnswer,
  extras = [],
  statuses = {},
  sources = {},
  predictions = {},
}) {
  const [part, setPart] = useState(group || "door");
  const [chosenType, setChosenType] = useState(initialType);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const nativeRef = useRef(null);
  const savingRef = useRef(false);
  const requirement = mediaRequirementFor(part, check?.id || "");
  const checkId = requirement?.checkId || check?.id || "";
  const questionId = !requirement && QUESTION_IDS.has(checkId) ? checkId : "";
  const type = requirement?.type || chosenType;
  const context = { sources, predictions, extras, observations };
  const attachedFiles = extras.filter(
    (item) =>
      item.group === part &&
      (!checkId || item.checkId === checkId || item.questionId === checkId)
  );
  const existingKeys = requirement
    ? requiredMediaEvidenceKeys(part, context, checkId)
    : photoEvidenceKeysFor(part, context, checkId);
  const provided = requirement
    ? existingKeys.length > 0
    : existingKeys.length > 0 || Boolean(check?.evidenceProvided);
  const selectedPreview = usePreview(
    selectedFiles.length && (type === "photo" || type === "video")
      ? selectedFiles[0]
      : null
  );
  const selectFiles = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setError("");
    if (event.target === nativeRef.current) event.target.value = "";
  };
  if (provided) {
    return (
      <div
        className="ds-evidence-form"
        data-required-media={requirement?.type || undefined}
      >
        <EvidenceGuideArt group={part} />
        {requirement && !check?.reason?.includes(requirement.instructions) && (
          <p>{requirement.instructions}</p>
        )}
        {check && (
          <div className="ds-simple-evidence-needs" data-check-id={check.id}>
            <strong>{check.label}</strong>
            <p>{check.reason}</p>
          </div>
        )}
        {!requirement && (
          <SectionQuestions
            group={part}
            observations={observations}
            busy={busy}
            onAnswer={onAnswer}
            questionId={checkId}
          />
        )}
        <div className="ds-section-attached">
          <strong>
            {requirement
              ? `${requirement.label} already provided`
              : "Evidence already provided"}
          </strong>
          {existingKeys.map((key) => (
            <div key={key}>
              <span>
                {PHOTO_LABELS[key] ||
                  extras.find((item) => item.id === key)?.label ||
                  "Evidence"}
              </span>
              <small>
                {statuses[key]?.phase === "uploaded" ||
                extras.find((item) => item.id === key)?.stored
                  ? "Uploaded"
                  : "Attached"}
              </small>
            </div>
          ))}
        </div>
        {existingKeys.map((key) => (
          <EvidenceMediaPreview
            key={key}
            evidenceKey={key}
            sources={sources}
            extras={extras}
            type={requirement?.type || "photo"}
            label={
              requirement?.type === "video"
                ? "Attached closing video"
                : requirement
                ? "Attached inside / escape-side hardware photo"
                : undefined
            }
          />
        ))}
        <p className="ds-muted">
          {requirement
            ? "This evidence is attached for review, not automatically passed. No duplicate upload is requested."
            : "The existing evidence is retained for review. No additional upload is requested."}
        </p>
        <button
          type="button"
          className="ds-button ds-button-secondary"
          disabled={busy}
          onClick={onClose}
        >
          Done
        </button>
      </div>
    );
  }
  return (
    <form
      className="ds-evidence-form"
      data-required-media={requirement?.type || undefined}
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy || savingRef.current) return;
        if (requirement && !selectedFiles.length) {
          setError(
            requirement.type === "video"
              ? "Record or upload a closing video. A photo or note cannot replace the video."
              : requirement?.checkId === LOCK_CONFIGURATION_CHECK
              ? "Take or upload certificate evidence. A note or video cannot replace the image."
              : "Take or upload a photo. A note or video cannot replace the image."
          );
          return;
        }
        if (!selectedFiles.length && !note.trim()) {
          setError("Choose evidence or add a note.");
          return;
        }
        if (
          requirement &&
          selectedFiles.some((file) =>
            requirement.type === "video"
              ? !/^video\//i.test(file.type)
              : !/^image\//i.test(file.type)
          )
        ) {
          setError(
            `Choose ${
              requirement.type === "video" ? "a video" : "an image"
            } for this check.`
          );
          return;
        }
        savingRef.current = true;
        try {
          setError("");
          await onSave({
            group: part,
            type,
            files: selectedFiles,
            note: note.trim(),
            checkId: checkId || null,
            questionId: questionId || null,
          });
        } catch (failure) {
          setError(friendlyError(failure));
        } finally {
          savingRef.current = false;
        }
      }}
    >
      <EvidenceGuideArt group={part} />
      {requirement && !check?.reason?.includes(requirement.instructions) && (
        <p>{requirement.instructions}</p>
      )}
      {check && (
        <div className="ds-simple-evidence-needs" data-check-id={check.id}>
          <strong>{check.label}</strong>
          <p>{check.reason}</p>
        </div>
      )}
      {!requirement && (
        <SectionQuestions
          group={part}
          observations={observations}
          busy={busy}
          onAnswer={onAnswer}
          questionId={checkId}
        />
      )}
      {questionId && (
        <p className="ds-muted">
          Answer changes update the checklist immediately. Attachments are
          supporting evidence for review; they do not automatically turn a
          failed or unanswered check into a pass.
        </p>
      )}
      {!requirement && attachedFiles.length > 0 && (
        <div className="ds-section-attached">
          <strong>
            Evidence already provided{checkId ? " for this check" : ""}
          </strong>
          {attachedFiles.map((item) => (
            <div key={item.id}>
              <span>{item.label || item.file?.name || "Evidence"}</span>
              <small>
                {statuses[item.id]?.phase === "uploaded" || item.stored
                  ? "Uploaded"
                  : "Ready to upload"}
              </small>
            </div>
          ))}
        </div>
      )}
      {!requirement && (
        <p>
          Add evidence to this same scan, now or later. Each file gets its own
          upload status.
        </p>
      )}
      {!requirement && (
        <label>
          Door part
          <select
            aria-label="Door part"
            value={part}
            disabled={busy || Boolean(checkId)}
            onChange={(event) => {
              setPart(event.target.value);
              setSelectedFiles([]);
              setError("");
              if (inputRef.current) inputRef.current.value = "";
            }}
          >
            {REPORT_PARTS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
            <option value="certificate">
              Certification / supporting records
            </option>
          </select>
        </label>
      )}
      {!requirement && (
        <label>
          Evidence type
          <select
            aria-label="Evidence type"
            value={type}
            disabled={busy}
            onChange={(event) => {
              setChosenType(event.target.value);
              setSelectedFiles([]);
              setError("");
              if (inputRef.current) inputRef.current.value = "";
            }}
          >
            <option value="photo">Photos</option>
            <option value="video">Video</option>
            <option value="document">PDF record</option>
          </select>
        </label>
      )}
      {requirement?.type === "video" && typeof onRecord === "function" && (
        <button
          type="button"
          className="ds-button ds-button-primary"
          disabled={busy}
          onClick={() => onRecord(part, checkId)}
        >
          Record closing video
        </button>
      )}
      {(type === "video" || type === "photo") && (
        <>
          <button
            type="button"
            className="ds-button ds-button-secondary"
            disabled={busy}
            onClick={() => nativeRef.current?.click()}
          >
            {type === "video"
              ? "Open phone video camera"
              : requirement?.checkId === LOCK_CONFIGURATION_CHECK
              ? "Take certificate photo"
              : requirement
              ? "Take photo"
              : "Take photo"}
          </button>
          <input
            ref={nativeRef}
            type="file"
            accept={type === "video" ? "video/*" : "image/*"}
            capture="environment"
            hidden
            disabled={busy}
            onChange={selectFiles}
          />
        </>
      )}
      <label>
        {requirement?.type === "video"
          ? "Upload closing video"
          : requirement?.checkId === LOCK_CONFIGURATION_CHECK
          ? "Upload certificate evidence"
          : requirement
          ? "Upload photo"
          : "Choose files"}
        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={busy}
          accept={
            type === "photo"
              ? "image/*"
              : type === "video"
              ? "video/mp4,video/quicktime,video/webm"
              : "application/pdf"
          }
          onChange={selectFiles}
        />
      </label>
      {selectedPreview &&
        (type === "video" ? (
          <video
            className="ds-evidence-video"
            src={selectedPreview}
            controls
            playsInline
            preload="metadata"
            aria-label="Selected closing video preview"
          />
        ) : (
          <img
            className="ds-selected-preview"
            src={selectedPreview}
            alt="Selected evidence preview"
          />
        ))}
      {!!selectedFiles.length && (
        <div className="ds-selected-files">
          {selectedFiles.map((file, index) => (
            <p key={`${file.name}-${index}`}>
              {file.name} <span>{(file.size / 1048576).toFixed(1)} MB</span>
            </p>
          ))}
        </div>
      )}
      <label>
        Note for the reviewer
        <textarea
          rows={3}
          maxLength={2000}
          value={note}
          disabled={busy}
          onChange={(event) => setNote(event.target.value)}
          placeholder={
            check ? `Evidence for ${check.label}` : "What should we look at?"
          }
        />
      </label>
      <p className="ds-muted">
        Photos: 15 MB each. Video/PDF: 50 MB each. Up to 20 extra files and 120
        MB per scan.{" "}
        {part === "hinges" && type === "photo"
          ? "Hinge photos are sent to the hinge/screw model when you save."
          : "Additional evidence is stored for review, not automatically analysed."}
      </p>
      {error && (
        <p className="ds-inline-error" role="alert">
          {error}
        </p>
      )}
      <div className="ds-review-actions">
        <button
          type="button"
          className="ds-button ds-button-secondary"
          disabled={busy}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ds-button ds-button-primary"
          disabled={busy}
        >
          {busy
            ? "Preparing evidence..."
            : requirement?.type === "video"
            ? "Add closing video"
            : requirement?.checkId === LOCK_CONFIGURATION_CHECK
            ? "Add certificate evidence"
            : requirement
            ? "Add photo evidence"
            : "Add evidence"}
        </button>
      </div>
    </form>
  );
}

function validateContact(contact) {
  if (!contact.name.trim() || contact.name.trim().length > 100)
    throw new Error("Enter your name (up to 100 characters).");
  if (
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) ||
    contact.email.length > 254
  )
    throw new Error("Enter a valid email address.");
  const digits = contact.phone.replace(/\D/g, "");
  if (
    digits.length < 7 ||
    digits.length > 15 ||
    !/^[+\d\s().-]+$/.test(contact.phone)
  )
    throw new Error(
      "Enter a phone number with 7 to 15 digits, including country code where needed."
    );
  return {
    name: contact.name.trim(),
    email: contact.email.trim(),
    phone: contact.phone.trim(),
  };
}
function ReviewForm({
  user,
  contact,
  onContactChange,
  record,
  uploadedCount,
  totalCount,
  busy,
  canSave,
  dirty,
  receipt,
  error,
  onSend,
  externalReview,
  checklist,
  onSummary,
}) {
  const [summaryAccepted, setSummaryAccepted] = useState(false);
  const [consent, setConsent] = useState(false),
    [message, setMessage] = useState(""),
    [localError, setLocalError] = useState("");
  return (
    <form
      className="ds-review-form"
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy) return;
        try {
          setLocalError("");
          const details = validateContact(contact);
          if (!summaryAccepted)
            throw new Error("Please confirm you reviewed the final result.");
          if (!consent)
            throw new Error("Please agree to share your evidence for review.");
          await onSend({
            contact: details,
            message: message.trim(),
            consent,
            summaryAcknowledged: summaryAccepted,
            reviewedFingerprint: checklistFingerprint(checklist),
          });
        } catch (failure) {
          setLocalError(friendlyError(failure));
        }
      }}
    >
      <p>
        Send your door photos and supporting evidence for a professional review.
      </p>
      <div className="ds-review-summary">
        <span>
          Scan<strong>{record?.doorName || "Your door scan"}</strong>
        </span>
        <span>
          Evidence
          <strong>
            {uploadedCount} / {totalCount} uploaded
          </strong>
        </span>
      </div>
      <AssessmentSummary sections={checklist} />
      <button
        type="button"
        className="ds-text-button"
        disabled={busy}
        onClick={onSummary}
      >
        View final result again
      </button>
      <label className="ds-consent">
        <input
          type="checkbox"
          checked={summaryAccepted}
          disabled={busy || Boolean(receipt)}
          required
          onChange={(event) => setSummaryAccepted(event.target.checked)}
        />
        <span>
          I have reviewed the final result, including failed checks and any
          checks still needing evidence.
        </span>
      </label>
      <label>
        Your name
        <input
          name="name"
          autoComplete="name"
          maxLength={100}
          required
          value={contact.name}
          disabled={busy || Boolean(receipt)}
          onChange={(event) =>
            onContactChange({ ...contact, name: event.target.value })
          }
        />
      </label>
      <label>
        Email address
        <input
          name="email"
          type="email"
          autoComplete="email"
          maxLength={254}
          required
          value={contact.email}
          disabled={busy || Boolean(receipt)}
          onChange={(event) =>
            onContactChange({ ...contact, email: event.target.value })
          }
        />
        {user?.email && (
          <small>Your signed-in email is filled in. You can change it.</small>
        )}
      </label>
      <label>
        Phone number
        <input
          name="tel"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          maxLength={30}
          required
          value={contact.phone}
          disabled={busy || Boolean(receipt)}
          onChange={(event) =>
            onContactChange({ ...contact, phone: event.target.value })
          }
        />
      </label>
      <label>
        Anything else? <span className="ds-optional">Optional</span>
        <textarea
          rows={3}
          maxLength={2000}
          value={message}
          disabled={busy || Boolean(receipt)}
          onChange={(event) => setMessage(event.target.value)}
        />
      </label>
      <label className="ds-consent">
        <input
          type="checkbox"
          required
          checked={consent}
          disabled={busy || Boolean(receipt)}
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>
          I agree to share these photos, evidence and contact details with
          FireChecked for review.
        </span>
      </label>
      {dirty && (
        <p className="ds-muted">
          Your new evidence will be saved to this scan before the review request
          is submitted.
        </p>
      )}
      {!externalReview && (
        <p className="ds-muted">
          The request is saved in your portal's review queue. Email
          notifications require your portal's review workflow to be connected.
        </p>
      )}
      {(localError || error) && (
        <p className="ds-inline-error" role="alert">
          {localError || error}
        </p>
      )}
      {receipt ? (
        <div className="ds-review-success" role="status">
          <strong>
            {receipt.delivery === "portal-queue"
              ? "Review request saved"
              : "Review request received"}
          </strong>
          <span>Reference: {receipt.reference}</span>
          <p>
            FireChecked will review your evidence, send you a PDF summary and
            contact you soon if anything needs discussing.
          </p>
          <p className="ds-muted">
            Submission is not a completed inspection or compliance decision.
          </p>
        </div>
      ) : (
        <button
          type="submit"
          className="ds-button ds-button-primary"
          disabled={busy || !canSave || !consent || !summaryAccepted}
        >
          {busy ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Saving &amp;
              submitting...
            </>
          ) : (
            "Submit for review"
          )}
        </button>
      )}
      {!canSave && (
        <p className="ds-inline-error">Add the front and back photos first.</p>
      )}
    </form>
  );
}
const REQUESTABLE_EVIDENCE_GROUPS = new Set([
  "closer",
  "smoke_control",
  "glass",
]);
const ADDABLE_EVIDENCE_GROUPS = new Set([
  ...REPORT_PARTS.map((part) => part.id),
  "certificate",
]);
const EVIDENCE_CHOICES = [
  ["door", "Door"],
  ["certificate", "Certificate"],
  ["closer", "Door closer"],
  ["hinges", "Hinges"],
  ["frame", "Frame"],
  ["smoke_control", "Intumescent strips"],
  ["locks", "Locks"],
  ["letterbox", "Letterbox"],
  ["glass", "Glass fire marking"],
];
const CLOSING_VIDEO_CHECK = "closing_video";
const KEYLESS_PHOTO_CHECK = "keyless_exit_photo";
const LOCK_CONFIGURATION_CHECK = "lock_configuration";
const KEYLESS_HARDWARE_CLASSES = ["keyless_exit", "push_pad", "emergency_lock"];
function mediaRequirementFor(group, checkId = "") {
  if (
    group === "closer" &&
    (!checkId || checkId === CLOSING_VIDEO_CHECK || checkId === "closerCloses")
  ) {
    return {
      group,
      checkId: CLOSING_VIDEO_CHECK,
      type: "video",
      label: "Closing video",
      requestLabel: "Record / upload closing video",
      viewLabel: "View closing video",
      instructions:
        "Send a video showing the whole door from open to closed. Release it without pushing and keep filming until it stops, so the reviewer can see whether it closes fully into the frame. Repeat from a small opening when safe. Keep fingers clear and do not lock yourself out.",
    };
  }
  if (group === "locks" && checkId === LOCK_CONFIGURATION_CHECK) {
    return {
      group,
      checkId: LOCK_CONFIGURATION_CHECK,
      type: "photo",
      label: "Certificate evidence",
      requestLabel: "Add certificate evidence",
      viewLabel: "Add certificate evidence",
      instructions:
        "Add a clear photo of the fire-door certificate or certification label for reviewer verification.",
    };
  }
  return null;
}
function availableEvidenceItem(item, sources = {}) {
  return Boolean(
    item &&
      (sources[item.id] ||
        item.file?.size > 0 ||
        savedImageUrl(item) ||
        savedImageUrl(item.stored))
  );
}
export function requiredMediaEvidenceKeys(
  group,
  { sources = {}, predictions = {}, extras = [], observations = [] } = {},
  checkId = ""
) {
  const requirement = mediaRequirementFor(group, checkId);
  if (!requirement) return [];
  const items = (Array.isArray(extras) ? extras : []).filter(
    (item) =>
      item.group === group &&
      item.type === requirement.type &&
      availableEvidenceItem(item, sources) &&
      (!item.file?.type ||
        (requirement.type === "video"
          ? /^video\//i.test(item.file.type)
          : /^image\//i.test(item.file.type)))
  );
  if (requirement.type === "video") {
    return [
      ...new Set(
        items
          .filter((item) => {
            const scope = item.checkId || item.questionId;
            return (
              !scope ||
              scope === CLOSING_VIDEO_CHECK ||
              scope === "closerCloses"
            );
          })
          .map((item) => item.id)
      ),
    ];
  }
  const keys = items
    .filter((item) =>
      [
        KEYLESS_PHOTO_CHECK,
        LOCK_CONFIGURATION_CHECK,
        "keyless_detected",
        "keyRequiredToExit",
      ].includes(item.checkId || item.questionId)
    )
    .map((item) => item.id);
  const exitSide = answerFrom(observations, "escapeSide");
  if (SIDES.includes(exitSide) && sources[exitSide]) {
    const classes = positiveClasses(predictions[exitSide]);
    if (KEYLESS_HARDWARE_CLASSES.some((id) => classes.has(id)))
      keys.push(exitSide);
  }
  return [...new Set(keys)];
}
function mediaRequirementForCheck(group, check) {
  return check?.requiredMedia ? mediaRequirementFor(group, check.id) : null;
}

export function photoEvidenceKeysFor(
  group,
  { sources = {}, predictions = {}, extras = [], observations = [] } = {},
  checkId = ""
) {
  const requirement = checkId ? mediaRequirementFor(group, checkId) : null;
  if (requirement)
    return requirement.type === "photo"
      ? requiredMediaEvidenceKeys(
          group,
          { sources, predictions, extras, observations },
          checkId
        )
      : [];
  const items = Array.isArray(extras) ? extras : [];
  const available = (item) =>
    Boolean(
      item &&
        (sources[item.id] ||
          item.file?.size > 0 ||
          savedImageUrl(item) ||
          savedImageUrl(item.stored))
    );
  const photos = items.filter(
    (item) => item.type === "photo" && available(item)
  );
  const scoped = photos.filter(
    (item) =>
      item.group === group &&
      (!checkId ||
        !(item.checkId || item.questionId) ||
        (item.checkId || item.questionId) === checkId)
  );
  const explicit = scoped.map((item) => item.id);
  const present = (keys) => keys.filter((key) => Boolean(sources[key]));
  const unique = (keys) => [...new Set(keys)];
  if (
    group === "certificate" ||
    (group === "door" && checkId === "door_identity")
  ) {
    return unique([
      ...present(["certificate"]),
      ...photos
        .filter(
          (item) =>
            item.group === "certificate" ||
            (item.group === "door" && item.checkId === "door_identity")
        )
        .map((item) => item.id),
    ]);
  }
  const hingeSide = HINGES.find((side) => checkId === `${side}_screws`);
  if (group === "hinges" && hingeSide) {
    return unique([
      ...present([hingeSide]),
      ...scoped
        .filter(
          (item) => item.checkId === checkId || item.questionId === checkId
        )
        .map((item) => item.id),
    ]);
  }
  if (group === "glass" && (!checkId || checkId === "glass_fire_marking"))
    return explicit;
  if (group === "gaps") return explicit;
  const classes = REPORT_PARTS.find((part) => part.id === group)?.classes || [];
  const detectedPhotos = Object.keys(sources).filter((side) => {
    const extra = items.find((item) => item.id === side);
    if (
      !sources[side] ||
      side === "certificate" ||
      (extra && extra.type !== "photo")
    )
      return false;
    const detected = positiveClasses(predictions[side]);
    return classes.some((cls) => detected.has(cls));
  });
  let base = [];
  if (["door", "frame", "locks"].includes(group)) base = SIDES;
  else if (group === "closer") base = ["back"];
  else if (group === "hinges")
    base = checkId === "hinge_rating" ? HINGES : ["back", ...HINGES];
  return unique([...present(base), ...detectedPhotos, ...explicit]);
}
function withPhotoEvidence(group, check, context) {
  const lockRuleWithoutFailure =
    group === "locks" &&
    check.id === LOCK_CONFIGURATION_CHECK &&
    check.status !== "fail";
  const requirement = lockRuleWithoutFailure
    ? null
    : mediaRequirementFor(group, check.id);
  if (requirement) {
    const keys = requiredMediaEvidenceKeys(group, context, check.id);
    const provided = keys.length > 0;
    return {
      ...check,
      requiredMedia: requirement.type,
      evidenceKeys: keys,
      attachedEvidenceKeys: (check.attachedEvidenceKeys || []).filter((key) =>
        keys.includes(key)
      ),
      photoEvidenceKeys: requirement.type === "photo" ? keys : [],
      hasPhotoEvidence: requirement.type === "photo" && provided,
      evidenceProvided: provided,
      needsEvidence: check.status !== "na" && !provided,
    };
  }
  const photoEvidenceKeys = photoEvidenceKeysFor(
    group,
    context,
    lockRuleWithoutFailure ? "" : check.id
  );
  const evidenceProvided =
    photoEvidenceKeys.length > 0 ||
    (check.attachedEvidenceKeys || []).length > 0 ||
    (check.source === "evidence-provided" &&
      (check.evidenceKeys || []).length > 0);
  return {
    ...check,
    ...(lockRuleWithoutFailure ? { requiredMedia: null } : {}),
    photoEvidenceKeys,
    hasPhotoEvidence: photoEvidenceKeys.length > 0,
    evidenceProvided,
    needsEvidence:
      !lockRuleWithoutFailure && check.status !== "na" && !evidenceProvided,
  };
}
function evidenceSectionFor(checklist, group) {
  if (group !== "certificate")
    return checklist.find((section) => section.id === group);
  const check = checklist
    .find((section) => section.id === "door")
    ?.checks.find((item) => item.id === "door_identity");
  return {
    id: "certificate",
    checks: check ? [check] : [],
    photoEvidenceKeys: check?.photoEvidenceKeys || [],
    evidenceProvided: Boolean(check?.evidenceProvided),
  };
}
function isEvidenceSectionApplicable(section) {
  if (!section || !ADDABLE_EVIDENCE_GROUPS.has(section.id)) return false;
  if (section.id === "locks")
    return (section.checks || []).some((check) => check.status === "fail");
  return (
    section.id !== "glass" ||
    (section.checks || []).some(
      (check) => check.id === "glass_detected" && check.status === "pass"
    )
  );
}
function canAddSectionEvidence(section) {
  return (
    isEvidenceSectionApplicable(section) &&
    ((section.checks || []).some(
      (check) => check.requiredMedia && check.needsEvidence
    ) ||
      (!section.evidenceProvided && !(section.photoEvidenceKeys || []).length))
  );
}
function canAddCheckEvidence(section, check) {
  return Boolean(
    check &&
      check.status !== "na" &&
      check.needsEvidence !== false &&
      !check.hasPhotoEvidence &&
      !check.evidenceProvided &&
      isEvidenceSectionApplicable(section)
  );
}
const EVIDENCE_PROMPTS = {
  closer: mediaRequirementFor("closer").instructions,
  locks:
    "Add a clear photo of the fire-door certificate or certification label for reviewer verification.",
  smoke_control: "Add a clear photo of the visible intumescent strips.",
  glass:
    "Add a close-up photo of the glass fire stamp or marking, or supporting fire-rating evidence.",
};
const groupForClass = (cls) =>
  REPORT_PARTS.find((part) => part.classes.includes(cls))?.id || "door";
export function buildAnnotationTour(predictions = {}, sources = {}) {
  const keys = [
    ...PHOTO_KEYS,
    ...Object.keys(predictions).filter((key) => !PHOTO_KEYS.includes(key)),
  ];
  return keys
    .filter((key) => key !== "certificate" && sources[key])
    .flatMap((side) =>
      (predictions[side]?.regions || [])
        .map((region, index) => ({
          id: `${side}:${index}`,
          side,
          index,
          label: classLabel(region?.cls),
          group: groupForClass(region?.cls),
          region,
        }))
        .filter((entry) => regionGeometry(entry.region, 1, 1))
    );
}
function EvidenceChooser({ selected, busy, onChoose, onBack, checklist = [] }) {
  return (
    <div className="ds-evidence-choices">
      {EVIDENCE_CHOICES.filter(([key]) =>
        canAddSectionEvidence(
          evidenceSectionFor(checklist, key) || { id: key, checks: [] }
        )
      ).map(([key, label]) => (
        <button
          type="button"
          key={key}
          className="ds-button ds-button-secondary"
          disabled={busy}
          data-suggested={selected === key}
          onClick={() => onChoose(key)}
        >
          {label}
        </button>
      ))}
      <button
        type="button"
        className="ds-button ds-button-primary"
        disabled={busy}
        onClick={onBack}
      >
        Back to final result
      </button>
    </div>
  );
}

const DOORSCAN_VISIT_HEARTBEAT_MS = 15000;
const DOORSCAN_ADMIN_PAGE_SIZE = 200;
const DOORSCAN_ADMIN_MAX_PAGES = 25;

export function isDoorScanAdminRole(userRole) {
  return normaliseAdminUserRole(userRole).toLowerCase() === "admin";
}

function assertDoorScanAdminRole(userRole, signal) {
  if (signal?.aborted)
    throw new Error("The admin history request was cancelled.");
  if (!isDoorScanAdminRole(userRole)) {
    const error = new Error(
      "Users history is available only to accounts with the admin role."
    );
    error.code = "permission-denied";
    throw error;
  }
}

function analyticsDate(value) {
  if (!value) return null;
  try {
    if (typeof value?.toDate === "function") return value.toDate();
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch (_) {
    return null;
  }
}

function analyticsMillis(value) {
  const date = analyticsDate(value);
  return date ? date.getTime() : 0;
}

function analyticsIso(value) {
  const date = analyticsDate(value);
  return date ? date.toISOString() : null;
}

function analyticsLocalDayKey(value) {
  const date = analyticsDate(value) || new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function analyticsDayLabel(value) {
  const date = analyticsDate(value);
  if (!date) return "Unknown date";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function analyticsTimeLabel(value) {
  const date = analyticsDate(value);
  if (!date) return "Unknown time";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function analyticsDateTimeLabel(value) {
  const date = analyticsDate(value);
  if (!date) return "Not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatAnalyticsDuration(value) {
  const totalSeconds = Math.max(0, Math.round((Number(value) || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function analyticsLocationLabel(location) {
  if (!location || typeof location !== "object") return "Location not recorded";
  const label = [
    location.city,
    location.region,
    location.countryName || location.country,
  ]
    .filter(Boolean)
    .join(", ");
  if (label) return label;
  if (location.lookupStatus === "pending") return "Resolving IP location...";
  if (location.available === false)
    return location.timezone
      ? `IP location unavailable · ${location.timezone}`
      : "IP location unavailable";
  return "Location not recorded";
}

function analyticsBrowserName(userAgent) {
  const value = String(userAgent || "");
  if (/OPR\/|Opera/i.test(value)) return "Opera";
  if (/Edg\//i.test(value)) return "Microsoft Edge";
  if (/Chrome\/|CriOS\//i.test(value)) return "Google Chrome";
  if (/Firefox\/|FxiOS\//i.test(value)) return "Mozilla Firefox";
  if (/Safari\//i.test(value) && !/Chrome|CriOS|Chromium/i.test(value))
    return "Safari";
  return "Unknown browser";
}

function analyticsOperatingSystem(userAgent, platform) {
  const value = `${userAgent || ""} ${platform || ""}`;
  if (/Windows NT/i.test(value)) return "Windows";
  if (/Android/i.test(value)) return "Android";
  if (/iPhone|iPad|iPod/i.test(value)) return "iOS / iPadOS";
  if (/Macintosh|Mac OS X|MacIntel/i.test(value)) return "macOS";
  if (/CrOS/i.test(value)) return "ChromeOS";
  if (/Linux/i.test(value)) return "Linux";
  return String(platform || "Unknown OS");
}

function analyticsDeviceType(userAgent) {
  const value = String(userAgent || "");
  const tablet = /iPad|Tablet|PlayBook|Silk/i.test(value);
  const mobile = /Mobi|Android|iPhone|iPad/i.test(value);
  return tablet ? "Tablet" : mobile ? "Mobile" : "Desktop";
}

async function collectScanVisitClientInfo() {
  const userAgent = navigator.userAgent || "";
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;
  let battery = null;
  let userAgentData = null;

  try {
    if (navigator.getBattery) {
      const value = await navigator.getBattery();
      battery = {
        levelPercent: Math.round(value.level * 100),
        charging: Boolean(value.charging),
      };
    }
  } catch (_) {}

  try {
    if (navigator.userAgentData) {
      let highEntropy = {};
      if (typeof navigator.userAgentData.getHighEntropyValues === "function") {
        try {
          highEntropy = await navigator.userAgentData.getHighEntropyValues([
            "architecture",
            "bitness",
            "model",
            "platformVersion",
            "fullVersionList",
          ]);
        } catch (_) {}
      }
      userAgentData = plainJSON({
        brands: navigator.userAgentData.brands || [],
        mobile: Boolean(navigator.userAgentData.mobile),
        platform: navigator.userAgentData.platform || null,
        ...highEntropy,
      });
    }
  } catch (_) {}

  const platform =
    navigator.userAgentData?.platform || navigator.platform || null;
  const queryParams = new URLSearchParams(window.location.search);
  return plainJSON({
    page: window.location.pathname || "/scan",
    pageUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || null,
    campaign: {
      source: queryParams.get("utm_source"),
      medium: queryParams.get("utm_medium"),
      campaign: queryParams.get("utm_campaign"),
      term: queryParams.get("utm_term"),
      content: queryParams.get("utm_content"),
    },
    browser: analyticsBrowserName(userAgent),
    operatingSystem: analyticsOperatingSystem(userAgent, platform),
    deviceType: analyticsDeviceType(userAgent),
    userAgent,
    userAgentData,
    platform,
    vendor: navigator.vendor || null,
    language: navigator.language || null,
    languages: Array.isArray(navigator.languages)
      ? navigator.languages.slice(0, 12)
      : [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    locale: Intl.DateTimeFormat().resolvedOptions().locale || null,
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    cpuCores: Number.isFinite(navigator.hardwareConcurrency)
      ? navigator.hardwareConcurrency
      : null,
    approximateMemoryGb: Number.isFinite(navigator.deviceMemory)
      ? navigator.deviceMemory
      : null,
    maxTouchPoints: Number.isFinite(navigator.maxTouchPoints)
      ? navigator.maxTouchPoints
      : 0,
    cookiesEnabled: Boolean(navigator.cookieEnabled),
    online: Boolean(navigator.onLine),
    webdriver: Boolean(navigator.webdriver),
    screen: {
      width: window.screen?.width ?? null,
      height: window.screen?.height ?? null,
      availableWidth: window.screen?.availWidth ?? null,
      availableHeight: window.screen?.availHeight ?? null,
      colorDepth: window.screen?.colorDepth ?? null,
      pixelDepth: window.screen?.pixelDepth ?? null,
      orientation: window.screen?.orientation?.type ?? null,
      devicePixelRatio: window.devicePixelRatio ?? null,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    network: connection
      ? {
          effectiveType: connection.effectiveType ?? null,
          downlinkMbps: Number.isFinite(connection.downlink)
            ? connection.downlink
            : null,
          rttMs: Number.isFinite(connection.rtt) ? connection.rtt : null,
          saveData: Boolean(connection.saveData),
        }
      : null,
    preferences: {
      darkMode:
        window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false,
      reducedMotion:
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ??
        false,
    },
    battery,
  });
}

function analyticsFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

async function fetchAnalyticsJson(url, timeoutMs = 5000) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      redirect: "follow",
      referrerPolicy: "no-referrer",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (!data || typeof data !== "object" || Array.isArray(data))
      throw new Error("Invalid JSON response");
    return data;
  } finally {
    window.clearTimeout(timer);
  }
}

function analyticsLocationHasUsefulData(location) {
  return Boolean(
    location &&
      (location.ip ||
        location.city ||
        location.region ||
        location.countryName ||
        location.country ||
        location.latitude != null ||
        location.longitude != null)
  );
}

async function locationFromIpapiIs() {
  const data = await fetchAnalyticsJson("https://api.ipapi.is", 4500);
  if (data.error || data.error_code || data.is_bogon)
    throw new Error(data.error || data.error_code || "No public IP location");
  const location = plainJSON({
    ip: data.ip || null,
    city: data.city || null,
    region: data.region || null,
    country: data.country || null,
    countryName: data.country || null,
    latitude: analyticsFiniteNumber(data.lat),
    longitude: analyticsFiniteNumber(data.lon),
    timezone: data.timezone || null,
    asn: data.asn || null,
    organisation: data.company || null,
    isp: data.company || null,
    approximate: true,
    available: true,
    lookupStatus: "complete",
    source: "ipapi.is",
    resolvedAt: new Date().toISOString(),
  });
  if (!analyticsLocationHasUsefulData(location))
    throw new Error("Provider returned no usable location fields");
  return location;
}

async function locationFromIpWhoIs() {
  const data = await fetchAnalyticsJson("https://ipwho.is/", 5000);
  if (data.success === false)
    throw new Error(data.message || "IP lookup failed");
  const timezone =
    data.timezone && typeof data.timezone === "object" ? data.timezone : {};
  const connection =
    data.connection && typeof data.connection === "object"
      ? data.connection
      : {};
  const location = plainJSON({
    ip: data.ip || null,
    city: data.city || null,
    region: data.region || null,
    country: data.country_code || data.country || null,
    countryCode: data.country_code || null,
    countryName: data.country || null,
    postal: data.postal || null,
    latitude: analyticsFiniteNumber(data.latitude),
    longitude: analyticsFiniteNumber(data.longitude),
    timezone: timezone.id || null,
    utcOffset: timezone.utc || null,
    asn: connection.asn != null ? String(connection.asn) : null,
    organisation: connection.org || connection.isp || null,
    isp: connection.isp || null,
    approximate: true,
    available: true,
    lookupStatus: "complete",
    source: "ipwho.is",
    resolvedAt: new Date().toISOString(),
  });
  if (!analyticsLocationHasUsefulData(location))
    throw new Error("Provider returned no usable location fields");
  return location;
}

async function locationFromIpapiCo() {
  const data = await fetchAnalyticsJson("https://ipapi.co/json/", 5000);
  if (data.error)
    throw new Error(data.message || data.reason || "ipapi.co lookup failed");
  const location = plainJSON({
    ip: data.ip || null,
    city: data.city || null,
    region: data.region || null,
    country: data.country || data.country_code || null,
    countryCode: data.country_code || data.country || null,
    countryName: data.country_name || null,
    postal: data.postal || null,
    latitude: analyticsFiniteNumber(data.latitude),
    longitude: analyticsFiniteNumber(data.longitude),
    timezone: data.timezone || null,
    utcOffset: data.utc_offset || null,
    asn: data.asn || null,
    organisation: data.org || null,
    isp: data.org || null,
    currency: data.currency || null,
    approximate: true,
    available: true,
    lookupStatus: "complete",
    source: "ipapi.co",
    resolvedAt: new Date().toISOString(),
  });
  if (!analyticsLocationHasUsefulData(location))
    throw new Error("Provider returned no usable location fields");
  return location;
}

async function getApproximateIpLocation() {
  const failures = [];
  const providers = [
    ["ipapi.is", locationFromIpapiIs],
    ["ipwho.is", locationFromIpWhoIs],
    ["ipapi.co", locationFromIpapiCo],
  ];
  for (const [provider, lookup] of providers) {
    try {
      return await lookup();
    } catch (error) {
      failures.push({
        provider,
        reason:
          error?.name === "AbortError"
            ? "timeout"
            : String(error?.message || error || "lookup failed").slice(0, 180),
      });
    }
  }
  return plainJSON({
    ip: null,
    city: null,
    region: null,
    country: null,
    countryName: null,
    latitude: null,
    longitude: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    approximate: true,
    available: false,
    lookupStatus: "failed",
    source: null,
    resolvedAt: new Date().toISOString(),
    failures,
  });
}

function visitTiming(tracker, stopActive = false) {
  if (!tracker?.startedAtMs) return { activeMs: 0, elapsedMs: 0 };
  const now = performance.now();
  if (tracker.activeStartedAt !== null) {
    tracker.activeMs += Math.max(0, now - tracker.activeStartedAt);
    tracker.activeStartedAt = stopActive ? null : now;
  }
  return {
    activeMs: Math.max(0, Math.round(tracker.activeMs)),
    elapsedMs: Math.max(0, Date.now() - tracker.startedAtMs),
  };
}

function useDoorScanVisitSession(services, authLoading, user, allowGuestScans) {
  const trackerRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const visitIdentityRef = useRef({
    ownerUid: "",
    sessionId: "",
    startedAtMs: 0,
  });
  if (user?.uid && visitIdentityRef.current.ownerUid !== user.uid) {
    visitIdentityRef.current = {
      ownerUid: user.uid,
      sessionId: newId(),
      startedAtMs: visitIdentityRef.current.ownerUid
        ? Date.now()
        : mountedAtRef.current,
    };
  }

  useEffect(() => {
    if (!services?.auth || authLoading || user || !allowGuestScans)
      return undefined;
    let cancelled = false;
    ensureScanOwner(services.auth, true).catch((error) => {
      if (!cancelled)
        console.warn("DoorScan visitor identity unavailable:", error);
    });
    return () => {
      cancelled = true;
    };
  }, [services, authLoading, user, allowGuestScans]);

  useEffect(() => {
    if (!services?.auth || !services?.db || authLoading || !user?.uid)
      return undefined;

    const identity =
      visitIdentityRef.current.ownerUid === user.uid &&
      visitIdentityRef.current.sessionId
        ? visitIdentityRef.current
        : { ownerUid: user.uid, sessionId: newId(), startedAtMs: Date.now() };
    visitIdentityRef.current = identity;
    const tracker = {
      sessionId: identity.sessionId,
      ownerUid: user.uid,
      startedAtMs: identity.startedAtMs,
      activeMs: 0,
      activeStartedAt:
        document.visibilityState === "visible" ? performance.now() : null,
      sessionRef: null,
      ready: false,
    };
    trackerRef.current = tracker;

    let cancelled = false;
    let heartbeatTimer = 0;
    let locationRetryTimer = 0;

    const sessionRef = doc(
      services.db,
      SCAN_COLLECTION,
      user.uid,
      "sessions",
      tracker.sessionId
    );
    tracker.sessionRef = sessionRef;

    const writeTiming = async (ended = false) => {
      if (!tracker.ready || services.auth.currentUser?.uid !== tracker.ownerUid)
        return;
      try {
        await updateDoc(sessionRef, {
          ...visitTiming(tracker, ended || document.hidden),
          lastSeenAt: serverTimestamp(),
          lastSeenAtClient: new Date().toISOString(),
          pageVisible: ended ? false : !document.hidden,
          ...(ended ? { endedAt: serverTimestamp() } : {}),
        });
      } catch (error) {
        console.warn("Unable to update DoorScan visit timing:", error);
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        visitTiming(tracker, true);
        void writeTiming(false);
      } else {
        if (tracker.activeStartedAt === null)
          tracker.activeStartedAt = performance.now();
        void writeTiming(false);
      }
    };
    const onPageHide = (event) => {
      if (event.persisted) {
        visitTiming(tracker, true);
        void writeTiming(false);
      } else {
        void writeTiming(true);
      }
    };
    const onPageShow = () => {
      if (
        document.visibilityState === "visible" &&
        tracker.activeStartedAt === null
      )
        tracker.activeStartedAt = performance.now();
      void writeTiming(false);
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    const start = async () => {
      try {
        const clientPromise = collectScanVisitClientInfo();
        const locationPromise = getApproximateIpLocation();
        const pendingLocation = plainJSON({
          ip: null,
          city: null,
          region: null,
          country: null,
          countryName: null,
          latitude: null,
          longitude: null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
          approximate: true,
          available: false,
          lookupStatus: "pending",
          source: null,
        });
        const initialLocation = await Promise.race([
          locationPromise,
          new Promise((resolve) =>
            window.setTimeout(() => resolve(pendingLocation), 1800)
          ),
        ]);
        const client = await clientPromise;
        if (cancelled || services.auth.currentUser?.uid !== tracker.ownerUid)
          return;
        const providerIds = Array.isArray(user.providerData)
          ? user.providerData.map((entry) => entry.providerId).filter(Boolean)
          : [];

        await setDoc(sessionRef, {
          ownerId: user.uid,
          visitorUid: user.uid,
          visitorAnonymous: Boolean(user.isAnonymous),
          visitSessionId: tracker.sessionId,
          page: window.location.pathname || "/scan",
          account: {
            displayName: user.displayName || null,
            email: user.email || null,
            phoneNumber: user.phoneNumber || null,
            providerIds,
          },
          startedAt: serverTimestamp(),
          startedAtClient: new Date(tracker.startedAtMs).toISOString(),
          lastSeenAt: serverTimestamp(),
          lastSeenAtClient: new Date().toISOString(),
          endedAt: null,
          pageVisible: !document.hidden,
          ...visitTiming(tracker, document.hidden),
          localDayKey: analyticsLocalDayKey(new Date(tracker.startedAtMs)),
          scanIds: [],
          location: initialLocation,
          client,
        });
        if (cancelled) return;
        tracker.ready = true;

        void locationPromise.then(async (location) => {
          if (
            cancelled ||
            !location ||
            services.auth.currentUser?.uid !== tracker.ownerUid
          )
            return;
          try {
            await setDoc(
              sessionRef,
              {
                location,
                lastSeenAt: serverTimestamp(),
                lastSeenAtClient: new Date().toISOString(),
              },
              { merge: true }
            );
          } catch (error) {
            console.warn("Unable to store DoorScan IP location:", error);
          }
          if (location.available === false) {
            locationRetryTimer = window.setTimeout(() => {
              if (
                cancelled ||
                services.auth.currentUser?.uid !== tracker.ownerUid
              )
                return;
              void getApproximateIpLocation().then(async (retryLocation) => {
                if (
                  cancelled ||
                  !retryLocation ||
                  retryLocation.available === false
                )
                  return;
                try {
                  await setDoc(
                    sessionRef,
                    {
                      location: retryLocation,
                      lastSeenAt: serverTimestamp(),
                      lastSeenAtClient: new Date().toISOString(),
                    },
                    { merge: true }
                  );
                } catch (error) {
                  console.warn(
                    "Unable to store retried DoorScan IP location:",
                    error
                  );
                }
              });
            }, 12000);
          }
        });
      } catch (error) {
        console.warn("DoorScan visit analytics unavailable:", error);
      }
    };

    void start();
    heartbeatTimer = window.setInterval(
      () => void writeTiming(false),
      DOORSCAN_VISIT_HEARTBEAT_MS
    );

    return () => {
      visitTiming(tracker, true);
      void writeTiming(true);
      cancelled = true;
      window.clearInterval(heartbeatTimer);
      window.clearTimeout(locationRetryTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      if (trackerRef.current === tracker) trackerRef.current = null;
    };
  }, [services, authLoading, user?.uid]);

  const getVisitSessionId = useCallback((ownerUid) => {
    const identity = visitIdentityRef.current;
    return identity.ownerUid === ownerUid ? identity.sessionId : "";
  }, []);

  const recordSavedScan = useCallback(
    (savedScan) => {
      const ownerId = String(savedScan?.ownerId || "");
      const scanId = String(savedScan?.id || savedScan?.scanId || "");
      const visitSessionId = String(savedScan?.visitSessionId || "");
      if (!services?.db || !ownerId || !scanId || !visitSessionId) return;
      if (services.auth.currentUser?.uid !== ownerId) return;
      const sessionRef = doc(
        services.db,
        SCAN_COLLECTION,
        ownerId,
        "sessions",
        visitSessionId
      );
      void setDoc(
        sessionRef,
        {
          ownerId,
          visitorUid: ownerId,
          visitSessionId,
          lastSeenAt: serverTimestamp(),
          lastSeenAtClient: new Date().toISOString(),
          lastScanAt: serverTimestamp(),
          scanIds: arrayUnion(scanId),
        },
        { merge: true }
      ).catch((error) =>
        console.warn("Unable to link saved scan to DoorScan visit:", error)
      );
    },
    [services]
  );

  return { getVisitSessionId, recordSavedScan };
}

function scanPathIdentity(path) {
  if (typeof path !== "string") return null;
  const parts = path.split("/");
  return parts.length === 4 &&
    parts[0] === SCAN_COLLECTION &&
    parts[2] === "scans" &&
    parts[1] &&
    parts[3]
    ? { ownerId: parts[1], id: parts[3], documentPath: path }
    : null;
}

function sessionPathIdentity(path) {
  if (typeof path !== "string") return null;
  const parts = path.split("/");
  return parts.length === 4 &&
    parts[0] === SCAN_COLLECTION &&
    parts[2] === "sessions" &&
    parts[1] &&
    parts[3]
    ? { ownerId: parts[1], id: parts[3], documentPath: path }
    : null;
}

function normaliseAdminScan(snapshot) {
  const identity = scanPathIdentity(snapshot?.ref?.path);
  if (!identity || snapshot.id !== identity.id) return null;
  const data = snapshot.data();
  if (!data || (data.ownerId != null && data.ownerId !== identity.ownerId))
    return null;
  return { ...data, ...identity };
}

function normaliseAdminSession(snapshot) {
  const identity = sessionPathIdentity(snapshot?.ref?.path);
  if (!identity || snapshot.id !== identity.id) return null;
  const data = snapshot.data({ serverTimestamps: "estimate" });
  if (!data) return null;
  if (data.ownerId != null && data.ownerId !== identity.ownerId) return null;
  if (data.visitorUid != null && data.visitorUid !== identity.ownerId)
    return null;
  return {
    ...data,
    ownerId: identity.ownerId,
    visitorUid: identity.ownerId,
    visitSessionId: data.visitSessionId || identity.id,
    id: identity.id,
    documentPath: identity.documentPath,
  };
}

async function loadAdminCollectionGroup(
  services,
  groupName,
  normalise,
  adminRole,
  signal
) {
  assertDoorScanAdminRole(adminRole, signal);
  const rows = [];
  let cursor = null;
  let capped = false;
  for (
    let pageIndex = 0;
    pageIndex < DOORSCAN_ADMIN_MAX_PAGES;
    pageIndex += 1
  ) {
    const constraints = [orderBy(documentId(), "asc")];
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(DOORSCAN_ADMIN_PAGE_SIZE));
    const snapshot = await getDocs(
      query(collectionGroup(services.db, groupName), ...constraints)
    );
    assertDoorScanAdminRole(adminRole, signal);
    snapshot.docs.forEach((item) => {
      const row = normalise(item);
      if (row) rows.push(row);
    });
    if (snapshot.docs.length < DOORSCAN_ADMIN_PAGE_SIZE) break;
    cursor = snapshot.docs[snapshot.docs.length - 1];
    if (pageIndex === DOORSCAN_ADMIN_MAX_PAGES - 1) capped = true;
  }
  return { rows, capped };
}

function normaliseAdminUserRole(value) {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, 120);
}

function useDoorScanCurrentUserRole(services, uid, authLoading) {
  const [state, setState] = useState({ role: "", loading: Boolean(uid) });

  useEffect(() => {
    setState({ role: "", loading: Boolean(uid) && !authLoading });
    if (authLoading || !uid || !services?.db) return undefined;
    if (services?.auth?.currentUser?.uid !== uid) {
      setState({ role: "", loading: false });
      return undefined;
    }

    const reference = doc(services.db, "users", uid);
    const unsubscribe = onSnapshot(
      reference,
      (snapshot) => {
        const role = snapshot.exists()
          ? normaliseAdminUserRole(snapshot.data()?.user_role)
          : "";
        setState({ role, loading: false });
      },
      (error) => {
        console.warn(`DoorScan could not read users/${uid}.user_role:`, error);
        setState({ role: "", loading: false });
      }
    );
    return unsubscribe;
  }, [services?.db, services?.auth, uid, authLoading]);

  return state;
}

function adminDatasetUids(sessions = [], scans = []) {
  return [
    ...new Set(
      [...sessions, ...scans]
        .map((item) => String(item?.ownerId || "").trim())
        .filter(Boolean)
    ),
  ];
}

function normaliseAdminUserProfile(data) {
  if (!data || typeof data !== "object")
    return { name: "", displayName: "", userRole: "" };
  const cleanText = (value, max = 160) =>
    typeof value === "string" ? value.trim().slice(0, max) : "";
  return {
    name: cleanText(data.name),
    displayName: cleanText(data.displayName),
    userRole: normaliseAdminUserRole(data.user_role),
  };
}

async function loadAdminUserProfiles(services, uids, adminRole, signal) {
  assertDoorScanAdminRole(adminRole, signal);
  const profiles = Object.create(null);
  let unavailable = false;
  const uniqueUids = [...new Set((uids || []).map(String).filter(Boolean))];

  for (let start = 0; start < uniqueUids.length; start += 20) {
    const batch = uniqueUids.slice(start, start + 20);
    const rows = await Promise.all(
      batch.map(async (uid) => {
        try {
          const snapshot = await getDoc(doc(services.db, "users", uid));
          assertDoorScanAdminRole(adminRole, signal);
          return [
            uid,
            snapshot.exists()
              ? normaliseAdminUserProfile(snapshot.data())
              : normaliseAdminUserProfile(null),
          ];
        } catch (error) {
          assertDoorScanAdminRole(adminRole, signal);
          if (String(error?.code || "").includes("permission-denied")) {
            unavailable = true;
            return [uid, normaliseAdminUserProfile(null)];
          }
          console.warn(`DoorScan could not read users/${uid} profile:`, error);
          return [uid, normaliseAdminUserProfile(null)];
        }
      })
    );
    rows.forEach(([uid, profile]) => {
      profiles[uid] = profile;
    });
  }
  return { profiles, unavailable };
}

async function loadAdminUsersDataset(services, adminRole, signal) {
  assertDoorScanAdminRole(adminRole, signal);
  const [sessionData, scanData] = await Promise.all([
    loadAdminCollectionGroup(
      services,
      "sessions",
      normaliseAdminSession,
      adminRole,
      signal
    ),
    loadAdminCollectionGroup(
      services,
      "scans",
      normaliseAdminScan,
      adminRole,
      signal
    ),
  ]);
  assertDoorScanAdminRole(adminRole, signal);
  const sessions = sessionData.rows;
  const userProfileData = await loadAdminUserProfiles(
    services,
    adminDatasetUids(sessions, scanData.rows),
    adminRole,
    signal
  );
  assertDoorScanAdminRole(adminRole, signal);
  return {
    sessions,
    scans: scanData.rows,
    userProfiles: userProfileData.profiles,
    profilesUnavailable: userProfileData.unavailable,
    capped: sessionData.capped || scanData.capped,
  };
}

async function loadAdminScan(services, row, adminRole, signal) {
  assertDoorScanAdminRole(adminRole, signal);
  const identity = scanPathIdentity(row?.documentPath);
  if (!identity || identity.id !== row.id || identity.ownerId !== row.ownerId)
    throw new Error("Invalid scan path or owner.");
  const snapshot = await getDoc(doc(services.db, identity.documentPath));
  assertDoorScanAdminRole(adminRole, signal);
  if (!snapshot.exists()) throw new Error("This scan no longer exists.");
  const record = normaliseAdminScan(snapshot);
  if (!record) throw new Error("The scan owner could not be verified.");
  try {
    const chunks = await getDocs(
      query(
        collection(doc(services.db, identity.documentPath), "resultChunks"),
        orderBy("index", "asc")
      )
    );
    assertDoorScanAdminRole(adminRole, signal);
    return {
      record,
      ...decodeScanPredictions(
        record,
        chunks.docs.map((item) => ({ ...item.data(), id: item.id }))
      ),
    };
  } catch (error) {
    assertDoorScanAdminRole(adminRole, signal);
    return {
      record,
      predictions: {},
      errors: { scan: friendlyError(error) },
    };
  }
}

function sessionReferenceTime(session) {
  return (
    analyticsMillis(session.lastSeenAt) ||
    analyticsMillis(session.lastSeenAtClient) ||
    analyticsMillis(session.endedAt) ||
    analyticsMillis(session.startedAt) ||
    analyticsMillis(session.startedAtClient)
  );
}

function scanReferenceTime(scan) {
  return analyticsMillis(scan.createdAt) || analyticsMillis(scan.updatedAt);
}

function buildAdminUsers(sessions = [], scans = [], userProfiles = {}) {
  const users = new Map();
  const ensure = (uid) => {
    if (!users.has(uid))
      users.set(uid, {
        uid,
        sessions: [],
        scans: [],
        unlinkedScans: [],
        account: null,
        lastLocation: null,
        lastClient: null,
        firstSeenMs: 0,
        lastSeenMs: 0,
        activeMs: 0,
        devices: [],
        profile: normaliseAdminUserProfile(userProfiles?.[uid]),
        userRole: normaliseAdminUserRole(userProfiles?.[uid]?.userRole),
      });
    return users.get(uid);
  };

  sessions.forEach((session) => {
    if (!session?.ownerId) return;
    ensure(session.ownerId).sessions.push({ ...session, scans: [] });
  });
  scans.forEach((scan) => {
    if (!scan?.ownerId) return;
    ensure(scan.ownerId).scans.push(scan);
  });

  return [...users.values()]
    .map((user) => {
      user.sessions.sort(
        (a, b) => sessionReferenceTime(b) - sessionReferenceTime(a)
      );
      user.scans.sort((a, b) => scanReferenceTime(b) - scanReferenceTime(a));
      const sessionMap = new Map();
      const scanSessionMap = new Map();
      user.sessions.forEach((session) => {
        [session.id, session.visitSessionId, session.sessionId]
          .filter(Boolean)
          .forEach((key) => sessionMap.set(String(key), session));
        (Array.isArray(session.scanIds) ? session.scanIds : []).forEach(
          (scanId) => scanSessionMap.set(String(scanId), session)
        );
      });
      user.scans.forEach((scan) => {
        const session =
          (scan.visitSessionId
            ? sessionMap.get(String(scan.visitSessionId))
            : null) || scanSessionMap.get(String(scan.id));
        if (session) session.scans.push(scan);
        else user.unlinkedScans.push(scan);
      });
      user.sessions.forEach((session) =>
        session.scans.sort(
          (a, b) => scanReferenceTime(a) - scanReferenceTime(b)
        )
      );

      const accountSession = user.sessions.find(
        (session) =>
          session.account?.displayName ||
          session.account?.email ||
          session.account?.phoneNumber
      );
      user.account = accountSession?.account || null;
      user.lastLocation =
        user.sessions.find((session) =>
          analyticsLocationHasUsefulData(session.location)
        )?.location || null;
      user.lastClient =
        user.sessions.find((session) => session.client)?.client || null;
      user.activeMs = user.sessions.reduce(
        (sum, session) => sum + Math.max(0, Number(session.activeMs) || 0),
        0
      );

      const times = [
        ...user.sessions.flatMap((session) => [
          analyticsMillis(session.startedAt) ||
            analyticsMillis(session.startedAtClient),
          sessionReferenceTime(session),
        ]),
        ...user.scans.flatMap((scan) => [scanReferenceTime(scan)]),
      ].filter(Boolean);
      user.firstSeenMs = times.length ? Math.min(...times) : 0;
      user.lastSeenMs = times.length ? Math.max(...times) : 0;

      const deviceMap = new Map();
      user.sessions.forEach((session) => {
        const client = session.client || {};
        const key =
          [
            client.deviceType,
            client.browser,
            client.operatingSystem || client.platform,
          ]
            .filter(Boolean)
            .join(" · ") || "Unknown device";
        const existing = deviceMap.get(key) || {
          label: key,
          sessions: 0,
          lastSeenMs: 0,
        };
        existing.sessions += 1;
        existing.lastSeenMs = Math.max(
          existing.lastSeenMs,
          sessionReferenceTime(session)
        );
        deviceMap.set(key, existing);
      });
      user.devices = [...deviceMap.values()].sort(
        (a, b) => b.sessions - a.sessions || b.lastSeenMs - a.lastSeenMs
      );
      return user;
    })
    .sort((a, b) => b.lastSeenMs - a.lastSeenMs || a.uid.localeCompare(b.uid));
}

function adminUserName(user) {
  const profile = user?.profile || {};
  const account = user?.account || {};
  if (profile.name) return profile.name;
  if (profile.displayName) return profile.displayName;
  if (account.displayName) return account.displayName;
  if (account.email) return account.email.split("@")[0];
  return user?.uid ? `User ${user.uid.slice(0, 8)}` : "Unknown user";
}

function adminUserSearchText(user) {
  return [
    user?.uid,
    user?.profile?.name,
    user?.profile?.displayName,
    user?.account?.displayName,
    user?.account?.email,
    user?.account?.phoneNumber,
    analyticsLocationLabel(user?.lastLocation),
    user?.lastClient?.browser,
    user?.lastClient?.operatingSystem,
    user?.lastClient?.platform,
    user?.userRole,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function sessionStatus(session, nowMs) {
  if (session?.endedAt) return { key: "closed", label: "Closed" };
  const lastSeen =
    analyticsMillis(session?.lastSeenAt) ||
    analyticsMillis(session?.lastSeenAtClient);
  const heartbeatFresh = Boolean(
    lastSeen && nowMs - lastSeen <= DOORSCAN_VISIT_HEARTBEAT_MS * 4
  );
  const visible = session?.pageVisible !== false;
  return heartbeatFresh && visible
    ? { key: "active", label: "Active" }
    : { key: "closed", label: "Closed" };
}

function sessionLiveTiming(session, nowMs) {
  const baseActive = Math.max(0, Number(session?.activeMs) || 0);
  const baseElapsed = Math.max(0, Number(session?.elapsedMs) || 0);
  const started =
    analyticsMillis(session?.startedAt) ||
    analyticsMillis(session?.startedAtClient);
  const lastSeen =
    analyticsMillis(session?.lastSeenAt) ||
    analyticsMillis(session?.lastSeenAtClient);
  const state = sessionStatus(session, nowMs);
  if (state.key !== "active")
    return { activeMs: baseActive, elapsedMs: baseElapsed };

  const elapsedMs = started
    ? Math.max(baseElapsed, nowMs - started)
    : baseElapsed;
  const liveDelta = lastSeen
    ? Math.max(0, Math.min(nowMs - lastSeen, DOORSCAN_VISIT_HEARTBEAT_MS * 3))
    : 0;
  return {
    elapsedMs,
    activeMs:
      session?.pageVisible === false ? baseActive : baseActive + liveDelta,
  };
}

function userLiveActiveMs(user, nowMs) {
  return (user?.sessions || []).reduce(
    (sum, session) => sum + sessionLiveTiming(session, nowMs).activeMs,
    0
  );
}

function userHasActiveSession(user, nowMs) {
  return (user?.sessions || []).some(
    (session) => sessionStatus(session, nowMs).key === "active"
  );
}

function userLatestScanMs(user) {
  return Math.max(
    0,
    ...(user?.scans || []).map((scan) => scanReferenceTime(scan) || 0)
  );
}

function adminCountryName(location) {
  if (!location || typeof location !== "object") return "";
  return String(location.countryName || location.country || "").trim();
}

function adminDateBounds(mode, fromValue, toValue, nowMs) {
  if (mode === "all") return { start: 0, end: Number.POSITIVE_INFINITY };
  const now = new Date(nowMs);
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).getTime();
  if (mode === "today")
    return { start: todayStart, end: todayStart + 86400000 - 1 };
  if (mode === "7d") return { start: todayStart - 6 * 86400000, end: nowMs };
  if (mode === "30d") return { start: todayStart - 29 * 86400000, end: nowMs };
  if (mode === "custom") {
    const start = fromValue ? new Date(`${fromValue}T00:00:00`).getTime() : 0;
    const end = toValue
      ? new Date(`${toValue}T23:59:59.999`).getTime()
      : Number.POSITIVE_INFINITY;
    return {
      start: Number.isFinite(start) ? start : 0,
      end: Number.isFinite(end) ? end : Number.POSITIVE_INFINITY,
    };
  }
  return { start: 0, end: Number.POSITIVE_INFINITY };
}

function adminTimeInBounds(value, bounds) {
  const time = Number(value) || 0;
  return Boolean(time && time >= bounds.start && time <= bounds.end);
}

function adminSessionCountry(session) {
  return adminCountryName(session?.location);
}

function adminSessionKeys(session) {
  return [session?.id, session?.visitSessionId, session?.sessionId]
    .filter(Boolean)
    .map(String);
}

function adminUserView(user, filters, bounds, nowMs) {
  if (!user) return null;
  const term = filters.search.trim().toLowerCase();
  if (term && !adminUserSearchText(user).includes(term)) return null;

  if (filters.role === "__none__" && user.userRole) return null;
  if (
    filters.role !== "all" &&
    filters.role !== "__none__" &&
    user.userRole !== filters.role
  )
    return null;

  const active = userHasActiveSession(user, nowMs);
  if (filters.status === "active" && !active) return null;
  if (filters.status === "closed" && active) return null;

  let sessions = user.sessions.filter((session) =>
    adminTimeInBounds(
      analyticsMillis(session.startedAt) ||
        analyticsMillis(session.startedAtClient) ||
        sessionReferenceTime(session),
      bounds
    )
  );

  if (filters.country !== "all")
    sessions = sessions.filter(
      (session) => adminSessionCountry(session) === filters.country
    );

  const matchingSessionKeys = new Set(sessions.flatMap(adminSessionKeys));
  const matchingSessionScanIds = new Set(
    sessions.flatMap((session) =>
      (session.scans || []).map((scan) => String(scan.id))
    )
  );
  let scans = user.scans.filter((scan) =>
    adminTimeInBounds(scanReferenceTime(scan), bounds)
  );
  if (filters.country !== "all") {
    scans = scans.filter(
      (scan) =>
        matchingSessionScanIds.has(String(scan.id)) ||
        (scan.visitSessionId &&
          matchingSessionKeys.has(String(scan.visitSessionId)))
    );
  }

  const hasActivityInRange = sessions.length > 0 || scans.length > 0;
  if (
    !hasActivityInRange &&
    (filters.date !== "all" || filters.country !== "all")
  )
    return null;
  if (filters.scans === "with" && scans.length === 0) return null;
  if (filters.scans === "without" && scans.length > 0) return null;

  const latestSession =
    [...sessions].sort(
      (a, b) => sessionReferenceTime(b) - sessionReferenceTime(a)
    )[0] || null;
  const latestScan = scans[0] || null;
  return {
    ...user,
    filteredSessions: sessions,
    filteredScans: scans,
    filteredActiveMs: sessions.reduce(
      (sum, session) => sum + sessionLiveTiming(session, nowMs).activeMs,
      0
    ),
    filteredLastLocation: latestSession?.location || user.lastLocation,
    filteredLastClient: latestSession?.client || user.lastClient,
    filteredLastSeenMs:
      Math.max(
        0,
        latestSession ? sessionReferenceTime(latestSession) : 0,
        latestScan ? scanReferenceTime(latestScan) : 0
      ) || user.lastSeenMs,
    active,
    latestScanMs: Math.max(
      0,
      ...scans.map((scan) => scanReferenceTime(scan) || 0)
    ),
    latestActivityMs: Math.max(
      0,
      ...sessions.map((session) => sessionReferenceTime(session) || 0),
      ...scans.map((scan) => scanReferenceTime(scan) || 0)
    ),
  };
}

function scanUploadOffset(scan, session) {
  const scanTime = analyticsMillis(scan?.createdAt);
  const sessionTime =
    analyticsMillis(session?.startedAt) ||
    analyticsMillis(session?.startedAtClient);
  if (!scanTime || !sessionTime || scanTime < sessionTime) return "";
  return `${formatAnalyticsDuration(
    scanTime - sessionTime
  )} after session start`;
}

function AdminScanViewer({ services, row, adminRole, onBack }) {
  const [state, setState] = useState({
    loading: true,
    record: null,
    predictions: {},
    errors: {},
    error: "",
  });
  const [modal, setModal] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({
      loading: true,
      record: null,
      predictions: {},
      errors: {},
      error: "",
    });
    loadAdminScan(services, row, adminRole, controller.signal)
      .then((result) => {
        if (active && !controller.signal.aborted)
          setState({ ...result, loading: false, error: "" });
      })
      .catch((error) => {
        if (active && !controller.signal.aborted)
          setState({
            loading: false,
            record: null,
            predictions: {},
            errors: {},
            error: friendlyError(error),
          });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [services, row.documentPath, row.ownerId, row.id, adminRole, retry]);

  if (!isDoorScanAdminRole(adminRole)) return null;

  const record = state.record;
  const extras = Array.isArray(record?.supplemental) ? record.supplemental : [];
  const entries = [
    ...Object.entries(record?.photos || {}),
    ...extras.map((item) => [item.id, item]),
  ];
  const sources = Object.fromEntries(
    entries.map(([key, item]) => [key, savedImageUrl(item)])
  );
  const observations = Array.isArray(record?.observations)
    ? record.observations
    : [];
  const checklist = (() => {
    const saved = record?.checklist?.sections;
    if (
      Array.isArray(saved) &&
      saved.length &&
      saved.every((section) => section?.id && Array.isArray(section.checks))
    )
      return saved;
    if (!record) return [];
    return buildSectionChecklist({
      predictions: state.predictions,
      sources,
      extras,
      observations,
      hingeConfiguration: record.hinges || {},
      certificateDecision: record.certification || null,
    });
  })();

  const context = (
    <div className="ds-admin-scan-context">
      <button type="button" className="ds-text-button" onClick={onBack}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to user
      </button>
      {/* <div>
        <strong>{record?.doorName || row.doorName || "Door scan"}</strong>
        <span>UID {row.ownerId}</span>
        <span>Scan {row.id} · read-only admin view</span>
      </div> */}
      {state.errors.scan && (
        <p role="alert" className="ds-inline-error">
          Photos are available, but some saved annotations could not load:{" "}
          {state.errors.scan}
        </p>
      )}
    </div>
  );

  if (state.loading || state.error)
    return (
      <section className="ds-users-panel" aria-label="Admin scan preview">
        {context}
        {state.loading ? (
          <p role="status" className="ds-muted">
            Opening saved scan...
          </p>
        ) : (
          <>
            <p className="ds-inline-error" role="alert">
              {state.error}
            </p>
            <button
              type="button"
              className="ds-button ds-button-secondary"
              onClick={() => setRetry((value) => value + 1)}
            >
              Retry
            </button>
          </>
        )}
      </section>
    );

  return (
    <>
      <ResultsView
        sources={sources}
        predictions={state.predictions}
        extras={extras}
        statuses={Object.fromEntries(
          Object.entries(state.errors).map(([key, error]) => [key, { error }])
        )}
        savedRecord={record}
        checklist={checklist}
        observations={observations}
        busy={false}
        dirty={false}
        blocked={Boolean(modal)}
        readOnly
        historyContext={context}
        onPhotos={() => setModal("photos")}
        onReview={() => setModal("final")}
      />
      {modal === "final" && (
        <Sheet title="Saved final results" onClose={() => setModal(null)}>
          <FinalResults
            sections={checklist}
            readOnly
            dirty={false}
            busy={false}
          />
        </Sheet>
      )}
      {modal === "photos" && (
        <Sheet title="Saved photos and evidence" onClose={() => setModal(null)}>
          <div className="ds-info-body">
            <p className="ds-muted">
              Read-only evidence for UID {row.ownerId}. Nothing here changes the
              owner's scan.
            </p>
            {entries.map(([key, item]) => (
              <div key={key} className="ds-section-attached">
                <strong>
                  {key === "certificate"
                    ? "Certificate"
                    : item.label || PHOTO_LABELS[key] || "Additional evidence"}
                </strong>
                {item.type === "document" ? (
                  sources[key] ? (
                    <a
                      className="ds-button ds-button-secondary"
                      href={sources[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open saved document
                    </a>
                  ) : (
                    <p>Document unavailable.</p>
                  )
                ) : sources[key] ? (
                  <EvidenceMediaPreview
                    evidenceKey={key}
                    sources={sources}
                    extras={extras}
                    type={item.type === "video" ? "video" : "photo"}
                    label={item.label || PHOTO_LABELS[key] || "Saved evidence"}
                  />
                ) : (
                  <p className="ds-muted">Saved media URL unavailable.</p>
                )}
                {item.note && <p>{item.note}</p>}
              </div>
            ))}
            {!entries.length && (
              <p>No uploaded files are available for this scan.</p>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}

function AdminDoorRow({ scan, session = null, onOpen }) {
  const offset = session ? scanUploadOffset(scan, session) : "";
  return (
    <button
      type="button"
      className="ds-scan-row ds-user-door-row"
      onClick={() => onOpen(scan)}
    >
      <EvidenceThumbnail
        src={
          savedImageUrl(scan.photos?.front) || savedImageUrl(scan.photos?.back)
        }
        label={scan.doorName || "Saved door"}
      />
      <span>
        <strong>{scan.doorName || "Door scan"}</strong>
        <small>
          {analyticsDateTimeLabel(scan.createdAt || scan.updatedAt)}
        </small>
        <small>
          {Object.keys(scan.photos || {}).length +
            (Array.isArray(scan.supplemental)
              ? scan.supplemental.length
              : 0)}{" "}
          uploaded files{offset ? ` · ${offset}` : ""}
        </small>
      </span>
      <FontAwesomeIcon icon={faChevronRight} />
    </button>
  );
}

function SessionTechnicalDetails({ session }) {
  const client = session.client || {};
  const location = session.location || {};
  const network = client.network || {};
  const screen = client.screen || {};
  const viewport = client.viewport || {};
  const rows = [
    ["Session ID", session.id],
    ["Firebase UID", session.ownerId],
    [
      "Started",
      analyticsDateTimeLabel(session.startedAt || session.startedAtClient),
    ],
    ["Last seen", analyticsDateTimeLabel(session.lastSeenAt)],
    ["Ended", analyticsDateTimeLabel(session.endedAt)],
    ["Public IP", location.ip || "Not recorded"],
    [
      "Approximate coordinates",
      location.latitude != null && location.longitude != null
        ? `${location.latitude}, ${location.longitude}`
        : "Not recorded",
    ],
    ["Location provider", location.source || "Not recorded"],
    ["ASN", location.asn || "Not recorded"],
    [
      "Network organisation",
      location.organisation || location.isp || "Not recorded",
    ],
    ["Browser", client.browser || "Not recorded"],
    [
      "Operating system",
      client.operatingSystem || client.platform || "Not recorded",
    ],
    ["Device type", client.deviceType || "Not recorded"],
    [
      "Memory",
      client.approximateMemoryGb != null
        ? `~${client.approximateMemoryGb} GB`
        : "Not exposed",
    ],
    ["Logical CPU cores", client.cpuCores ?? "Not exposed"],
    [
      "Display",
      screen.width && screen.height
        ? `${screen.width} × ${screen.height}`
        : "Not recorded",
    ],
    [
      "Viewport",
      viewport.width && viewport.height
        ? `${viewport.width} × ${viewport.height}`
        : "Not recorded",
    ],
    ["Device pixel ratio", screen.devicePixelRatio ?? "Not recorded"],
    ["Connection", network.effectiveType || "Not exposed"],
    [
      "Downlink",
      network.downlinkMbps != null
        ? `${network.downlinkMbps} Mbps`
        : "Not exposed",
    ],
    ["RTT", network.rttMs != null ? `${network.rttMs} ms` : "Not exposed"],
    ["Language", client.language || "Not recorded"],
    ["Timezone", location.timezone || client.timezone || "Not recorded"],
    ["Referrer", client.referrer || "Direct / not supplied"],
  ];
  return (
    <details className="ds-session-technical">
      <summary>Technical details</summary>
      <dl className="ds-technical-grid">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
      </dl>
      {Array.isArray(location.failures) && location.failures.length > 0 && (
        <div className="ds-location-failures">
          <strong>Location lookup attempts</strong>
          {location.failures.map((failure, index) => (
            <span key={`${failure.provider}-${index}`}>
              {failure.provider}: {failure.reason}
            </span>
          ))}
        </div>
      )}
      <details className="ds-raw-details">
        <summary>Raw recorded fields</summary>
        <pre>{JSON.stringify(session, null, 2)}</pre>
      </details>
    </details>
  );
}

function AdminUserDetail({ user, nowMs, onBack, onOpenScan }) {
  const activeSession =
    user.sessions.find(
      (session) => sessionStatus(session, nowMs).key === "active"
    ) || null;
  const activeTiming = activeSession
    ? sessionLiveTiming(activeSession, nowMs)
    : null;
  const dayGroups = useMemo(() => {
    const map = new Map();
    user.sessions.forEach((session) => {
      const start =
        session.startedAt || session.startedAtClient || session.lastSeenAt;
      const key = start ? analyticsLocalDayKey(start) : "unknown";
      if (!map.has(key)) map.set(key, { key, start, sessions: [] });
      map.get(key).sessions.push(session);
    });
    return [...map.values()].sort(
      (a, b) => analyticsMillis(b.start) - analyticsMillis(a.start)
    );
  }, [user]);

  return (
    <section
      className="ds-users-panel ds-user-detail"
      aria-label={`User ${user.uid}`}
    >
      <header className="ds-users-heading">
        <button
          type="button"
          className="ds-icon-button"
          onClick={onBack}
          aria-label="Back to users"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div>
          <p className="ds-eyebrow">USER ACTIVITY</p>
          <h1>{adminUserName(user)}</h1>
          <span>
            {user.account?.email || user.account?.phoneNumber || user.uid}
          </span>
        </div>
      </header>

      {/* <div className="ds-user-identity">
        <span>UID</span>
        <strong>{user.uid}</strong>
        <small>
          {user.sessions.some((session) => session.visitorAnonymous)
            ? "Includes anonymous session"
            : "Signed-in account"}
        </small>
        <span
          className="ds-user-role-badge"
          title="Loaded from users/{uid}.user_role"
        >
          Role · {user.userRole || "Not set"}
        </span>
      </div> */}

      {activeSession && activeTiming && (
        <div className="ds-user-live-banner" role="status">
          <span className="ds-live-dot" aria-hidden="true" />
          <div>
            <strong>Active now</strong>
            <span>
              {formatAnalyticsDuration(activeTiming.activeMs)} active this visit
            </span>
          </div>
        </div>
      )}

      <div className="ds-user-metrics">
        <div>
          <strong>{user.sessions.length}</strong>
          <span>Sessions</span>
        </div>
        <div>
          <strong>{user.scans.length}</strong>
          <span>Doors</span>
        </div>
        <div>
          <strong>
            {formatAnalyticsDuration(userLiveActiveMs(user, nowMs))}
          </strong>
          <span>Active time</span>
        </div>
        <div>
          <strong>
            {userHasActiveSession(user, nowMs) ? "Active" : "Closed"}
          </strong>
          <span>Status</span>
        </div>
      </div>

      <div className="ds-user-overview">
        <div>
          <span>Profile name</span>
          <strong>{adminUserName(user)}</strong>
        </div>
        <div>
          <span>Account</span>
          <strong>
            {[
              user.account?.displayName,
              user.account?.email,
              user.account?.phoneNumber,
            ]
              .filter(Boolean)
              .join(" · ") || "No account details recorded"}
          </strong>
        </div>
        {/* <div>
          <span>User role</span>
          <strong>{user.userRole || "Not set"}</strong>
        </div> */}
        <div>
          <span>First recorded</span>
          <strong>{analyticsDateTimeLabel(user.firstSeenMs)}</strong>
        </div>
        <div>
          <span>Last recorded</span>
          <strong>{analyticsDateTimeLabel(user.lastSeenMs)}</strong>
        </div>
        <div>
          <span>Last location</span>
          <strong>{analyticsLocationLabel(user.lastLocation)}</strong>
        </div>
        <div>
          <span>Latest device</span>
          <strong>
            {[
              user.lastClient?.deviceType,
              user.lastClient?.browser,
              user.lastClient?.operatingSystem || user.lastClient?.platform,
            ]
              .filter(Boolean)
              .join(" · ") || "Not recorded"}
          </strong>
        </div>
      </div>

      {user.devices.length > 0 && (
        <details className="ds-user-devices">
          <summary>Devices used ({user.devices.length})</summary>
          <div>
            {user.devices.map((device) => (
              <span key={device.label}>
                <strong>{device.label}</strong>
                <small>
                  {device.sessions} session{device.sessions === 1 ? "" : "s"}
                </small>
              </span>
            ))}
          </div>
        </details>
      )}

      <div className="ds-user-days">
        {dayGroups.map((day) => {
          const scans = day.sessions.reduce(
            (sum, session) => sum + session.scans.length,
            0
          );
          const active = day.sessions.reduce(
            (sum, session) => sum + sessionLiveTiming(session, nowMs).activeMs,
            0
          );
          return (
            <section className="ds-user-day" key={day.key}>
              <header>
                <div>
                  <strong>{analyticsDayLabel(day.start)}</strong>
                  <span>
                    {day.sessions.length} session
                    {day.sessions.length === 1 ? "" : "s"} · {scans} door
                    {scans === 1 ? "" : "s"} · {formatAnalyticsDuration(active)}{" "}
                    active
                  </span>
                </div>
              </header>
              <div className="ds-session-list">
                {day.sessions.map((session) => {
                  const state = sessionStatus(session, nowMs);
                  const liveTiming = sessionLiveTiming(session, nowMs);
                  const client = session.client || {};
                  const location = session.location || {};
                  return (
                    <details
                      className={`ds-session-card${
                        state.key === "active" ? " is-active-session" : ""
                      }`}
                      key={session.id}
                    >
                      <summary>
                        <span className="ds-session-time">
                          {analyticsTimeLabel(
                            session.startedAt || session.startedAtClient
                          )}
                        </span>
                        <span className={`ds-session-state is-${state.key}`}>
                          {state.label}
                        </span>
                        <strong>
                          {formatAnalyticsDuration(liveTiming.elapsedMs)}{" "}
                          elapsed ·{" "}
                          {formatAnalyticsDuration(liveTiming.activeMs)} active
                        </strong>
                        <small>
                          {session.scans.length} door
                          {session.scans.length === 1 ? "" : "s"} ·{" "}
                          {analyticsLocationLabel(location)}
                        </small>
                      </summary>
                      <div className="ds-session-body">
                        <div className="ds-session-summary-grid">
                          <div>
                            <span>Started</span>
                            <strong>
                              {analyticsDateTimeLabel(
                                session.startedAt || session.startedAtClient
                              )}
                            </strong>
                          </div>
                          <div>
                            <span>Last seen</span>
                            <strong>
                              {analyticsDateTimeLabel(session.lastSeenAt)}
                            </strong>
                          </div>
                          <div>
                            <span>Device</span>
                            <strong>
                              {[
                                client.deviceType,
                                client.browser,
                                client.operatingSystem || client.platform,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "Not recorded"}
                            </strong>
                          </div>
                          <div>
                            <span>Location</span>
                            <strong>{analyticsLocationLabel(location)}</strong>
                          </div>
                        </div>

                        <div className="ds-session-doors">
                          <div className="ds-session-section-title">
                            <strong>Doors uploaded in this session</strong>
                            <span>{session.scans.length}</span>
                          </div>
                          {session.scans.length ? (
                            session.scans.map((scan) => (
                              <AdminDoorRow
                                key={scan.documentPath}
                                scan={scan}
                                session={session}
                                onOpen={onOpenScan}
                              />
                            ))
                          ) : (
                            <p className="ds-muted">
                              No completed door scan is linked to this visit.
                            </p>
                          )}
                        </div>
                        {/* <SessionTechnicalDetails session={session} /> */}
                      </div>
                    </details>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {user.unlinkedScans.length > 0 && (
        <section className="ds-unlinked-scans">
          <header>
            <strong>Earlier / unlinked saved doors</strong>
            <span>
              These scans pre-date visit linking or were saved without a visit
              session ID.
            </span>
          </header>
          {user.unlinkedScans.map((scan) => (
            <AdminDoorRow
              key={scan.documentPath}
              scan={scan}
              onOpen={onOpenScan}
            />
          ))}
        </section>
      )}
    </section>
  );
}

function AdminUsersPanel({ services, busy, adminRole }) {
  const [state, setState] = useState({
    sessions: [],
    scans: [],
    loading: true,
    error: "",
    capped: false,
    userProfiles: {},
    profilesUnavailable: false,
  });
  const [selectedUid, setSelectedUid] = useState("");
  const [selectedScan, setSelectedScan] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    country: "all",
    date: "all",
    from: "",
    to: "",
    status: "all",
    scans: "all",
    role: "all",
    sort: "activity",
  });
  const [nowMs, setNowMs] = useState(Date.now());
  const requestRef = useRef(0);

  const setFilter = useCallback((key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  }, []);

  const load = useCallback(async () => {
    if (!isDoorScanAdminRole(adminRole)) return;
    const request = ++requestRef.current;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    try {
      const data = await loadAdminUsersDataset(
        services,
        adminRole,
        controller.signal
      );
      if (request !== requestRef.current || controller.signal.aborted) return;
      setState({ ...data, loading: false, error: "" });
    } catch (error) {
      if (request !== requestRef.current || controller.signal.aborted) return;
      setState((previous) => ({
        ...previous,
        loading: false,
        error: String(error?.code || "").includes("permission-denied")
          ? "Users history was denied by Firestore. Accounts with users/{uid}.user_role = admin must have read access to DoorScan sessions, scans, resultChunks and user profiles."
          : friendlyError(error),
      }));
    }
  }, [services, adminRole]);

  useEffect(() => {
    void load();
    return () => {
      requestRef.current += 1;
    };
  }, [load]);

  useEffect(() => {
    if (!isDoorScanAdminRole(adminRole) || !services?.db) return undefined;
    const liveLimit = DOORSCAN_ADMIN_PAGE_SIZE * DOORSCAN_ADMIN_MAX_PAGES;
    const liveError = (error) => {
      console.warn("DoorScan live admin feed unavailable:", error);
    };
    const unsubscribeSessions = onSnapshot(
      query(collectionGroup(services.db, "sessions"), limit(liveLimit)),
      (snapshot) => {
        if (!isDoorScanAdminRole(adminRole)) return;
        const current = snapshot.docs
          .map(normaliseAdminSession)
          .filter(Boolean);
        setState((previous) => {
          if (snapshot.metadata.fromCache && previous.sessions.length)
            return previous;
          return { ...previous, sessions: current };
        });
      },
      liveError
    );
    const unsubscribeScans = onSnapshot(
      query(collectionGroup(services.db, "scans"), limit(liveLimit)),
      (snapshot) => {
        if (!isDoorScanAdminRole(adminRole)) return;
        const current = snapshot.docs.map(normaliseAdminScan).filter(Boolean);
        setState((previous) => {
          if (snapshot.metadata.fromCache && previous.scans.length)
            return previous;
          return { ...previous, scans: current };
        });
      },
      liveError
    );
    return () => {
      unsubscribeSessions();
      unsubscribeScans();
    };
  }, [services, adminRole]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!isDoorScanAdminRole(adminRole) || !services?.db) return undefined;
    const missing = adminDatasetUids(state.sessions, state.scans).filter(
      (uid) => !Object.prototype.hasOwnProperty.call(state.userProfiles, uid)
    );
    if (!missing.length) return undefined;
    const controller = new AbortController();
    loadAdminUserProfiles(services, missing, adminRole, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setState((previous) => ({
          ...previous,
          userProfiles: { ...previous.userProfiles, ...result.profiles },
          profilesUnavailable:
            previous.profilesUnavailable || result.unavailable,
        }));
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          console.warn("DoorScan admin role lookup failed:", error);
      });
    return () => controller.abort();
  }, [services, adminRole, state.sessions, state.scans, state.userProfiles]);

  const users = useMemo(
    () => buildAdminUsers(state.sessions, state.scans, state.userProfiles),
    [state.sessions, state.scans, state.userProfiles]
  );

  const countries = useMemo(() => {
    const values = new Set();
    state.sessions.forEach((session) => {
      const country = adminSessionCountry(session);
      if (country) values.add(country);
    });
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [state.sessions]);

  const roles = useMemo(() => {
    const values = new Set(users.map((user) => user.userRole).filter(Boolean));
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [users]);

  const bounds = useMemo(
    () => adminDateBounds(filters.date, filters.from, filters.to, nowMs),
    [filters.date, filters.from, filters.to, nowMs]
  );

  const visibleUsers = useMemo(() => {
    const rows = users
      .map((user) => adminUserView(user, filters, bounds, nowMs))
      .filter(Boolean);
    rows.sort((a, b) => {
      if (filters.sort === "scan")
        return (
          b.latestScanMs - a.latestScanMs ||
          b.latestActivityMs - a.latestActivityMs
        );
      if (filters.sort === "doors")
        return (
          b.filteredScans.length - a.filteredScans.length ||
          b.latestActivityMs - a.latestActivityMs
        );
      if (filters.sort === "active")
        return (
          b.filteredActiveMs - a.filteredActiveMs ||
          b.latestActivityMs - a.latestActivityMs
        );
      return (
        b.latestActivityMs - a.latestActivityMs || a.uid.localeCompare(b.uid)
      );
    });
    return rows;
  }, [users, filters, bounds, nowMs]);

  const selectedUser = users.find((entry) => entry.uid === selectedUid) || null;
  const activeNow = visibleUsers.filter((entry) => entry.active).length;
  const visibleSessionCount = visibleUsers.reduce(
    (sum, entry) => sum + entry.filteredSessions.length,
    0
  );
  const visibleScanCount = visibleUsers.reduce(
    (sum, entry) => sum + entry.filteredScans.length,
    0
  );
  const visibleActiveMs = visibleUsers.reduce(
    (sum, entry) => sum + entry.filteredActiveMs,
    0
  );
  const filtersActive =
    filters.search ||
    filters.country !== "all" ||
    filters.date !== "all" ||
    filters.status !== "all" ||
    filters.scans !== "all" ||
    filters.role !== "all" ||
    filters.sort !== "activity";

  const clearFilters = () =>
    setFilters({
      search: "",
      country: "all",
      date: "all",
      from: "",
      to: "",
      status: "all",
      scans: "all",
      role: "all",
      sort: "activity",
    });

  if (!isDoorScanAdminRole(adminRole)) return null;
  if (selectedScan)
    return (
      <AdminScanViewer
        services={services}
        row={selectedScan}
        adminRole={adminRole}
        onBack={() => setSelectedScan(null)}
      />
    );
  if (selectedUser)
    return (
      <AdminUserDetail
        user={selectedUser}
        nowMs={nowMs}
        onBack={() => setSelectedUid("")}
        onOpenScan={setSelectedScan}
      />
    );

  return (
    <section
      className="ds-users-panel ds-admin-dashboard"
      aria-label="All DoorScan users"
    >
      <header className="ds-users-heading ds-dashboard-heading">
        <div>
          <p className="ds-eyebrow">ADMIN DASHBOARD</p>
          <h1>Users</h1>
          <span>Live activity, sessions and saved doors in one place.</span>
        </div>
        <button
          type="button"
          className="ds-text-button"
          disabled={busy || state.loading}
          onClick={load}
        >
          Refresh
        </button>
      </header>

      <div className="ds-dashboard-kpis" aria-label="Dashboard summary">
        <div className="is-live-kpi">
          <span className="ds-live-dot" aria-hidden="true" />
          <strong>{activeNow}</strong>
          <span>Active now</span>
        </div>
        <div>
          <strong>{visibleUsers.length}</strong>
          <span>Users</span>
        </div>
        <div>
          <strong>{visibleSessionCount}</strong>
          <span>Sessions</span>
        </div>
        <div>
          <strong>{visibleScanCount}</strong>
          <span>Doors</span>
        </div>
      </div>
      <div className="ds-dashboard-active-total">
        <span>Active time in this view</span>
        <strong>{formatAnalyticsDuration(visibleActiveMs)}</strong>
      </div>

      <div className="ds-admin-filter-card">
        <label className="ds-users-search">
          <span className="ds-filter-label">Search</span>
          <input
            type="search"
            value={filters.search}
            onChange={(event) => setFilter("search", event.target.value)}
            placeholder="Name, email, UID, city, country or device"
            autoComplete="off"
          />
        </label>

        <div
          className="ds-status-filter"
          role="group"
          aria-label="User status filter"
        >
          {[
            ["all", "All"],
            ["active", "Active"],
            ["closed", "Closed"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={filters.status === key}
              onClick={() => setFilter("status", key)}
            >
              {key === "active" && (
                <span className="ds-live-dot" aria-hidden="true" />
              )}
              {label}
            </button>
          ))}
        </div>

        <div className="ds-admin-filter-grid">
          <label>
            <span>Country</span>
            <select
              value={filters.country}
              onChange={(event) => setFilter("country", event.target.value)}
            >
              <option value="all">All countries</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Date</span>
            <select
              value={filters.date}
              onChange={(event) => setFilter("date", event.target.value)}
            >
              <option value="all">All time</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="custom">Custom dates</option>
            </select>
          </label>
          <label>
            <span>Doors</span>
            <select
              value={filters.scans}
              onChange={(event) => setFilter("scans", event.target.value)}
            >
              <option value="all">All users</option>
              <option value="with">Has scans</option>
              <option value="without">No scans</option>
            </select>
          </label>
          {/* <label>
            <span>Role</span>
            <select
              value={filters.role}
              onChange={(event) => setFilter("role", event.target.value)}
            >
              <option value="all">All roles</option>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
              <option value="__none__">Not set</option>
            </select>
          </label> */}
          <label>
            <span>Sort</span>
            <select
              value={filters.sort}
              onChange={(event) => setFilter("sort", event.target.value)}
            >
              <option value="activity">Latest activity</option>
              <option value="scan">Latest scan</option>
              <option value="doors">Most doors</option>
              <option value="active">Most active time</option>
            </select>
          </label>
        </div>

        {filters.date === "custom" && (
          <div className="ds-custom-date-filter">
            <label>
              <span>From</span>
              <input
                type="date"
                value={filters.from}
                onChange={(event) => setFilter("from", event.target.value)}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={filters.to}
                onChange={(event) => setFilter("to", event.target.value)}
              />
            </label>
          </div>
        )}

        <div className="ds-filter-result-line">
          <span>
            Showing <strong>{visibleUsers.length}</strong> of {users.length}{" "}
            users
          </span>
          {filtersActive && (
            <button
              type="button"
              className="ds-text-button"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {state.capped && (
        <p className="ds-admin-warning">
          The client-side safety cap was reached while loading global history.
          Up to 5,000 scan or session documents per collection group are shown.
        </p>
      )}
      {state.profilesUnavailable && (
        <p className="ds-admin-warning">
          User profiles could not be read. Allow admin accounts to read the
          required users/{"{uid}"} profile documents.
        </p>
      )}
      {state.error && (
        <p className="ds-inline-error" role="alert">
          {state.error}
        </p>
      )}
      {state.loading && (
        <p className="ds-muted" role="status">
          Loading users, sessions and saved doors...
        </p>
      )}

      <div className="ds-user-list">
        {visibleUsers.map((entry) => {
          const name = adminUserName(entry);
          const initial = name.trim().charAt(0).toUpperCase() || "U";
          const activeSession = entry.sessions.find(
            (session) => sessionStatus(session, nowMs).key === "active"
          );
          const activeTiming = activeSession
            ? sessionLiveTiming(activeSession, nowMs)
            : null;
          const latestScan = entry.filteredScans[0] || entry.scans[0] || null;
          return (
            <button
              type="button"
              className={`ds-user-card${entry.active ? " is-active-user" : ""}`}
              key={entry.uid}
              disabled={busy}
              onClick={() => setSelectedUid(entry.uid)}
            >
              <span className="ds-user-avatar" aria-hidden="true">
                {initial}
              </span>
              <span className="ds-user-card-copy">
                <span className="ds-user-card-title">
                  <strong>{name}</strong>
                  <em className={entry.active ? "is-active" : "is-closed"}>
                    {entry.active ? "Active" : "Closed"}
                  </em>
                </span>
                <small>
                  {entry.account?.email ||
                    entry.account?.phoneNumber ||
                    entry.uid}
                </small>
                <span
                  className="ds-user-role-badge"
                  title="users/{uid}.user_role"
                >
                  Role · {entry.userRole || "Not set"}
                </span>
                {entry.active && activeTiming && (
                  <span className="ds-user-live-line">
                    <span className="ds-live-dot" aria-hidden="true" />
                    Live now · {formatAnalyticsDuration(
                      activeTiming.activeMs
                    )}{" "}
                    active this visit
                  </span>
                )}
                <span className="ds-user-card-stats">
                  {entry.filteredSessions.length} session
                  {entry.filteredSessions.length === 1 ? "" : "s"} ·{" "}
                  {entry.filteredScans.length} door
                  {entry.filteredScans.length === 1 ? "" : "s"} ·{" "}
                  {formatAnalyticsDuration(entry.filteredActiveMs)} active
                </span>
                <small>
                  Last seen {analyticsDateTimeLabel(entry.filteredLastSeenMs)} ·{" "}
                  {analyticsLocationLabel(entry.filteredLastLocation)}
                </small>
                <small>
                  {latestScan
                    ? `Latest scan ${analyticsDateTimeLabel(
                        latestScan.createdAt || latestScan.updatedAt
                      )} · ${latestScan.doorName || "Door scan"}`
                    : "No saved door scans"}
                </small>
                <small>
                  {[
                    entry.filteredLastClient?.browser,
                    entry.filteredLastClient?.operatingSystem ||
                      entry.filteredLastClient?.platform,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "Device not recorded"}
                </small>
              </span>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          );
        })}
      </div>

      {!state.loading && !state.error && !visibleUsers.length && (
        <div className="ds-empty ds-dashboard-empty">
          <FontAwesomeIcon icon={faUsersGear} />
          <strong>No users match these filters</strong>
          <p>Clear a filter or widen the country/date selection.</p>
          {filtersActive && (
            <button
              type="button"
              className="ds-button ds-button-secondary"
              onClick={clearFilters}
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function HistoryPanel({ scans, uid, busy, onRefresh, onMore, onOpen, onNew }) {
  return (
    <section className="ds-history-native" aria-label="Scan history">
      <div className="ds-panel-heading">
        <div>
          <p className="ds-eyebrow">SAVED SCANS</p>
          <h1>Your history.</h1>
        </div>
        <button
          type="button"
          className="ds-text-button"
          disabled={busy || scans.loading || !uid}
          onClick={onRefresh}
        >
          Refresh
        </button>
      </div>
      <p className="ds-muted">
        Open a scan to see its annotations or add evidence.
      </p>
      <div className="ds-saved-scan-list">
        {scans.rows.map((row) => (
          <button
            type="button"
            className="ds-scan-row"
            disabled={busy}
            key={row.id}
            onClick={() => onOpen(row)}
          >
            <EvidenceThumbnail
              src={savedImageUrl(row.photos?.front)}
              label={row.doorName || "Saved door"}
            />
            <span>
              <strong>{row.doorName || "Door scan"}</strong>
              <small>{displayDate(row.updatedAt || row.createdAt)}</small>
              <small>
                {Object.keys(row.photos || {}).length +
                  (row.supplemental?.length || 0)}{" "}
                uploaded files
              </small>
            </span>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        ))}
      </div>
      {scans.loading && (
        <p role="status" className="ds-muted">
          Loading your scans...
        </p>
      )}
      {!scans.rows.length && !scans.loading && !scans.error && (
        <div className="ds-empty">
          <FontAwesomeIcon icon={faHistory} />
          <strong>No saved scans yet</strong>
          <p>
            {uid
              ? "Your scans will appear here after uploading."
              : "Sign in or save your first scan to see your results."}
          </p>
        </div>
      )}
      {scans.error && (
        <p role="alert" className="ds-inline-error">
          {scans.error}
        </p>
      )}
      {scans.more && (
        <button
          type="button"
          className="ds-button ds-button-secondary"
          disabled={busy || scans.loading}
          onClick={onMore}
        >
          Show more scans
        </button>
      )}
      <button
        type="button"
        className="ds-button ds-button-primary"
        disabled={busy}
        onClick={onNew}
      >
        Scan another door
      </button>
    </section>
  );
}
export const normaliseClassId = (value) => {
  const id = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return id === "closer" || id === "doorcloser" ? "door_closer" : id;
};
export const SECTION_CHECKLIST_VERSION = "section-evidence-lock-count-3";
export const DEFAULT_CHECKLIST_POLICY = Object.freeze({
  requiredGroups: ["door", "closer", "hinges"],
  requireKeylessDetectionOnConfirmedExitSide: true,
});
const CHECK_LABELS = {
  pass: "Passed",
  fail: "Failed",
  review: "Needs review",
  na: "Not Available",
};
const SECTION_QUESTIONS = {
  door: [
    {
      id: "doorDamage",
      label: "Can you see damage to the door leaf?",
      yesFails: true,
    },
  ],
  closer: [],
  frame: [
    {
      id: "frameDamage",
      label: "Can you see damage to the frame?",
      yesFails: true,
    },
  ],
  smoke_control: [
    {
      id: "stripsPresent",
      label: "Can you see intumescent strips around the door or frame?",
    },
  ],
  locks: [
    {
      id: "keyRequiredToExit",
      label:
        "From the inside / escape side, is a key needed to unlock ANY lock to get out?",
      yesFails: true,
    },
  ],
  glass: [{ id: "glassFitted", label: "Is glass fitted in this door?" }],
  letterbox: [
    { id: "letterboxFitted", label: "Is a letterbox fitted in this door?" },
  ],
};
const QUESTION_IDS = new Set([
  ...Object.values(SECTION_QUESTIONS)
    .flat()
    .map((q) => q.id),
  "escapeSide",
]);
const answerFrom = (observations, id) =>
  [...(observations || [])]
    .reverse()
    .find(
      (o) => o.type === "checklist-answer" && o.questionId === id && !o.stale
    )?.answer || "unknown";
function positiveClasses(prediction) {
  const result = prediction?.prediction || prediction;
  const found = new Set();
  if (!result) return found;
  for (const [name, count] of Object.entries(result.counts || {}))
    if (Number(count) > 0) found.add(normaliseClassId(name));
  for (const region of Array.isArray(result.regions) ? result.regions : [])
    if (region?.cls) found.add(normaliseClassId(region.cls));
  return found;
}
function isAnalysed(prediction) {
  const p = prediction?.prediction || prediction;
  return (
    !!p && Array.isArray(p.regions) && p.counts && typeof p.counts === "object"
  );
}
export function assessLockConfiguration({
  predictions = {},
  sources = {},
  extras = [],
} = {}) {
  const items = Array.isArray(extras) ? extras : [];
  const photoKeys = [
    ...new Set([
      ...SIDES,
      ...items
        .filter((item) => item.group === "locks" && item.type === "photo")
        .map((item) => item.id),
    ]),
  ];
  const readCounts = (value) => {
    const result = value?.prediction || value;
    if (
      !result ||
      result.error ||
      ["failed", "pending", "analysing"].includes(
        value?.analysisStatus || result.analysisStatus
      )
    )
      return null;
    const hasCounts =
      result.counts &&
      typeof result.counts === "object" &&
      !Array.isArray(result.counts);
    const hasRegions = Array.isArray(result.regions);
    if (!hasCounts && !hasRegions) return null;
    const counts = Object.create(null);
    if (hasCounts)
      for (const [name, raw] of Object.entries(result.counts)) {
        if (typeof raw !== "number" && typeof raw !== "string") continue;
        const amount = Number(raw);
        if (!Number.isFinite(amount) || amount < 0) continue;
        const id = normaliseClassId(name);
        counts[id] = Math.max(counts[id] || 0, Math.floor(amount));
      }
    const regionCounts = Object.create(null);
    for (const region of hasRegions ? result.regions : []) {
      if (typeof region?.cls !== "string" || !region.cls.trim()) continue;
      const id = normaliseClassId(region.cls);
      regionCounts[id] = (regionCounts[id] || 0) + 1;
    }
    for (const [id, amount] of Object.entries(regionCounts))
      counts[id] = Math.max(counts[id] || 0, amount);
    return counts;
  };
  const analyses = photoKeys
    .map((side) => ({ side, counts: readCounts(predictions[side]) }))
    .filter((entry) => entry.counts !== null);
  const targetClasses = ["mortice_lock", "yale_lock"];
  const analysedSides = SIDES.filter((side) =>
    analyses.some((entry) => entry.side === side)
  );
  const complete = analysedSides.length === SIDES.length;
  const detections = targetClasses.flatMap((cls) => {
    const count = Math.max(
      0,
      ...analyses.map(({ counts }) => Number(counts[cls] || 0))
    );
    return count > 0 ? [{ cls, count }] : [];
  });
  const detectedSides = analyses
    .filter(({ counts }) => targetClasses.some((cls) => (counts[cls] || 0) > 0))
    .map(({ side }) => side);
  const hasKeyless = analyses.some(({ counts }) =>
    KEYLESS_HARDWARE_CLASSES.some((id) => (counts[id] || 0) > 0)
  );
  const hasLocks = detections.length > 0;
  const maxCounts = Object.fromEntries(
    targetClasses.map((id) => [
      id,
      Math.max(0, ...analyses.map(({ counts }) => counts[id] || 0)),
    ])
  );
  let status, reason;
  if (detections.length) {
    status = "fail";
    const found = detections
      .map(({ cls, count }) =>
        count > 1
          ? `${count} ${classLabel(cls)} detections`
          : `${classLabel(cls)} detected`
      )
      .join("; ");
    reason = `${found}. Certificate evidence is required for review.`;
  } else if (!complete) {
    status = "review";
    reason =
      "Front/back photo analysis is incomplete. Process or retry the door photos before applying the lock check; unavailable results are not treated as no locks.";
  } else {
    status = "pass";
    reason =
      "No Mortice Lock or Yale Lock was detected in the analysed front/back photos.";
  }
  return {
    status,
    reason,
    complete,
    hasKeyless,
    hasLocks,
    maxCounts,
    duplicateViews: [],
    evidenceKeys: [
      ...new Set(detectedSides.filter((side) => Boolean(sources[side]))),
    ],
  };
}

export function buildSectionChecklist({
  predictions = {},
  sources = {},
  extras = [],
  observations = [],
  hingeConfiguration = {},
  certificateDecision = null,
  policy = DEFAULT_CHECKLIST_POLICY,
} = {}) {
  const photoContext = { sources, predictions, extras, observations };
  const required = new Set(
    policy.requiredGroups || DEFAULT_CHECKLIST_POLICY.requiredGroups
  );
  const sideClasses = Object.fromEntries(
    Object.keys(predictions).map((side) => [
      side,
      positiveClasses(predictions[side]),
    ])
  );
  const detected = (classes, sides = SIDES) =>
    sides.some((side) => classes.some((c) => sideClasses[side]?.has(c)));
  const complete = SIDES.every((side) => isAnalysed(predictions[side]));
  const answer = (id) => answerFrom(observations, id);
  const output = REPORT_PARTS.map((part) => ({
    id: part.id,
    label: part.label,
    checks: [],
    status: "review",
    evidenceKeys: [],
    needs: [],
  }));
  const group = (id) => output.find((s) => s.id === id);
  const add = (id, key, label, status, reason, source, evidenceKeys = []) => {
    const attachedEvidenceKeys = extras
      .filter(
        (item) =>
          item.group === id &&
          (item.checkId === key || item.questionId === key) &&
          (sources[item.id] ||
            item.file?.size > 0 ||
            savedImageUrl(item) ||
            savedImageUrl(item.stored))
      )
      .map((item) => item.id);
    group(id).checks.push(
      withPhotoEvidence(
        id,
        {
          id: key,
          label,
          status,
          reason,
          source,
          evidenceKeys: [
            ...new Set([...evidenceKeys, ...attachedEvidenceKeys]),
          ],
          attachedEvidenceKeys,
        },
        photoContext
      )
    );
  };
  const presence = (
    id,
    key,
    label,
    classes,
    sides = SIDES,
    mandatory = required.has(id)
  ) => {
    const seen = detected(classes, sides);
    const hasPhoto = photoEvidenceKeysFor(id, photoContext, key).length > 0;
    const analysed =
      sides.length && sides.every((side) => isAnalysed(predictions[side]));
    add(
      id,
      key,
      label,
      seen ? "pass" : "review",
      seen
        ? "Part identified in the model output. Presence only; condition, operation and fire rating are not proved."
        : hasPhoto
        ? "Not identified in the available model output. The existing photo is retained for review; this does not confirm that the item is missing."
        : analysed && mandatory
        ? "Required item not detected in the model output. Add clear evidence for review; this is not a confirmed failure."
        : "Not established from the available model output. Add evidence or ask the reviewer to check.",
      "model-identification",
      sides.filter((side) => detected(classes, [side]))
    );
    return seen;
  };
  const attached = (id, types, checkId = "") =>
    extras.filter(
      (e) =>
        (e.group === id || (id === "door" && e.group === "certificate")) &&
        types.includes(e.type) &&
        Boolean(
          sources[e.id] ||
            e.file?.size > 0 ||
            savedImageUrl(e) ||
            savedImageUrl(e.stored)
        ) &&
        (!checkId ||
          !(e.checkId || e.questionId) ||
          (e.checkId || e.questionId) === checkId)
    );
  const evidence = (id, key, label, types, text, base = []) => {
    const requirement = mediaRequirementFor(id, key);
    if (requirement) {
      const mediaKeys = requiredMediaEvidenceKeys(id, photoContext, key);
      const reportedClosingConcern =
        id === "closer" && answer("closerCloses") === "no";
      const reason = mediaKeys.length
        ? requirement.type === "video"
          ? "Closing video attached. A reviewer must check the open-to-close movement and whether the door closes fully. The video has not been automatically assessed."
          : "Inside / escape-side photo attached. The release hardware and operation still need review; a photograph is not an operation test."
        : requirement.instructions;
      add(
        id,
        key,
        label,
        reportedClosingConcern ? "fail" : "review",
        (reportedClosingConcern
          ? "You previously reported that the door does not close fully. This concern remains for review. "
          : "") + reason,
        mediaKeys.length ? "media-review" : "evidence-required",
        mediaKeys
      );
      return;
    }
    const keys = [
      ...base.filter((k) => sources[k]),
      ...attached(id, types, key).map((e) => e.id),
    ];
    const canRequestEvidence = REQUESTABLE_EVIDENCE_GROUPS.has(id);
    if (keys.length) {
      add(
        id,
        key,
        label,
        "pass",
        "Required supporting evidence is attached.",
        "evidence-provided",
        keys
      );
      return;
    }
    const existingPhotos = photoEvidenceKeysFor(id, photoContext, key);
    if (existingPhotos.length) {
      const reviewReason =
        key === "closing_video"
          ? "Photo already provided. Closing operation still needs review; a still photo does not establish that the door closes fully."
          : key === "frame_installation"
          ? "Frame photo already provided. Concealed fixings and installation still need review."
          : key === "lock_suitability"
          ? "Lock photo already provided. Suitability and escape operation still need review."
          : "Photo already provided for this item. The existing photo will be reviewed; no additional upload is requested.";
      add(
        id,
        key,
        label,
        "review",
        reviewReason,
        "photo-review",
        existingPhotos
      );
      return;
    }
    if (canRequestEvidence) {
      add(id, key, label, "review", text, "evidence-required", []);
      group(id).needs.push(text);
      return;
    }
    add(
      id,
      key,
      label,
      "na",
      "No additional evidence is requested for this item in this flow.",
      "not-requested",
      []
    );
  };
  const reported = (id, question, label, yesPass = true, failureText = "") => {
    const value = answer(question);
    const status =
      value === "unknown"
        ? "review"
        : (value === "yes") === yesPass
        ? "pass"
        : "fail";
    add(
      id,
      question,
      label,
      status,
      value === "unknown"
        ? "Please answer Yes, No or Not sure."
        : status === "fail"
        ? failureText || "The reported answer needs attention."
        : "Passed this limited resident-reported check only. This is not an independent inspection.",
      "resident-report"
    );
  };
  const damage = (id, cls, question, label) => {
    const seen = detected([cls]);
    const value = answer(question);
    const subject = id === "door" ? "door leaf" : "frame";
    const hasPhoto =
      photoEvidenceKeysFor(id, photoContext, question).length > 0;
    if (seen) {
      add(
        id,
        question,
        label,
        "fail",
        hasPhoto
          ? `The model flagged possible damage to the ${subject}. The existing photo is retained for review; this preliminary finding remains unresolved.`
          : `The model flagged possible damage to the ${subject}. Add a clear close-up for review. This preliminary finding is not cleared just by uploading evidence.`,
        "model-concern",
        SIDES.filter((side) => detected([cls], [side]))
      );
      return;
    }
    add(
      id,
      question,
      label,
      value === "unknown" ? "review" : value === "yes" ? "fail" : "pass",
      value === "unknown"
        ? `Answer whether you can see damage to the ${subject}.${
            hasPhoto
              ? " The photo is already provided."
              : " You can attach a clear photo to this question."
          }`
        : value === "yes"
        ? hasPhoto
          ? `You reported visible damage to the ${subject}. The existing photo is retained for review. You can correct the answer if it was selected by mistake.`
          : `You reported visible damage to the ${subject}. Add a clear close-up of the affected area for review, or correct the answer if it was selected by mistake.`
        : `You reported no visible damage to the ${subject}. Passed this resident-reported condition check only; this is not a completed inspection.`,
      "resident-report"
    );
  };
  presence("door", "door_detected", "Door identified", ["door"]);
  damage("door", "door_damage", "doorDamage", "Visible door condition");
  if (sources.certificate) {
    const rating = certificateDecision?.rating || "No";
    add(
      "door",
      "door_identity",
      "Door certification / fire rating",
      rating === "FD30" || rating === "FD60"
        ? "pass"
        : rating === "Assumed"
        ? "review"
        : "review",
      rating === "FD30" || rating === "FD60"
        ? `${rating} marking identified from the certificate photo text.`
        : rating === "Assumed"
        ? "Fire-door related certification text was identified, but no clear FD30 or FD60 rating was found."
        : "Certificate photo is attached, but no meaningful fire-rating text was identified.",
      "certificate-text",
      ["certificate"]
    );
  } else {
    evidence(
      "door",
      "door_identity",
      "Door identity / fire rating",
      ["photo", "document"],
      "Add a certification-label photo or existing door record.",
      ["certificate"]
    );
  }
  presence(
    "closer",
    "closer_detected",
    "Closer identified",
    ["door_closer"],
    [
      "back",
      "front",
      ...extras
        .filter((item) => item.group === "closer" && item.type === "photo")
        .map((item) => item.id),
    ]
  );
  evidence(
    "closer",
    "closing_video",
    "Open-to-close video",
    ["video"],
    "Add a video showing the door released without pushing, from a wide and a small opening."
  );
  const hingeKeys = [
    ...hingePhotoKeys(hingeConfiguration.reportedCount || 3),
    ...attached("hinges", ["photo"]).map((e) => e.id),
  ];
  const baseHingeSeen = detected(["hinge"]);
  const detailHingeSeen = detected(
    [
      "hinge",
      "normal",
      "missing",
      "loose",
      "reflection",
      "screw",
      "missing_screw",
      "loose_screw",
    ],
    hingeKeys
  );
  add(
    "hinges",
    "hinge_detected",
    "Hinges identified",
    baseHingeSeen || detailHingeSeen
      ? "pass"
      : complete && required.has("hinges")
      ? "fail"
      : "review",
    baseHingeSeen || detailHingeSeen
      ? "Hinge-related features identified. Product rating and all fixings are not verified."
      : photoEvidenceKeysFor("hinges", photoContext).length
      ? "Hinge photos are already provided. Hinge features were not identified in the available analysis and need review."
      : complete
      ? "Required hinge features not detected. Add one clear photo per fitted hinge."
      : "Waiting for completed analysis.",
    "model-identification"
  );
  hingePhotoKeys(hingeConfiguration.reportedCount || 3).forEach((key) => {
    const exists = !!sources[key];
    const prediction = predictions[key];
    const classes = positiveClasses(prediction);
    const concern = ["missing", "loose", "missing_screw", "loose_screw"].some(
      (cls) => classes.has(cls)
    );
    const normal = classes.has("normal");
    const uncertain = classes.has("reflection");
    add(
      "hinges",
      `${key}_screws`,
      `${PHOTO_LABELS[key]} - screws`,
      !exists || !isAnalysed(prediction)
        ? "review"
        : concern
        ? "fail"
        : uncertain
        ? "review"
        : normal
        ? "pass"
        : "fail",
      !exists
        ? "Add one photo of the whole hinge. All screws share this evidence."
        : !isAnalysed(prediction)
        ? "Photo present; hinge analysis is pending, failed or unavailable."
        : concern
        ? "Model flagged loose or missing screw(s). Review this hinge photo as a whole; no separate evidence is required for each screw."
        : uncertain
        ? "Reflection was detected. The existing hinge photo needs manual review."
        : normal
        ? "Only the visible model-classified screw observations passed. Hidden screws and hinge suitability remain unverified."
        : "Required screw observations were not detected in this hinge photo. The existing photo is retained for review.",
      !exists || !isAnalysed(prediction) ? "evidence-required" : "model-triage",
      exists ? [key] : []
    );
    if (!exists)
      group("hinges").needs.push(
        `Add a ${PHOTO_LABELS[key].toLowerCase()} photo.`
      );
  });
  attached("hinges", ["photo"]).forEach((item) => {
    const classes = positiveClasses(predictions[item.id]);
    const concern = ["missing", "loose", "missing_screw", "loose_screw"].some(
      (cls) => classes.has(cls)
    );
    add(
      "hinges",
      `${item.id}_check`,
      "Additional hinge evidence",
      concern ? "fail" : "pass",
      concern
        ? "Additional hinge evidence has a model-flagged screw concern. Review together with the original hinge photos."
        : "Additional hinge photo is attached with no model-flagged loose or missing screw concern.",
      "model-triage",
      [item.id]
    );
  });
  const hingeTextResults = Object.values(
    hingeConfiguration.textResults || {}
  ).filter(Boolean);
  if (hingeTextResults.length) {
    const certifiedCount = hingeTextResults.filter(
      (item) => item.certified
    ).length;
    add(
      "hinges",
      "hinge_rating",
      "Hinge fire-rating markings",
      certifiedCount > 0 ? "pass" : "review",
      certifiedCount > 0
        ? `${certifiedCount} hinge photo${
            certifiedCount === 1 ? "" : "s"
          } contained a recognised fire-rated or certification marking.`
        : "Hinge photos were checked for text, but no recognised fire-rated or certification marking was identified.",
      "hinge-text",
      hingePhotoKeys(hingeConfiguration.reportedCount || 3).filter(
        (key) => sources[key]
      )
    );
  } else {
    evidence(
      "hinges",
      "hinge_rating",
      "Hinge rating / suitability",
      ["photo", "document"],
      "Add readable hinge markings or supporting records.",
      hingePhotoKeys(hingeConfiguration.reportedCount || 3)
    );
  }
  if (hingeConfiguration.reportedCount === 2)
    add(
      "hinges",
      "hinge_count",
      "Two hinges reported",
      "review",
      "Check the required hinge configuration against this door specification. Two photos or two hinges alone do not establish compliance.",
      "resident-report"
    );
  damage("frame", "frame_damage", "frameDamage", "Visible frame condition");
  evidence(
    "frame",
    "frame_installation",
    "Frame installation",
    ["photo", "document"],
    "Add a clear frame photo. Concealed fixings require an appropriate inspection."
  );
  const glassSeen = detected(["glass", "safety_glass"]);
  const glassValue = answer("glassFitted");
  if (!glassSeen) {
    add(
      "glass",
      "glass_applicability",
      "Glass evidence",
      "na",
      "Glass was not detected in the available model output, so no glass evidence is requested.",
      "model-identification"
    );
  } else {
    presence(
      "glass",
      "glass_detected",
      "Glass identified",
      ["glass", "safety_glass"],
      SIDES,
      true
    );
    if (glassValue === "no")
      add(
        "glass",
        "glass_conflict",
        "Conflicting glass report",
        "review",
        "Glass was detected in the photo but was reported as not fitted. Please check the answer.",
        "conflict"
      );
    evidence(
      "glass",
      "glass_fire_marking",
      "Glass fire stamp / marking",
      ["photo", "document"],
      "Add a close-up photo of the glass fire stamp or marking, or supporting fire-rating evidence."
    );
  }
  const letterboxSeen = detected(["letter_box"]);
  const letterboxValue = answer("letterboxFitted");
  if (letterboxValue === "no" && !letterboxSeen)
    add(
      "letterbox",
      "letterbox_applicability",
      "Fitting applicability",
      "na",
      "Letterbox not fitted, as reported by the resident.",
      "resident-report"
    );
  else {
    presence(
      "letterbox",
      "letterbox_detected",
      "Letterbox identified",
      ["letter_box"],
      SIDES,
      letterboxValue === "yes"
    );
    if (letterboxValue === "no" && letterboxSeen)
      add(
        "letterbox",
        "letterbox_conflict",
        "Conflicting evidence",
        "review",
        "The model detected a letterbox but it was reported as not fitted. Please check the answer.",
        "conflict"
      );
  }
  reported(
    "smoke_control",
    "stripsPresent",
    "Intumescent strips reported present",
    true,
    "No intumescent strips were reported. Failed the presence checklist; the reviewer must establish the required specification."
  );
  evidence(
    "smoke_control",
    "strips_evidence",
    "Intumescent strip evidence",
    ["photo"],
    "Add a clear photo showing the visible intumescent strips."
  );
  const lockResult = assessLockConfiguration(photoContext);
  const lockReason = lockResult.reason;
  add(
    "locks",
    LOCK_CONFIGURATION_CHECK,
    lockResult.status === "fail"
      ? "Certificate evidence required"
      : "Lock configuration",
    lockResult.status,
    lockReason,
    "configured-photo-rule",
    lockResult.evidenceKeys
  );
  if (answer("keyRequiredToExit") === "yes") {
    reported(
      "locks",
      "keyRequiredToExit",
      "Release without a key",
      false,
      "You reported that a key is needed to get out from the inside / escape side. This reported concern remains Failed for review, independently of the photo lock-count result."
    );
  }
  evidence(
    "gaps",
    "gap_measurement",
    "Gap measurements",
    ["photo", "document"],
    "Add measured gap evidence and the applicable door specification, or request an on-site measurement."
  );
  return output.map((section) => {
    section.status = section.checks.some((c) => c.status === "fail")
      ? "fail"
      : section.checks.some((c) => c.status === "review")
      ? "review"
      : section.checks.every((c) => c.status === "na")
      ? "na"
      : "pass";
    section.photoEvidenceKeys = photoEvidenceKeysFor(section.id, photoContext);
    section.evidenceProvided =
      section.photoEvidenceKeys.length > 0 ||
      section.checks.some(
        (check) =>
          check.source === "evidence-provided" && check.evidenceKeys.length > 0
      );
    section.evidenceKeys = [
      ...new Set([
        ...section.checks.flatMap((check) => check.evidenceKeys),
        ...section.photoEvidenceKeys,
      ]),
    ];
    section.needs = [
      ...new Set(
        section.checks
          .filter(
            (check) =>
              check.status === "review" &&
              check.source === "evidence-required" &&
              check.needsEvidence
          )
          .map((check) => check.reason)
      ),
    ];
    return section;
  });
}
export function summariseChecklist(sections) {
  const counts = { pass: 0, fail: 0, review: 0, na: 0 };
  for (const section of sections)
    for (const check of section.checks) counts[check.status] += 1;
  return counts;
}
function CheckBadge({ status, label }) {
  return (
    <span className={`ds-check-badge is-${status}`}>
      <span aria-hidden="true">
        {status === "pass"
          ? "\u2713"
          : status === "fail"
          ? "!"
          : status === "na"
          ? "\u2013"
          : "?"}
      </span>
      {label || CHECK_LABELS[status] || "Needs review"}
    </span>
  );
}
function AssessmentSummary({ sections }) {
  const counts = summariseChecklist(sections);
  return (
    <div className="ds-assessment-summary" aria-label="Photo checklist summary">
      {[
        ["pass", "Passed checks"],
        ["fail", "Failed checks"],
        ["review", "Needs review"],
      ].map(([status, label]) => (
        <div key={status} className={`ds-summary-tile is-${status}`}>
          <span className="ds-summary-symbol" aria-hidden="true">
            {status === "pass" ? "\u2713" : status === "fail" ? "!" : "?"}
          </span>
          <strong>{counts[status]}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}
function CheckEvidenceAction({ group, check, busy, onEvidence }) {
  if (!check || check.status === "na") return null;
  const checkHasEvidence =
    check.hasPhotoEvidence ||
    check.evidenceProvided ||
    (check.attachedEvidenceKeys || []).length > 0;
  if (group === "gaps" && !checkHasEvidence) {
    return <span className="ds-simple-unavailable">Not available</span>;
  }
  if (typeof onEvidence !== "function") return null;
  const requirement = mediaRequirementForCheck(group, check);
  if (requirement) {
    const label = check.evidenceProvided
      ? requirement.viewLabel
      : requirement.requestLabel;
    return (
      <button
        type="button"
        className="ds-text-button"
        disabled={busy}
        aria-label={label}
        onClick={() => onEvidence(group, check)}
      >
        {label}
      </button>
    );
  }
  const isQuestion = QUESTION_IDS.has(check.id);
  const provided =
    check.hasPhotoEvidence ||
    check.evidenceProvided ||
    (check.attachedEvidenceKeys || []).length > 0;
  if (!isQuestion && (provided || check.needsEvidence === false)) return null;
  const label = isQuestion
    ? provided
      ? "Review answer"
      : "Answer / add evidence"
    : "Add evidence";
  return (
    <button
      type="button"
      className="ds-text-button"
      disabled={busy}
      aria-label={`${label} for ${check.label}`}
      onClick={() => onEvidence(group, check)}
    >
      {label}
    </button>
  );
}
function SectionCheckList({
  section,
  compact = false,
  busy = false,
  onEvidence,
}) {
  return (
    <div className={`ds-check-list ${compact ? "is-compact" : ""}`}>
      {section.checks.map((check) => (
        <div className="ds-check-row" key={check.id}>
          <div>
            <strong>{check.label}</strong>
            <p>{check.reason}</p>
            {(check.requiredMedia ||
              QUESTION_IDS.has(check.id) ||
              canAddCheckEvidence(section, check)) && (
              <CheckEvidenceAction
                group={section.id}
                check={check}
                busy={busy}
                onEvidence={onEvidence}
              />
            )}
          </div>
          <CheckBadge
            status={check.status}
            label={
              check.status === "review"
                ? QUESTION_IDS.has(check.id)
                  ? "Needs answer"
                  : check.needsEvidence
                  ? "Needs evidence"
                  : "Needs review"
                : undefined
            }
          />
        </div>
      ))}
    </div>
  );
}
function SectionQuestions({
  group,
  observations,
  busy,
  onAnswer,
  questionId = "",
}) {
  const questions = (SECTION_QUESTIONS[group] || []).filter(
    (question) => !questionId || question.id === questionId
  );
  const showEscapeSide =
    group === "locks" && (!questionId || questionId === "escapeSide");
  if ((!questions.length && !showEscapeSide) || typeof onAnswer !== "function")
    return null;
  return (
    <div className="ds-section-questions">
      {showEscapeSide && (
        <fieldset disabled={busy}>
          <legend>Which photo shows the inside / escape side?</legend>
          <div className="ds-answer-buttons">
            {[
              ["front", "Front"],
              ["back", "Back"],
              ["unknown", "Not sure"],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                aria-pressed={answerFrom(observations, "escapeSide") === value}
                onClick={() => onAnswer(group, "escapeSide", value)}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {questions.map((q) => (
        <fieldset key={q.id} disabled={busy}>
          <legend>{q.label}</legend>
          <div className="ds-answer-buttons">
            {[
              ["yes", "Yes"],
              ["no", "No"],
              ["unknown", "Not sure"],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                aria-pressed={answerFrom(observations, q.id) === value}
                onClick={() => onAnswer(group, q.id, value)}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
      ))}
      <p className="ds-muted">
        Your answers are recorded as resident observations, not independent
        inspection results. Only check what is safe to see or operate.
      </p>
    </div>
  );
}
function EvidenceGuideArt({ group }) {
  const [replay, setReplay] = useState(0);
  return (
    <div className={`ds-evidence-animation is-${group}`}>
      <div key={replay} className="ds-evidence-art" aria-hidden="true">
        {group === "closer" ? (
          <div className="ds-demo-door">
            <span className="ds-demo-leaf" />
            <i className="ds-demo-closer" />
          </div>
        ) : group === "smoke_control" ? (
          <svg viewBox="0 0 180 160">
            <path d="M46 145V15h90v130" />
            <path className="ds-demo-seal" d="M55 144V24h72v120" />
            <path d="M62 142V31h57v111M113 94h-9" />
            <circle className="ds-demo-detail" cx="128" cy="76" r="15" />
          </svg>
        ) : group === "hinges" ? (
          <svg viewBox="0 0 180 160">
            <rect x="43" y="25" width="42" height="110" rx="4" />
            <rect x="95" y="25" width="42" height="110" rx="4" />
            <path d="M88 23h5v114h-5z" />
            {[45, 80, 115].flatMap((y) =>
              [64, 116].map((x) => (
                <g className="ds-demo-screw" key={`${x}-${y}`}>
                  <circle cx={x} cy={y} r="6" />
                  <path d={`M${x - 3} ${y}h6`} />
                </g>
              ))
            )}
          </svg>
        ) : group === "certificate" || group === "door" ? (
          <svg viewBox="0 0 180 160">
            <rect x="55" y="15" width="70" height="128" rx="7" />
            <path d="M70 35l20-9 20 9v21c-3 14-11 21-20 26-9-5-17-12-20-26zM80 51l8 8 15-19" />
            <path className="ds-demo-mark" d="M68 97h44M68 109h32M68 121h44" />
          </svg>
        ) : (
          <svg viewBox="0 0 180 160">
            <path d="M49 149V12h82v137M55 147V19h70v128M43 149h94" />
            {group === "glass" ? (
              <>
                <rect x="69" y="35" width="43" height="43" />
                <path className="ds-demo-mark" d="M74 63l28-23m-21 33 22-18" />
              </>
            ) : group === "locks" ? (
              <>
                <rect x="96" y="81" width="23" height="45" rx="4" />
                <path className="ds-demo-mark" d="M107 94H84" />
                <circle className="ds-demo-detail" cx="107" cy="113" r="7" />
                <path className="ds-demo-mark" d="M102 113h10" />
              </>
            ) : group === "letterbox" ? (
              <rect
                className="ds-demo-mark"
                x="66"
                y="83"
                width="48"
                height="13"
                rx="2"
              />
            ) : (
              <path
                className="ds-demo-seal"
                d={
                  group === "gaps"
                    ? "M55 29H130V144H55ZM55 29V144M130 29V144"
                    : "M49 143V12h82v131"
                }
              />
            )}
          </svg>
        )}
      </div>
      <div className="ds-evidence-art-caption">
        <span>
          {group === "closer"
            ? "Video guide: open the door, release without pushing, and film until it stops."
            : group === "locks"
            ? "Photo guide: show the inside thumbturn, lever or push pad. Example only; your fittings may differ."
            : group === "smoke_control"
            ? "Show the accessible strip and seal along its length."
            : group === "hinges"
            ? "One whole hinge photo includes every visible screw."
            : "Show the whole part, then any readable markings."}
        </span>
        <button
          type="button"
          aria-label="Replay evidence guide animation"
          onClick={() => setReplay((n) => n + 1)}
        >
          <FontAwesomeIcon icon={faRedo} />
        </button>
      </div>
    </div>
  );
}
function SectionEvidencePanel({
  group,
  observations,
  extras,
  statuses,
  busy,
  onAnswer,
  onFiles,
  onRecord,
  onDifferent,
  checklist,
  sources = {},
}) {
  const section = checklist.find((s) => s.id === group);
  const extraFiles = extras.filter((e) => e.group === group);
  const files = [
    ...(section?.evidenceKeys || [])
      .filter((key) => PHOTO_KEYS.includes(key) && sources[key])
      .map((key) => ({ id: key, label: PHOTO_LABELS[key] })),
    ...extraFiles,
  ];
  const hasEvidence = files.length > 0;
  const isCloser = group === "closer";
  const isStrips = group === "smoke_control";
  return (
    <div className="ds-section-evidence-panel">
      <EvidenceGuideArt group={group} />
      {isStrips && (
        <SectionQuestions
          group={group}
          observations={observations}
          busy={busy}
          onAnswer={onAnswer}
        />
      )}
      {isCloser &&
        (() => {
          const check = section?.checks.find(
            (item) => item.id === CLOSING_VIDEO_CHECK
          );
          return check?.evidenceProvided ? (
            <>
              <p>{check.reason}</p>
              {(check.evidenceKeys || []).map((key) => (
                <EvidenceMediaPreview
                  key={key}
                  evidenceKey={key}
                  type="video"
                  sources={sources}
                  extras={extras}
                />
              ))}
            </>
          ) : (
            <>
              <p>{EVIDENCE_PROMPTS.closer}</p>
              <div className="ds-evidence-action-grid is-simple">
                <button
                  className="ds-evidence-action primary"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    onRecord(group, check || { id: CLOSING_VIDEO_CHECK })
                  }
                >
                  <UiIcon name="play" />
                  <strong>Record closing video</strong>
                  <span>
                    Show the door from open to closed, without pushing
                  </span>
                </button>
                <button
                  className="ds-evidence-action"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    onFiles(
                      group,
                      "video",
                      check || { id: CLOSING_VIDEO_CHECK }
                    )
                  }
                >
                  <UiIcon name="play" />
                  <strong>Upload closing video</strong>
                  <span>Choose an existing open-to-close video</span>
                </button>
              </div>
            </>
          );
        })()}
      {isStrips && !section?.evidenceProvided && !hasEvidence && (
        <>
          <p>{EVIDENCE_PROMPTS[group]}</p>
          <div className="ds-evidence-action-grid is-simple">
            <button
              className="ds-evidence-action primary"
              type="button"
              disabled={busy}
              onClick={() => onFiles(group, "photo")}
            >
              <FontAwesomeIcon icon={faImage} />
              <strong>Photograph the strips</strong>
              <span>
                Show the visible intumescent strips around the door or frame
              </span>
            </button>
          </div>
        </>
      )}
      {hasEvidence && (
        <div className="ds-section-attached">
          <strong>Evidence already provided</strong>
          {files.map((e) => (
            <div key={e.id}>
              <span>{e.label || e.file?.name || "Evidence"}</span>
              <small>
                {statuses[e.id]?.phase === "uploaded"
                  ? "Uploaded"
                  : "Ready to upload"}
              </small>
            </div>
          ))}
        </div>
      )}
      {section && (
        <details className="ds-section-details">
          <summary>Checks for this section</summary>
          <SectionCheckList
            section={section}
            busy={busy}
            onEvidence={(part, check) =>
              onFiles(
                part,
                mediaRequirementFor(part, check.id)?.type || "photo",
                check
              )
            }
          />
        </details>
      )}
      <button
        type="button"
        className="ds-text-button"
        disabled={busy}
        onClick={onDifferent}
      >
        Back
      </button>
    </div>
  );
}
export function checklistFingerprint(sections) {
  return JSON.stringify(
    sections.map((section) => ({
      id: section.id,
      checks: section.checks.map((check) => ({
        id: check.id,
        status: check.status,
        reason: check.reason,
        evidenceKeys: check.evidenceKeys,
      })),
    }))
  );
}
function FinalResults({
  pdf,
  sections,
  dirty,
  busy,
  receipt,
  onEvidence,
  onContinue,
  readOnly = false,
}) {
  const [filter, setFilter] = useState("all");
  const visible = sections.filter(
    (s) =>
      s.id !== "glass" &&
      (filter === "all" || s.checks.some((c) => c.status === filter))
  );
  return (
    <div className="ds-final-results">
      <p>
        {readOnly
          ? "Saved photo checklist. This admin view is read-only and cannot change the owner's evidence or answers."
          : "Review the photo checklist before sending. Unresolved checks and reported concerns will be included in your review request."}
      </p>
      <AssessmentSummary sections={sections} />
      <p className="ds-checklist-limit">
        These are preliminary evidence checks, not a fire-door certificate. A
        passed item only passes the specific check shown. Failed detections are
        not proof of absence.
      </p>
      <div
        className="ds-summary-filters"
        role="group"
        aria-label="Filter final result"
      >
        {[
          ["all", "All sections"],
          ["fail", "Failed"],
          ["review", "Needs review"],
          ["pass", "Passed"],
        ].map(([key, label]) => (
          <button
            type="button"
            key={key}
            aria-pressed={key === filter}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="ds-final-section-list">
        {visible.map((section) => (
          <article
            className={`ds-final-section is-${section.status}`}
            key={section.id}
          >
            <header>
              <div className="ds-final-section-title">
                <CheckBadge
                  status={section.status}
                  label={
                    section.status === "review"
                      ? section.checks.some(
                          (check) =>
                            check.status === "review" &&
                            check.source === "evidence-required" &&
                            check.needsEvidence
                        )
                        ? "Needs evidence"
                        : "Needs review"
                      : undefined
                  }
                />
                <h3>{section.label}</h3>
              </div>
            </header>
            <SectionCheckList
              busy={busy}
              onEvidence={readOnly ? undefined : onEvidence}
              section={
                filter === "all"
                  ? section
                  : {
                      ...section,
                      checks: section.checks.filter((c) => c.status === filter),
                    }
              }
            />
            {!readOnly &&
              canAddSectionEvidence(section) &&
              !section.checks.some((check) => check.requiredMedia) &&
              (section.id === "gaps" ? (
                <span className="ds-simple-unavailable">Not available</span>
              ) : (
                <button
                  className="ds-button ds-button-secondary ds-final-add-evidence"
                  type="button"
                  disabled={busy}
                  onClick={() => onEvidence(section.id)}
                >
                  Add evidence
                </button>
              ))}
          </article>
        ))}
        {!visible.length && <p>No checks in this category.</p>}
      </div>
      {!readOnly && dirty && (
        <p className="ds-muted">
          Your changes are not uploaded yet. They will be saved before the
          request is sent.
        </p>
      )}

      {pdf && <DoorScanPdfButton state={pdf} disabled={busy} />}

      {!readOnly && (
        <button
          type="button"
          className="ds-button ds-button-primary"
          disabled={busy}
          onClick={onContinue}
        >
          {receipt ? "View submission" : "Continue to contact & send"}
        </button>
      )}
    </div>
  );
}
function VideoEvidenceRecorder({
  group,
  checkId = null,
  busy,
  onSave,
  onCancel,
  onUpload,
}) {
  const videoRef = useRef(null),
    streamRef = useRef(null),
    recorderRef = useRef(null),
    chunks = useRef([]),
    live = useRef(false),
    session = useRef(0),
    saving = useRef(false);
  const [phase, setPhase] = useState("opening"),
    [message, setMessage] = useState(""),
    [seconds, setSeconds] = useState(0),
    [file, setFile] = useState(null),
    [retry, setRetry] = useState(0);
  const preview = usePreview(file);
  const release = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);
  useEffect(() => {
    live.current = true;
    const ticket = ++session.current;
    let cancelled = false;
    const start = async () => {
      try {
        if (
          !window.isSecureContext ||
          !navigator.mediaDevices?.getUserMedia ||
          typeof window.MediaRecorder !== "function"
        )
          throw new Error(
            "Live video recording is unavailable. Use your phone camera or choose an existing video."
          );
        setPhase("opening");
        setMessage("");
        setFile(null);
        setSeconds(0);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled || ticket !== session.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if (!cancelled) setPhase("ready");
      } catch (e) {
        if (!cancelled) {
          release();
          setPhase("error");
          setMessage(cameraErrorMessage(e));
        }
      }
    };
    start();
    const interrupt = () => {
      if (document.hidden) {
        const rec = recorderRef.current;
        if (rec && rec.state === "recording") rec.stop();
        else {
          release();
          setPhase("paused");
          setMessage("Recording paused. Resume the camera or upload a video.");
        }
      }
    };
    document.addEventListener("visibilitychange", interrupt);
    return () => {
      cancelled = true;
      live.current = false;
      session.current++;
      document.removeEventListener("visibilitychange", interrupt);
      const rec = recorderRef.current;
      if (rec) {
        rec.ondataavailable = null;
        rec.onstop = null;
        rec.onerror = null;
        if (rec.state !== "inactive") rec.stop();
      }
      release();
    };
  }, [retry, release]);
  useEffect(() => {
    if (phase !== "recording") return undefined;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - started) / 1000);
      setSeconds(elapsed);
      if (elapsed >= 30 && recorderRef.current?.state === "recording")
        recorderRef.current.stop();
    }, 200);
    return () => window.clearInterval(timer);
  }, [phase]);
  const record = () => {
    try {
      const stream = streamRef.current;
      if (!stream) return;
      const formats = ["video/webm;codecs=vp8", "video/webm", "video/mp4"];
      const mimeType = formats.find((t) =>
        window.MediaRecorder.isTypeSupported(t)
      );
      const rec = new window.MediaRecorder(
        stream,
        mimeType
          ? { mimeType, videoBitsPerSecond: 2500000 }
          : { videoBitsPerSecond: 2500000 }
      );
      recorderRef.current = rec;
      chunks.current = [];
      let bytes = 0;
      rec.ondataavailable = (e) => {
        if (e.data.size) {
          chunks.current.push(e.data);
          bytes += e.data.size;
          if (bytes > 45 * 1024 * 1024 && rec.state === "recording") rec.stop();
        }
      };
      rec.onstop = () => {
        if (!live.current) return;
        const type = String(rec.mimeType || mimeType || "video/webm").split(
          ";"
        )[0];
        const result = new File(
          chunks.current,
          `${group}-evidence-${Date.now()}.${
            type === "video/mp4" ? "mp4" : "webm"
          }`,
          { type }
        );
        release();
        if (!result.size) {
          setPhase("error");
          setMessage("No video was recorded. Please try again.");
          return;
        }
        setFile(result);
        setPhase("review");
      };
      rec.onerror = () => {
        release();
        setPhase("error");
        setMessage(
          "Recording stopped unexpectedly. Please retry or upload a video."
        );
      };
      rec.start(500);
      setSeconds(0);
      setPhase("recording");
    } catch (e) {
      setMessage(cameraErrorMessage(e));
    }
  };
  return (
    <div className="ds-video-evidence">
      <EvidenceGuideArt group={group} />
      <p>
        {group === "closer"
          ? "Film the whole door as you release it without pushing. Repeat from a small opening when safe."
          : "Show the accessible part slowly and keep the details in focus."}{" "}
        Keep fingers clear. Do not lock yourself out.
      </p>
      <video
        ref={videoRef}
        autoPlay={!file}
        src={file ? preview : undefined}
        muted={!file}
        controls={!!file}
        playsInline
        className="ds-evidence-video"
      />
      <p className="ds-muted">
        Up to 30 seconds per clip. No microphone audio is recorded.
      </p>
      {message && (
        <p role="alert" className="ds-inline-error">
          {message}
        </p>
      )}
      {phase === "opening" && <p role="status">Opening video camera...</p>}
      {phase === "ready" && (
        <button
          type="button"
          className="ds-button ds-button-primary"
          onClick={record}
        >
          Start recording
        </button>
      )}
      {phase === "recording" && (
        <button
          type="button"
          className="ds-button ds-button-primary"
          onClick={() => recorderRef.current?.stop()}
        >
          Stop recording ({seconds}s)
        </button>
      )}
      {phase === "review" && (
        <div className="ds-review-actions">
          <button
            type="button"
            className="ds-button ds-button-secondary"
            disabled={busy}
            onClick={() => setRetry((n) => n + 1)}
          >
            Record again
          </button>
          <button
            type="button"
            className="ds-button ds-button-primary"
            disabled={busy}
            onClick={async () => {
              if (saving.current) return;
              saving.current = true;
              try {
                await onSave({
                  group,
                  type: "video",
                  files: [file],
                  note: "",
                  checkId:
                    checkId ||
                    (group === "closer" ? CLOSING_VIDEO_CHECK : null),
                });
              } catch (e) {
                setMessage(friendlyError(e));
              } finally {
                saving.current = false;
              }
            }}
          >
            Use this video
          </button>
        </div>
      )}
      {["error", "paused"].includes(phase) && (
        <button
          type="button"
          className="ds-button ds-button-secondary"
          onClick={() => setRetry((n) => n + 1)}
        >
          Resume camera
        </button>
      )}
      {phase !== "recording" && (
        <button
          type="button"
          className="ds-button ds-button-secondary"
          disabled={busy}
          onClick={onUpload}
        >
          Phone camera / choose video
        </button>
      )}
      <button
        type="button"
        className="ds-text-button"
        disabled={busy}
        onClick={() => {
          if (
            (phase === "recording" || phase === "review") &&
            !window.confirm("Discard this unconfirmed video?")
          )
            return;
          onCancel();
        }}
      >
        Cancel
      </button>
    </div>
  );
}
function CameraGuideOverlay({ side, bounds }) {
  const kind = HINGES.includes(side)
    ? "hinge"
    : side === "certificate"
    ? "certificate"
    : "door";
  return (
    <div
      className={`ds-camera-shape is-${kind}`}
      style={bounds ? { ...bounds, right: "auto", bottom: "auto" } : undefined}
      aria-hidden="true"
    >
      <div className="ds-camera-shape-inner">
        <svg
          viewBox={
            kind === "door"
              ? "0 0 180 300"
              : kind === "hinge"
              ? "0 0 180 220"
              : "0 0 260 160"
          }
          preserveAspectRatio="xMidYMid meet"
        >
          {kind === "door" ? (
            <>
              <path className="ds-shape-border" d="M12 288V12h156v276z" />
              <path d="M21 288V23h138v265M8 291h164" />
              {side === "back" && <path d="M61 33h75v13H61zM73 46l23 18h50" />}
              <path d="M134 165h14" />
            </>
          ) : kind === "hinge" ? (
            <>
              <rect
                className="ds-shape-border"
                x="22"
                y="16"
                width="136"
                height="188"
                rx="9"
              />
              <path d="M87 20v180m8-180v180" />
              {[52, 110, 168].flatMap((y) =>
                [52, 128].map((x) => (
                  <circle key={`${x}-${y}`} cx={x} cy={y} r="8" />
                ))
              )}
            </>
          ) : (
            <>
              <rect
                className="ds-shape-border"
                x="12"
                y="12"
                width="236"
                height="136"
                rx="10"
              />
              <path d="M32 39h90M32 59h140M32 82h68M32 112h160" />
              <circle cx="213" cy="48" r="17" />
            </>
          )}
        </svg>
        <span>
          {kind === "door"
            ? "Include the whole door and frame"
            : kind === "hinge"
            ? "One whole hinge, including every screw"
            : "Keep the whole label and numbers readable"}
        </span>
      </div>
    </div>
  );
}
const GROUPED_TOUR_DWELL_MS = TOUR_PART_TIME;
const GROUPED_EMPTY_LIST = Object.freeze([]);
const GROUPED_EMPTY_MAP = Object.freeze({});
const GROUPED_CONTEXT_CLASSES = new Set([
  "screw",
  "missing_screw",
  "loose_screw",
  "normal",
  "missing",
  "loose",
  "reflection",
]);
const GROUPED_POSSIBLE_DEFECTS = new Set([
  "door_damage",
  "frame_damage",
  "missing_screw",
  "loose_screw",
  "missing",
  "loose",
]);
const GROUPED_SECTION_COPY = {
  door: "Review the door and its fittings together. Supporting identification records belong to this section.",
  closer:
    "Review the closer and its fixings together. A closing test or reviewed video is needed to assess operation.",
  hinges:
    "Review each hinge photo, including its screws and markings. All screw observations belong to the hinge shown.",
  frame:
    "Review the frame and any damage together. No detected frame defect does not prove that the whole frame is satisfactory.",
  glass:
    "Review the glazing, beads and available markings. A glass class label does not establish a fire-resistance rating.",
  smoke_control:
    "Review the door seals and threshold together. This section concerns the door, not the building smoke-control system.",
  locks:
    "Review all locks, latches and handles together. Escape operation must be checked from the side used to leave.",
  letterbox:
    "Review the letterbox and any supporting evidence together. Not detected does not mean not fitted.",
  gaps: "Record measurements against the applicable door specification. The supplied class list contains no gap-measurement output.",
};
function getGroupedPrediction(predictions, side) {
  const value = predictions?.[side];
  return value?.prediction || value || null;
}
export function buildGroupedAnnotationTour(
  predictions = {},
  sources = {},
  extras = []
) {
  const supplemental = Array.isArray(extras) ? extras : [];
  const extraById = new Map(supplemental.map((item) => [item.id, item]));
  const definitions = REPORT_PARTS;
  const definitionById = new Map(definitions.map((part) => [part.id, part]));
  const classToSection = new Map();
  for (const part of definitions) {
    for (const cls of part.classes) {
      if (!classToSection.has(cls)) classToSection.set(cls, part.id);
    }
  }
  const sourceOrder = [
    ...new Set([
      ...PHOTO_KEYS,
      ...Object.keys(predictions || {}),
      ...Object.keys(sources || {}),
    ]),
  ];
  const photoKeys = sourceOrder.filter((side) => {
    const extra = extraById.get(side);
    return (
      side !== "certificate" &&
      Boolean(sources?.[side]) &&
      (!extra?.type || extra.type === "photo")
    );
  });
  const predictionSides = [
    ...new Set([...SIDES, ...HINGES, ...Object.keys(predictions || {})]),
  ].filter((side) => side !== "certificate");
  const viewSideOrder = [
    ...new Set([
      ...PHOTO_KEYS.filter((key) => key !== "certificate"),
      ...predictionSides,
      ...photoKeys,
    ]),
  ];
  const sections = definitions.map((part) => ({
    id: part.id,
    label: part.label,
    preferredSide: part.source,
    detections: [],
    evidenceKeys: [],
    views: [],
    possibleDefects: [],
    needsAssignment: [],
  }));
  const sectionById = new Map(sections.map((section) => [section.id, section]));
  for (const side of predictionSides) {
    const prediction = getGroupedPrediction(predictions, side);
    const regions = Array.isArray(prediction?.regions)
      ? prediction.regions
      : [];
    const extra = extraById.get(side);
    const contextGroup = HINGES.includes(side)
      ? "hinges"
      : definitionById.has(extra?.group)
      ? extra.group
      : null;
    regions.forEach((rawRegion, index) => {
      const region = { ...rawRegion, cls: normaliseClassId(rawRegion?.cls) };
      if (!region.cls) return;
      const mappedGroup = classToSection.get(region.cls);
      const isGeneric = GROUPED_CONTEXT_CLASSES.has(region.cls);
      const group =
        (isGeneric && contextGroup) || mappedGroup || contextGroup || "door";
      const section = sectionById.get(group);
      if (!section) return;
      const needsAssignment =
        (!mappedGroup && !contextGroup) || (isGeneric && !contextGroup);
      const detection = {
        id: `${side}:${index}`,
        side,
        index,
        label: classLabel(region.cls),
        group,
        region,
        hasGeometry: Boolean(regionGeometry(region, 1, 1)),
        needsAssignment,
      };
      section.detections.push(detection);
      if (needsAssignment) section.needsAssignment.push(detection);
      if (GROUPED_POSSIBLE_DEFECTS.has(region.cls)) {
        section.possibleDefects.push(detection);
      }
    });
    const classesWithRegions = new Set(
      regions.map((region) => normaliseClassId(region?.cls))
    );
    const countedClasses = new Set();
    for (const [name, value] of Object.entries(prediction?.counts || {})) {
      const cls = normaliseClassId(name);
      if (
        !Number.isFinite(Number(value)) ||
        Number(value) <= 0 ||
        classesWithRegions.has(cls) ||
        countedClasses.has(cls)
      )
        continue;
      const mappedGroup = classToSection.get(cls);
      const group =
        (GROUPED_CONTEXT_CLASSES.has(cls) && contextGroup) ||
        mappedGroup ||
        contextGroup;
      const section = sectionById.get(group);
      if (!section) continue;
      countedClasses.add(cls);
      const detection = {
        id: `${side}:count:${cls}`,
        side,
        index: -1,
        label: classLabel(cls),
        group,
        region: { cls },
        count: Number(value),
        hasGeometry: false,
        needsAssignment: false,
        fromCounts: true,
      };
      section.detections.push(detection);
      if (GROUPED_POSSIBLE_DEFECTS.has(cls))
        section.possibleDefects.push(detection);
    }
  }
  return sections.map((section) => {
    const pageMap = new Map();
    for (const detection of section.detections) {
      const cls = normaliseClassId(detection.region.cls) || "unlabelled";
      const key = `${detection.side}:${cls}`;
      if (!pageMap.has(key)) {
        pageMap.set(key, {
          id: `${section.id}:${detection.side}:${cls}`,
          side: detection.side,
          cls,
          classLabel: classLabel(cls),
          detections: [],
          isOverview: false,
        });
      }
      pageMap.get(key).detections.push(detection);
    }
    const detectionsBySide = new Map();
    for (const detection of section.detections) {
      if (!detectionsBySide.has(detection.side)) {
        detectionsBySide.set(detection.side, []);
      }
      detectionsBySide.get(detection.side).push(detection);
    }
    for (const side of predictionSides) {
      const detections = detectionsBySide.get(side) || [];
      if (!detections.length) continue;
      pageMap.set(`${side}:__all__`, {
        id: `${section.id}:${side}:__all__`,
        side,
        cls: "__all__",
        classLabel: "All detected",
        detections,
        isOverview: false,
        isAll: true,
      });
    }
    for (const side of photoKeys) {
      const explicitGroup = HINGES.includes(side)
        ? "hinges"
        : extraById.get(side)?.group;
      if (explicitGroup !== section.id) continue;
      const hasSide = [...pageMap.values()].some((page) => page.side === side);
      if (!hasSide) {
        pageMap.set(`${side}:overview`, {
          id: `${section.id}:${side}:overview`,
          side,
          cls: "",
          classLabel: PHOTO_LABELS[side] || "Evidence",
          detections: [],
          isOverview: true,
          isAll: true,
        });
      }
    }
    section.views = [...pageMap.values()].sort((a, b) => {
      const ai = viewSideOrder.indexOf(a.side);
      const bi = viewSideOrder.indexOf(b.side);
      if (ai !== bi) return ai - bi;
      if (a.isAll && !b.isAll) return -1;
      if (!a.isAll && b.isAll) return 1;
      return a.classLabel.localeCompare(b.classLabel);
    });
    section.evidenceKeys = [
      ...new Set([
        ...section.detections.map((d) => d.side),
        ...supplemental
          .filter((item) => item.group === section.id)
          .map((item) => item.id),
        ...(section.id === "door" && sources?.certificate
          ? ["certificate"]
          : []),
      ]),
    ];
    if (!section.views.length) {
      const fallbackSide = photoKeys.includes(section.preferredSide)
        ? section.preferredSide
        : photoKeys.find((side) => SIDES.includes(side)) || photoKeys[0] || "";
      section.views = [
        {
          id: `${section.id}:overview`,
          side: fallbackSide,
          cls: "",
          classLabel: section.label,
          detections: [],
          isOverview: true,
        },
      ];
    }
    section.status =
      section.possibleDefects.length || section.needsAssignment.length
        ? "review"
        : "unassessed";
    return section;
  });
}
export function componentRegionsForView(view) {
  return (view?.detections || [])
    .filter((detection) => regionGeometry(detection.region, 1, 1))
    .map((detection) => ({
      ...detection.region,
      displayLabel: detection.label,
      sourceDetectionId: detection.id,
    }));
}
export function buildIndividualPhotoViews(
  predictions = {},
  sources = {},
  extras = []
) {
  const supplemental = Array.isArray(extras) ? extras : [];
  const extraById = new Map(supplemental.map((item) => [item.id, item]));
  const keys = [
    ...new Set([
      ...PHOTO_KEYS,
      ...supplemental.map((item) => item.id),
      ...Object.keys(sources || {}),
      ...Object.keys(predictions || {}),
    ]),
  ];
  return keys
    .filter((side) => {
      const extra = extraById.get(side);
      return (
        typeof sources?.[side] === "string" &&
        Boolean(sources[side]) &&
        (!extra?.type || extra.type === "photo")
      );
    })
    .map((side) => {
      const prediction = getGroupedPrediction(predictions, side);
      const rawRegions = Array.isArray(prediction?.regions)
        ? prediction.regions
        : [];
      const regions = rawRegions
        .map((region, index) => {
          if (!region || !regionGeometry(region, 1, 1)) return null;
          const cls = normaliseClassId(region.cls);
          return {
            ...region,
            cls,
            displayLabel: region.displayLabel || classLabel(cls),
            sourceDetectionId: `${side}:${index}`,
          };
        })
        .filter(Boolean);
      const label =
        PHOTO_LABELS[side] || extraById.get(side)?.label || "Additional photo";
      return {
        id: side,
        side,
        src: sources[side],
        label,
        shortLabel:
          side === "front"
            ? "Front"
            : side === "back"
            ? "Back"
            : side === "certificate"
            ? "Certificate"
            : label,
        regions,
        labels: [...new Set(regions.map((region) => region.displayLabel))],
      };
    });
}

export function buildGroupedPhotoViews(
  sections = [],
  photos = [],
  extras = []
) {
  const supplemental = Array.isArray(extras) ? extras : [];
  return sections.flatMap((section) => {
    const sides = new Set([
      ...(section.views || []).map((view) => view.side),
      ...(section.detections || []).map((detection) => detection.side),
      ...supplemental
        .filter(
          (item) =>
            item.group === section.id ||
            (section.id === "door" && item.group === "certificate")
        )
        .map((item) => item.id),
      ...(section.id === "door" ? ["certificate"] : []),
    ]);
    const matchingPhotos = photos.filter((photo) => sides.has(photo.side));
    const fallbackPhoto =
      photos.find((photo) => photo.side === section.preferredSide) ||
      photos.find((photo) => SIDES.includes(photo.side)) ||
      photos[0];
    const groupPhotos = matchingPhotos.length
      ? matchingPhotos
      : fallbackPhoto
      ? [fallbackPhoto]
      : [];
    return groupPhotos.map((photo) => {
      const detections = (section.detections || []).filter(
        (detection) => detection.side === photo.side
      );
      return {
        ...photo,
        id: `${section.id}:${photo.side}`,
        groupId: section.id,
        groupLabel: section.label,
        regions: componentRegionsForView({ detections }),
      };
    });
  });
}

export function buildSingleResultSections(
  sections = [],
  photos = [],
  extras = []
) {
  const supplemental = Array.isArray(extras) ? extras : [];
  const extraById = new Map(supplemental.map((item) => [item.id, item]));
  const groupIds = new Set(sections.map((section) => section.id));
  const certificatePhotos = photos.filter(
    (photo) =>
      photo.side === "certificate" ||
      extraById.get(photo.side)?.group === "certificate"
  );
  const isCertificate = (photo) => certificatePhotos.includes(photo);
  const doorPhotos = photos.filter((photo) => SIDES.includes(photo.side));
  const result = sections.map((section) => {
    const assigned = photos.filter(
      (photo) =>
        !isCertificate(photo) && extraById.get(photo.side)?.group === section.id
    );
    let candidates;
    if (section.id === "door") {
      const unassigned = photos.filter(
        (photo) =>
          !isCertificate(photo) &&
          !PHOTO_KEYS.includes(photo.side) &&
          !groupIds.has(extraById.get(photo.side)?.group)
      );
      candidates = [...doorPhotos, ...assigned, ...unassigned];
    } else if (section.id === "closer") {
      candidates = [
        ...doorPhotos.filter((photo) => photo.side === "back"),
        ...doorPhotos.filter((photo) => photo.side === "front"),
        ...assigned,
      ];
    } else if (section.id === "hinges") {
      const closeups = photos.filter((photo) => HINGES.includes(photo.side));
      const detectedSides = new Set(
        (section.detections || []).map((item) => item.side)
      );
      const detectedPhotos = photos.filter(
        (photo) => !isCertificate(photo) && detectedSides.has(photo.side)
      );
      const backPhoto = photos.filter((photo) => photo.side === "back");
      candidates = [...backPhoto, ...detectedPhotos, ...closeups, ...assigned];
    } else {
      candidates = [...doorPhotos, ...assigned];
    }
    const wanted = new Set(candidates.map((photo) => photo.id));
    return {
      ...section,
      label:
        section.id === "hinges"
          ? "Hinges"
          : section.id === "locks"
          ? "Locks"
          : section.label,
      photos: (section.id === "closer"
        ? candidates.filter(
            (photo, index, all) =>
              all.findIndex((item) => item.id === photo.id) === index
          )
        : photos.filter((photo) => wanted.has(photo.id))
      ).map((photo) => {
        const detections = (section.detections || []).filter(
          (detection) => detection.side === photo.side
        );
        const regions = componentRegionsForView({ detections });
        return {
          ...photo,
          groupId: section.id,
          regions,
          labels: [...new Set(regions.map((region) => region.displayLabel))],
        };
      }),
    };
  });
  const certificateEvidence = supplemental.filter(
    (item) => item.group === "certificate"
  );
  if (certificatePhotos.length || certificateEvidence.length) {
    const afterDoor = result.findIndex((section) => section.id === "door") + 1;
    result.splice(Math.max(0, afterDoor), 0, {
      id: "certificate",
      label: "Certificate",
      preferredSide: "certificate",
      detections: [],
      views: [],
      evidenceKeys: [
        ...new Set([
          ...certificatePhotos.map((photo) => photo.side),
          ...certificateEvidence.map((item) => item.id),
        ]),
      ],
      photos: certificatePhotos,
    });
  }
  return result;
}

export function buildGroupedResultDetails({
  section,
  photo,
  checklist = GROUPED_EMPTY_LIST,
  sources = GROUPED_EMPTY_MAP,
  extras = GROUPED_EMPTY_LIST,
  aggregatePhotos = false,
  predictions = GROUPED_EMPTY_MAP,
  observations = GROUPED_EMPTY_LIST,
} = {}) {
  if (!section) return null;
  const currentSide = photo?.side || "";
  const groupId = section.id;
  const definition = REPORT_PARTS.find((part) => part.id === groupId);
  const assessment = (Array.isArray(checklist) ? checklist : []).find(
    (item) => item.id === (groupId === "certificate" ? "door" : groupId)
  );
  const supplemental = (Array.isArray(extras) ? extras : []).filter(
    (item) =>
      item.group === groupId ||
      (groupId === "door" && item.group === "certificate")
  );
  const photoSpecific =
    !aggregatePhotos && (groupId === "hinges" || groupId === "locks");
  const detections = (section.detections || []).filter(
    (item) => !photoSpecific || item.side === currentSide
  );
  const detectedLabels = [
    ...new Set(
      detections
        .map((item) => item.label || classLabel(item.region?.cls))
        .filter(Boolean)
    ),
  ];
  const detectedClasses = new Set(
    detections.map((item) => normaliseClassId(item.region?.cls))
  );
  const missingLabels = (definition?.classes || [])
    .filter((cls) => !detectedClasses.has(cls))
    .map(classLabel);
  const photoContext = { sources, predictions, extras, observations };
  const allChecks = (Array.isArray(assessment?.checks) ? assessment.checks : [])
    .filter(
      (check) => groupId !== "certificate" || check.id === "door_identity"
    )
    .map((check) => withPhotoEvidence(groupId, check, photoContext));
  const hingeSides = new Set([
    ...HINGES,
    ...(section.detections || []).map((item) => item.side),
    ...supplemental.map((item) => item.id),
  ]);
  const checks =
    groupId === "hinges" && !aggregatePhotos
      ? allChecks.filter((check) => {
          const namedSide = [...hingeSides].find(
            (side) =>
              check.id === `${side}_screws` || check.id === `${side}_check`
          );
          if (namedSide) return namedSide === currentSide;
          if (
            check.source === "model-triage" &&
            check.evidenceKeys?.length === 1
          )
            return check.evidenceKeys[0] === currentSide;
          return true;
        })
      : allChecks;
  const failedChecks = checks.filter((check) => check.status === "fail");
  const status = checks.length
    ? failedChecks.length
      ? "fail"
      : checks.some((check) => check.status === "review")
      ? "review"
      : checks.every((check) => check.status === "na")
      ? "na"
      : "pass"
    : groupId === "certificate"
    ? "review"
    : assessment?.status || "review";
  const mediaChecks = checks.filter(
    (check) =>
      check.requiredMedia && check.status !== "fail" && check.status !== "na"
  );
  const evidenceChecks = checks.filter(
    (check) =>
      !check.requiredMedia &&
      check.status === "review" &&
      check.source === "evidence-required" &&
      check.needsEvidence
  );
  const needs = [
    ...new Set(evidenceChecks.map((check) => check.reason).filter(Boolean)),
  ];
  const photoEvidenceKeys = photoEvidenceKeysFor(groupId, photoContext);
  const alreadyHasEvidence =
    photoEvidenceKeys.length > 0 ||
    checks.some(
      (check) =>
        check.source === "evidence-provided" && check.evidenceKeys?.length
    ) ||
    supplemental.some(
      (item) =>
        item.group === groupId &&
        (sources[item.id] ||
          item.file?.size > 0 ||
          savedImageUrl(item) ||
          savedImageUrl(item.stored))
    );
  const glassSeen =
    (section.detections || []).some((item) =>
      ["glass", "safety_glass"].includes(normaliseClassId(item.region?.cls))
    ) ||
    allChecks.some(
      (check) => check.id === "glass_detected" && check.status === "pass"
    );
  const canAddEvidence =
    !alreadyHasEvidence &&
    ADDABLE_EVIDENCE_GROUPS.has(groupId) &&
    (groupId !== "locks" || failedChecks.length > 0) &&
    (groupId !== "glass" || glassSeen);
  const evidenceLabel =
    groupId === "certificate"
      ? "certificate"
      : groupId === "closer"
      ? "door closer"
      : groupId === "smoke_control"
      ? "intumescent strip"
      : groupId === "glass"
      ? "glass fire-marking"
      : (section.label || groupId).toLowerCase();
  const title = section.label;
  return {
    groupId,
    title,
    status,
    certificateChecks: groupId === "certificate" ? checks : [],
    copy:
      (groupId === "certificate"
        ? "Review the certificate photo and the available fire-rating text."
        : GROUPED_SECTION_COPY[groupId]) ||
      "Review the available photo evidence for this part of the door.",
    hasDetections: detectedLabels.length > 0,
    detectedLabels,
    missingLabels,
    failedChecks,
    evidenceChecks,
    mediaChecks,
    ruleChecks: [],
    questionChecks: checks.filter(
      (check) =>
        QUESTION_IDS.has(check.id) &&
        check.status !== "fail" &&
        check.status !== "na"
    ),
    needs,
    alreadyHasEvidence,
    canAddEvidence,
    evidenceLabel,
  };
}

function GroupedResultCard({ details, groupNumber, busy, onEvidence }) {
  if (!details) return null;
  const {
    groupId,
    title,
    status,
    copy,
    hasDetections,
    detectedLabels,
    missingLabels,
    failedChecks,
    needs,
    alreadyHasEvidence,
    canAddEvidence,
    evidenceLabel,
    certificateChecks = [],
    questionChecks = [],
    evidenceChecks = [],
    mediaChecks = [],
    ruleChecks = [],
  } = details;
  return (
    <article
      className="ds-simple-result-card ds-grouped-result-card"
      aria-label={`${title} details`}
      data-group-id={groupId}
      data-status={status}
    >
      <div className="ds-simple-result-title">
        <div>
          <span className="ds-simple-section-number">
            {String(groupNumber).padStart(2, "0")}
          </span>
          <h2>{title}</h2>
        </div>

        {["pass", "fail", "na"].includes(status) && (
          <CheckBadge status={status} />
        )}
      </div>
      {/* <p className="ds-simple-group-copy">{copy}</p> */}
      {groupId === "certificate" ? (
        <div className="ds-simple-certificate-details">
          <span className="ds-simple-info-label">Certificate details</span>
          {certificateChecks.length ? (
            certificateChecks.map((check, index) => (
              <p key={check.id || index}>{check.reason}</p>
            ))
          ) : (
            <p>
              {alreadyHasEvidence
                ? "Certificate evidence is attached. Its fire-rating text has not been assessed yet."
                : "No certificate photo is attached."}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* <div
            className={`ds-simple-detection-status ${
              hasDetections ? "is-detected" : "is-not-detected"
            }`}
          >
            <div className="ds-simple-detection-icon">
              {hasDetections ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                <span aria-hidden="true">&mdash;</span>
              )}
            </div>
            <div>
              <strong>{hasDetections ? "Detected" : "Not detected"}</strong>
              <p>
                {hasDetections
                  ? detectedLabels.length === 1
                    ? `${detectedLabels[0]} was identified in the available photo evidence.`
                    : `${detectedLabels.length} related items were identified in the available photo evidence.`
                  : "This group was not identified in the available model output. This does not confirm that it is missing."}
              </p>
            </div>
          </div>
          {hasDetections && (
            <div className="ds-simple-found">
              <span className="ds-simple-info-label">Identified</span>
              <p>{detectedLabels.join(", ")}</p>
            </div>
          )}
          {!hasDetections && missingLabels.length > 0 && (
            <div className="ds-simple-not-found">
              <span className="ds-simple-info-label">
                Not identified in these photos
              </span>
              <p>{missingLabels.join(", ")}</p>
            </div>
          )} */}

          {hasDetections && (
            <>
              <div className="ds-simple-detection-status is-detected">
                <div className="ds-simple-detection-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>

                <div>
                  <strong>Detected</strong>
                  <p>
                    {detectedLabels.length === 1
                      ? `${detectedLabels[0]} was identified in the available photo evidence.`
                      : `${detectedLabels.length} related items were identified in the available photo evidence.`}
                  </p>
                </div>
              </div>

              <div className="ds-simple-found">
                <span className="ds-simple-info-label">Identified</span>
                <p>{detectedLabels.join(", ")}</p>
              </div>
            </>
          )}
        </>
      )}

      {ruleChecks.map((check) => (
        <div
          className="ds-simple-found"
          key={check.id}
          data-check-id={check.id}
        >
          <span className="ds-simple-info-label">{check.label}</span>
          <p>{check.reason}</p>
        </div>
      ))}
      {failedChecks.length > 0 && (
        <div className="ds-simple-attention">
          <strong>Needs attention</strong>
          {failedChecks.map((check, index) => (
            <div key={check.id || index} data-check-id={check.id}>
              <p>
                {check.label}: {check.reason}
              </p>
              {(check.requiredMedia ||
                QUESTION_IDS.has(check.id) ||
                check.needsEvidence) && (
                <CheckEvidenceAction
                  group={groupId}
                  check={check}
                  busy={busy}
                  onEvidence={onEvidence}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {mediaChecks.length > 0 && (
        <div className="ds-simple-evidence-needs">
          {mediaChecks.map((check) => (
            <div key={check.id} data-check-id={check.id}>
              <span className="ds-simple-info-label">{check.label}</span>
              <p>{check.reason}</p>
              <CheckEvidenceAction
                group={groupId}
                check={check}
                busy={busy}
                onEvidence={onEvidence}
              />
            </div>
          ))}
        </div>
      )}
      {questionChecks.length > 0 && (
        <div className="ds-simple-evidence-needs">
          <span className="ds-simple-info-label">
            Condition / operation answers
          </span>
          {questionChecks.map((check) => (
            <div key={check.id} data-check-id={check.id}>
              <p>
                <strong>{check.label}</strong>: {check.reason}
              </p>
              {(check.requiredMedia ||
                QUESTION_IDS.has(check.id) ||
                check.needsEvidence) && (
                <CheckEvidenceAction
                  group={groupId}
                  check={check}
                  busy={busy}
                  onEvidence={onEvidence}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {evidenceChecks.length > 0 && (groupId !== "glass" || canAddEvidence) && (
        <div className="ds-simple-evidence-needs">
          <span className="ds-simple-info-label">
            Evidence to add or review
          </span>
          {evidenceChecks.map((check) => (
            <div key={check.id}>
              <p>{check.reason}</p>
              {groupId === "gaps" ? (
                <span className="ds-simple-unavailable">Not available</span>
              ) : (
                <CheckEvidenceAction
                  group={groupId}
                  check={check}
                  busy={busy}
                  onEvidence={onEvidence}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {alreadyHasEvidence && groupId !== "locks" && (
        <p className="ds-simple-evidence-present">
          <FontAwesomeIcon icon={faCheck} />
          {[...mediaChecks, ...failedChecks].some(
            (check) => check.requiredMedia && check.needsEvidence
          )
            ? "Photo evidence already provided"
            : "Evidence already provided"}
        </p>
      )}
      {canAddEvidence &&
        !mediaChecks.length &&
        !failedChecks.some((check) => check.requiredMedia) &&
        typeof onEvidence === "function" &&
        (groupId === "gaps" ? (
          <span className="ds-simple-unavailable">Not available</span>
        ) : (
          <button
            type="button"
            className="ds-button ds-button-secondary ds-simple-evidence-button"
            disabled={busy}
            onClick={() => onEvidence(groupId)}
          >
            Add {evidenceLabel} evidence
          </button>
        ))}
    </article>
  );
}

function hasGlassDetection(predictions = {}) {
  return Object.values(predictions || {}).some((value) => {
    const prediction = value?.prediction || value;
    return (Array.isArray(prediction?.regions) ? prediction.regions : []).some(
      (region) =>
        ["glass", "safety_glass"].includes(normaliseClassId(region?.cls))
    );
  });
}

function ResultsView({
  pdf,
  sources = GROUPED_EMPTY_MAP,
  predictions = GROUPED_EMPTY_MAP,
  extras = GROUPED_EMPTY_LIST,
  statuses = GROUPED_EMPTY_MAP,
  savedRecord,
  busy,
  dirty,
  retryAnalysis = false,
  blocked,
  checklist = GROUPED_EMPTY_LIST,
  observations = GROUPED_EMPTY_LIST,
  onPhotos,
  onEvidence,
  onReview,
  onSave,
  onInfo,
  readOnly = false,
  historyContext = null,
}) {
  const browserRef = useRef(null);
  const enlargeButtonRef = useRef(null);
  const individualPhotos = useMemo(
    () => buildIndividualPhotoViews(predictions, sources, extras),
    [predictions, sources, extras]
  );
  const baseSections = useMemo(
    () => buildGroupedAnnotationTour(predictions, sources, extras),
    [predictions, sources, extras]
  );
  const sections = useMemo(() => {
    const built = buildSingleResultSections(
      baseSections,
      individualPhotos,
      extras
    );
    return hasGlassDetection(predictions)
      ? built
      : built.filter((section) => section.id !== "glass");
  }, [baseSections, individualPhotos, extras, predictions]);
  const [selection, setSelection] = useState({
    sectionId: "door",
    photoId: "front",
  });
  const [imageZoomed, setImageZoomed] = useState(false);
  const [imageInteracting, setImageInteracting] = useState(false);
  const [interactionToken, setInteractionToken] = useState(0);
  const [touring, setTouring] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const [resetToken, setResetToken] = useState(0);
  const [mediaState, setMediaState] = useState({
    key: "",
    ready: false,
    failed: false,
  });
  const [pageVisible, setPageVisible] = useState(true);
  const locked = Boolean(busy || blocked);
  const sectionIndex = Math.max(
    0,
    sections.findIndex((section) => section.id === selection.sectionId)
  );
  const activeSection = sections[sectionIndex] || null;
  const photos = activeSection?.photos || GROUPED_EMPTY_LIST;
  const photoIndex = Math.max(
    0,
    photos.findIndex((photo) => photo.id === selection.photoId)
  );
  const current = photos[photoIndex] || null;
  const currentKey = `${activeSection?.id || ""}|${current?.id || ""}|${
    current?.src || ""
  }`;
  const atStart = sectionIndex === 0;
  const atEnd = sectionIndex >= sections.length - 1;
  const advancing = (touring || photos.length > 1) && !imageInteracting;
  const groupDetails = useMemo(
    () =>
      buildGroupedResultDetails({
        section: activeSection,
        photo: current,
        checklist,
        sources,
        extras,
        predictions,
        observations,
        aggregatePhotos: true,
      }),
    [
      activeSection,
      current,
      checklist,
      sources,
      extras,
      predictions,
      observations,
    ]
  );

  const choosePhoto = useCallback((section, photo) => {
    if (!section) return;
    setSelection({ sectionId: section.id, photoId: photo?.id || "" });
    setResetToken((value) => value + 1);
  }, []);
  const pausePhotos = useCallback(() => {
    setTouring(false);
  }, []);
  const restartPhotoDwell = useCallback(() => {
    setInteractionToken((value) => value + 1);
  }, []);
  const movePart = useCallback(
    (delta) => {
      if (locked) return;
      const target = sections[sectionIndex + delta];
      if (!target) return;
      setTouring(false);
      choosePhoto(target, target.photos[0]);
    },
    [locked, sections, sectionIndex, choosePhoto]
  );
  const toggleTour = () => {
    if (locked) return;
    if (touring) {
      pausePhotos();
      return;
    }
    if (!sections.length) return;
    choosePhoto(sections[0], sections[0].photos[0]);
    setTouring(true);
  };
  const closeEnlarged = useCallback(() => {
    setEnlarged(false);
    setResetToken((value) => value + 1);
  }, []);
  const onViewerReady = useCallback(
    (ready, failed) => {
      setMediaState((previous) =>
        previous.key === currentKey &&
        previous.ready === ready &&
        previous.failed === failed
          ? previous
          : { key: currentKey, ready, failed }
      );
    },
    [currentKey]
  );
  const onViewerZoom = useCallback((isZoomed) => {
    setImageZoomed(isZoomed);
  }, []);
  const openEnlarged = useCallback(() => {
    if (locked || !current) return;
    setEnlarged(true);
    setResetToken((value) => value + 1);
  }, [locked, current]);

  useEffect(() => {
    if (!activeSection) return;
    const next = { sectionId: activeSection.id, photoId: current?.id || "" };
    setSelection((previous) =>
      previous.sectionId === next.sectionId && previous.photoId === next.photoId
        ? previous
        : next
    );
  }, [activeSection?.id, current?.id]);
  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  useEffect(() => {
    if (locked || !sections.length) setTouring(false);
    if (!individualPhotos.length) setEnlarged(false);
  }, [locked, sections.length, individualPhotos.length]);
  useEffect(() => {
    if (!advancing || locked || !pageVisible || !activeSection)
      return undefined;
    if (
      current &&
      (mediaState.key !== currentKey ||
        (!mediaState.ready && !mediaState.failed))
    )
      return undefined;
    const timer = window.setTimeout(() => {
      if (photoIndex < photos.length - 1) {
        choosePhoto(activeSection, photos[photoIndex + 1]);
      } else if (touring) {
        const nextSection = sections[sectionIndex + 1];
        if (nextSection) choosePhoto(nextSection, nextSection.photos[0]);
        else pausePhotos();
      } else if (photos.length > 1) {
        choosePhoto(activeSection, photos[0]);
      }
    }, GROUPED_TOUR_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [
    advancing,
    locked,
    pageVisible,
    activeSection,
    current,
    mediaState,
    currentKey,
    photoIndex,
    photos,
    touring,
    sections,
    sectionIndex,
    choosePhoto,
    pausePhotos,
    resetToken,
    interactionToken,
  ]);

  useEffect(() => {
    const node = browserRef.current;
    if (!node) return undefined;
    const keyDown = (event) => {
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.target?.closest?.(
          'input, textarea, select, .ds-media-viewport, [contenteditable="true"]'
        )
      )
        return;
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault();
        movePart(event.key === "ArrowRight" ? 1 : -1);
      }
    };
    node.addEventListener("keydown", keyDown);
    return () => node.removeEventListener("keydown", keyDown);
  }, [movePart]);
  useEffect(() => {
    if (!enlarged) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() =>
      enlargeButtonRef.current?.focus({ preventScroll: true })
    );
    const keyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeEnlarged();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = [
        ...(browserRef.current?.querySelectorAll(
          'button:not(:disabled), a[href], [tabindex="0"]'
        ) || []),
      ].filter((node) => node.getClientRects().length);
      const first = nodes[0],
        last = nodes[nodes.length - 1];
      if (!first) return;
      const inside = browserRef.current?.contains(document.activeElement);
      if (event.shiftKey && (!inside || document.activeElement === first)) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (!inside || document.activeElement === last)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", keyDown);
      document.body.style.overflow = previousOverflow;
      enlargeButtonRef.current?.focus({ preventScroll: true });
    };
  }, [enlarged, closeEnlarged]);

  if (!individualPhotos.length) {
    return (
      <section
        className="ds-results-native ds-simple-results"
        aria-label="Door scan evidence"
      >
        {historyContext}
        {readOnly && (
          <div className="ds-browser-actions">
            <button
              type="button"
              className="ds-button ds-button-secondary"
              onClick={onPhotos}
            >
              View photos and evidence
            </button>
            <button
              type="button"
              className="ds-button ds-button-primary"
              onClick={onReview}
            >
              Saved final results
            </button>
          </div>
        )}
        <div className="ds-empty">
          <UiIcon name="evidence" />
          <strong>No photos available yet</strong>
          <p>
            Take or upload the front and back door photos to view the results.
          </p>
        </div>
      </section>
    );
  }
  const title = activeSection?.label || "Evidence";
  const regionCount = current?.regions.length || 0;
  return (
    <section
      ref={browserRef}
      className={`ds-results-native ds-simple-results ds-results-browser ds-single-results ${
        enlarged ? "is-clean-view is-zoom-view" : ""
      }`}
      role={enlarged ? "dialog" : undefined}
      aria-modal={enlarged ? true : undefined}
      aria-label={
        enlarged ? "Enlarged door scan evidence" : "Door scan evidence"
      }
      data-view-mode="grouped"
      data-photo-side={current?.side || ""}
      data-group={activeSection?.id || ""}
      data-enlarged={enlarged}
      data-playing={advancing}
      data-touring={touring}
    >
      {!enlarged && historyContext}
      {!enlarged && (
        <>
          <div className="ds-simple-results-heading">
            <div>
              <p className="ds-eyebrow">
                {readOnly ? "SAVED EVIDENCE" : "YOUR EVIDENCE"}
              </p>
              <h1>{readOnly ? "Saved door evidence" : "Your door evidence"}</h1>
            </div>
            <span
              className={`ds-pill ${
                !dirty && savedRecord ? "is-complete" : ""
              }`}
            >
              {dirty ? "Not uploaded" : savedRecord ? "Saved" : "Captured"}
            </span>
          </div>
        </>
      )}
      <div className="ds-browser-panel">
        <div className="ds-browser-controls">
          <span className="ds-browser-part-count">
            Part {sectionIndex + 1} of {sections.length}
          </span>
          <div className="ds-browser-control-actions">
            <button
              type="button"
              className="ds-browser-tour"
              disabled={locked || sections.length < 2}
              onClick={toggleTour}
              aria-pressed={touring}
              aria-label={touring ? "Stop tour" : "Tour every part and photo"}
            >
              {!touring && <UiIcon name="play" />}
              {touring ? "Stop tour" : "Tour"}
            </button>
            <button
              type="button"
              ref={enlargeButtonRef}
              className="ds-browser-enlarge"
              disabled={!enlarged && (locked || !current)}
              aria-label={enlarged ? "Close enlarged view" : "Enlarge photo"}
              aria-expanded={enlarged}
              onClick={enlarged ? closeEnlarged : openEnlarged}
            >
              {enlarged ? "Close" : "Enlarge"}
            </button>
          </div>
        </div>
        <div className="ds-browser-caption">
          <div>
            <strong>{title}</strong>
            <span aria-live={advancing ? "off" : "polite"} aria-atomic="true">
              {current
                ? `Photo ${photoIndex + 1} of ${photos.length}`
                : "No photo for this part"}
              {current &&
                ` \u00b7 ${regionCount} ${
                  regionCount === 1 ? "annotation" : "annotations"
                }`}
              {touring ? " \u00b7 Tour" : ""}
            </span>
          </div>
        </div>
        <div className="ds-simple-image-wrap">
          <InlinePhotoViewer
            src={current?.src || ""}
            side={current?.side || activeSection?.preferredSide || "front"}
            regions={current?.regions || NO_REGIONS}
            showAnnotations
            selected={-1}
            focusSelected={false}
            focusGroup
            showTools
            overlayTools={false}
            onReady={onViewerReady}
            onZoomChange={onViewerZoom}
            onGestureChange={setImageInteracting}
            onInteract={restartPhotoDwell}
            onActivate={enlarged ? undefined : openEnlarged}
            initialZoom={1}
            resetToken={resetToken}
          />
        </div>
        <div
          className="ds-simple-result-nav ds-simple-result-nav-under-image ds-browser-nav"
          role="group"
          aria-label="Part navigation"
        >
          <button
            type="button"
            className="ds-simple-nav-back"
            disabled={locked || atStart}
            onClick={() => movePart(-1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Previous
          </button>
          <span aria-live="polite" aria-atomic="true">
            <small>Part</small>
            {sectionIndex + 1} / {sections.length}
          </span>
          <button
            type="button"
            className="ds-simple-nav-next"
            disabled={locked || atEnd}
            onClick={() => movePart(1)}
          >
            Next
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {!enlarged && groupDetails && (
        <GroupedResultCard
          details={groupDetails}
          groupNumber={sectionIndex + 1}
          busy={locked}
          onEvidence={readOnly ? undefined : onEvidence}
        />
      )}
      {!enlarged && (
        <>
          {statuses[current?.side]?.error && (
            <p className="ds-inline-error" role="alert">
              {statuses[current.side].error}
            </p>
          )}
          {!readOnly && (dirty || retryAnalysis) && (
            <button
              type="button"
              className="ds-button ds-button-secondary ds-simple-save"
              disabled={locked}
              onClick={onSave}
            >
              {dirty ? "Process & upload changes" : "Retry analysis"}
            </button>
          )}
          <div className="ds-browser-actions">
            <button
              type="button"
              className="ds-button ds-button-secondary"
              disabled={locked}
              onClick={onPhotos}
            >
              {readOnly ? "View photos and evidence" : "Manage photos"}
            </button>
            <button
              type="button"
              className="ds-button ds-button-primary"
              disabled={locked}
              onClick={onReview}
            >
              Final results
            </button>
          </div>
          {pdf && <DoorScanPdfButton state={pdf} disabled={locked} />}
          <p className="ds-result-disclaimer">
            These are preliminary evidence checks, not a fire-door certificate.
            A part not detected in a photo is not automatically confirmed as
            missing.
          </p>
        </>
      )}
    </section>
  );
}

function DoorScanSession({
  doorId,
  doorName,
  predictionUrl,
  hingePredictionUrl,
  transformHingeResponse,
  services,
  user,
  authLoading,
  setupError,
  allowGuestScans,
  className,
  style,
  submitReview,
  checklistPolicy = DEFAULT_CHECKLIST_POLICY,
  pdfOptions,
}) {
  useScanPageZoomLock();
  const uid = user?.uid || "";
  const { role: currentUserRole } = useDoorScanCurrentUserRole(
    services,
    uid,
    authLoading
  );
  const isAdminUser = isDoorScanAdminRole(currentUserRole);
  const { getVisitSessionId, recordSavedScan: recordVisitScan } =
    useDoorScanVisitSession(services, authLoading, user, allowGuestScans);
  const viewportRef = useRef(null),
    deviceInputRef = useRef(null),
    pickerSideRef = useRef("front");
  const alive = useRef(false),
    busyRef = useRef(false),
    controllerRef = useRef(null),
    selectionRequest = useRef(0);
  const servicesRef = useRef(services),
    previousUid = useRef(uid),
    previousServices = useRef(services);
  servicesRef.current = services;
  const jobsRef = useRef({}),
    scanDocumentRef = useRef(null),
    sessionIdRef = useRef("");
  const revisionRef = useRef(0),
    baseRevisionRef = useRef(-1),
    mutationRef = useRef(null),
    sendingRef = useRef(false);
  const listRequest = useRef(0),
    listCursor = useRef(null);
  const captureContext = useRef({ tab: "scan", single: false });
  const contactDirty = useRef({ name: false, email: false, phone: false });
  const [tab, setTab] = useState("scan"),
    [scanName, setScanName] = useState(doorName || "");
  const [photos, setPhotos] = useState({}),
    [results, setResults] = useState({}),
    [statuses, setStatuses] = useState({});
  const [activeSide, setActiveSide] = useState("front"),
    [cameraSide, setCameraSide] = useState(null);
  const [cameraEntryRect, setCameraEntryRect] = useState(null);
  const [cameraInitialPhoto, setCameraInitialPhoto] = useState(null),
    [cameraInitialMetadata, setCameraInitialMetadata] = useState(null);
  const [hingeConfiguration, setHingeConfiguration] = useState({
    reportedCount: 3,
    recordedAt: null,
  });
  const [certificateDecision, setCertificateDecision] = useState(null),
    [extras, setExtras] = useState([]),
    [observations, setObservations] = useState([]);
  const [currentScan, setCurrentScan] = useState(null),
    [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false),
    [progress, setProgress] = useState(0),
    [stage, setStage] = useState(""),
    [error, setError] = useState("");
  const [sheet, setSheet] = useState(null),
    [receipt, setReceipt] = useState(null);
  const [contact, setContact] = useState({
    name: user?.displayName || "",
    email: user?.email || "",
    phone: user?.phoneNumber || "",
  });
  const [scans, setScans] = useState({
      rows: [],
      loading: false,
      more: false,
      error: "",
    }),
    [scanEpoch, setScanEpoch] = useState(0);
  const navigationTabs = useMemo(
    () => [
      ["scan", "Scan", faCamera],
      ["evidence", "Evidence", faImage],
      ["results", "Results", faHistory],
      ...(isAdminUser ? [["users", "Users", faUsersGear]] : []),
    ],
    [isAdminUser]
  );
  const hingeCount = hingeConfiguration.reportedCount;
  const frontBackReady = SIDES.every((key) => photos[key]);
  const dirty = currentScan
    ? revision !== (currentScan.revision || 0)
    : Boolean(
        Object.keys(photos).length || extras.length || observations.length
      );
  const authorizationMessage =
    setupError ||
    (authLoading
      ? "Checking your session..."
      : (!uid || user?.isAnonymous) && !allowGuestScans
      ? "Sign in to process and upload your scan."
      : "");
  const previewFiles = useMemo(
    () => ({
      ...Object.fromEntries(
        Object.entries(photos).filter(([, value]) => value instanceof Blob)
      ),
      ...Object.fromEntries(
        extras
          .filter((entry) => entry.file)
          .map((entry) => [entry.id, entry.file])
      ),
    }),
    [photos, extras]
  );
  const originalFiles = useMemo(
    () =>
      Object.fromEntries(
        allScanEntries(jobsRef.current, extras)
          .map(([key, job]) => [
            key,
            job.originalFile || job.capture?.originalFile || job.file,
          ])
          .filter(([, file]) => file instanceof Blob)
      ),
    [photos, extras]
  );
  const previews = usePhotoPreviews(previewFiles),
    originalPreviews = usePhotoPreviews(originalFiles);
  const sources = useMemo(
    () => ({
      ...Object.fromEntries(
        [
          ...Object.entries(currentScan?.photos || {}),
          ...(currentScan?.supplemental || []).map((entry) => [
            entry.id,
            entry,
          ]),
        ]
          .filter(([key]) =>
            PHOTO_KEYS.includes(key)
              ? Object.prototype.hasOwnProperty.call(photos, key)
              : extras.some((entry) => entry.id === key)
          )
          .map(([key, value]) => [key, savedImageUrl(value)])
      ),
      ...previews,
    }),
    [currentScan, previews, photos, extras]
  );
  const originalSources = useMemo(
    () => ({
      ...Object.fromEntries(
        [
          ...Object.entries(currentScan?.photos || {}),
          ...(currentScan?.supplemental || []).map((entry) => [
            entry.id,
            entry,
          ]),
        ]
          .filter(([key]) =>
            PHOTO_KEYS.includes(key)
              ? Object.prototype.hasOwnProperty.call(photos, key)
              : extras.some((entry) => entry.id === key)
          )
          .map(([key, value]) => [
            key,
            safeMediaUrl(value.originalImage?.url) || savedImageUrl(value),
          ])
      ),
      ...originalPreviews,
    }),
    [currentScan, originalPreviews, photos, extras]
  );
  const checklist = useMemo(
    () =>
      buildSectionChecklist({
        predictions: results,
        sources,
        extras,
        observations,
        hingeConfiguration,
        certificateDecision,
        policy: checklistPolicy,
      }),
    [
      results,
      sources,
      extras,
      observations,
      hingeConfiguration,
      certificateDecision,
      checklistPolicy,
    ]
  );

  const getPdfSnapshot = useCallback(
    () => ({
      scan: currentScan || {},
      scanId: currentScan?.id || sessionIdRef.current || "",
      doorId: currentScan?.doorId ?? doorId,
      doorName: scanName || currentScan?.doorName || doorName || "Door scan",
      revision,
      checklist,
      checklistVersion: SECTION_CHECKLIST_VERSION,
      predictions: results,
      groupedSections: buildGroupedAnnotationTour(results, sources, extras),
      parts: REPORT_PARTS,
      classNames,
      sectionCopy: GROUPED_SECTION_COPY,
      hiddenCheckLabels: [LOCK_CONFIGURATION_CHECK],
      questions: Object.fromEntries(
        Object.values(SECTION_QUESTIONS)
          .flat()
          .map((question) => [question.id, question.label])
      ),
      photoKeys: Object.keys(photos),
      photoMetadata: Object.fromEntries(
        allScanEntries(jobsRef.current, extras).map(([key, job]) => [
          key,
          { ...job.stored, ...job },
        ])
      ),
      sources,
      files: previewFiles,
      extras,
      statuses,
      observations,
      hingeConfiguration,
      certificateDecision,
      contact,
      receipt,
      dirty,
    }),
    [
      currentScan,
      doorId,
      doorName,
      scanName,
      revision,
      checklist,
      results,
      sources,
      photos,
      previewFiles,
      extras,
      statuses,
      observations,
      hingeConfiguration,
      certificateDecision,
      contact,
      receipt,
      dirty,
    ]
  );
  const pdf = useDoorSnapshotPdf({
    getSnapshot: getPdfSnapshot,
    resetKey: `${uid}:${
      currentScan?.id || sessionIdRef.current || "draft"
    }:${revision}:${scanEpoch}`,
    options: pdfOptions,
  });

  const totalCount = Object.keys(photos).length + extras.length;
  const uploadedCount = [
    ...Object.keys(photos),
    ...extras.map((entry) => entry.id),
  ].filter((key) => statuses[key]?.phase === "uploaded").length;
  const hasFailedAnalysis = allScanEntries(jobsRef.current, extras).some(
    ([, job]) => job.analysisStatus === "failed"
  );
  const reset = useCallback(
    (notice = "") => {
      selectionRequest.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
      busyRef.current = false;
      sendingRef.current = false;
      jobsRef.current = {};
      scanDocumentRef.current = null;
      sessionIdRef.current = "";
      revisionRef.current = 0;
      baseRevisionRef.current = -1;
      mutationRef.current = null;
      setPhotos({});
      setResults({});
      setStatuses({});
      setExtras([]);
      setObservations([]);
      setCurrentScan(null);
      setRevision(0);
      setCameraSide(null);
      setCameraInitialPhoto(null);
      setCameraInitialMetadata(null);
      setActiveSide("front");
      setCertificateDecision(null);
      setHingeConfiguration({ reportedCount: 3, recordedAt: null });
      setBusy(false);
      setProgress(0);
      setStage("");
      setSheet(null);
      setTab("scan");
      setReceipt(null);
      setError(notice);
      setScanName(doorName || "");
    },
    [doorName]
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      selectionRequest.current += 1;
      listRequest.current += 1;
      controllerRef.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (!isAdminUser && tab === "users") setTab("results");
  }, [isAdminUser, tab]);

  useEffect(() => {
    if (previousUid.current && previousUid.current !== uid) {
      reset(
        "Your sign-in changed. Previous scans remain saved to their owner."
      );
      contactDirty.current = { name: false, email: false, phone: false };
      setContact({ name: "", email: "", phone: "" });
    }
    previousUid.current = uid;
    listRequest.current += 1;
    listCursor.current = null;
    setScans({ rows: [], loading: false, more: false, error: "" });
  }, [uid, reset]);
  useEffect(() => {
    if (previousServices.current !== services) {
      previousServices.current = services;
      reset("The Firebase connection changed. Start a fresh scan.");
    }
  }, [services, reset]);
  useEffect(() => {
    setContact((previous) => ({
      name: contactDirty.current.name ? previous.name : user?.displayName || "",
      email: contactDirty.current.email ? previous.email : user?.email || "",
      phone: contactDirty.current.phone
        ? previous.phone
        : user?.phoneNumber || "",
    }));
  }, [user?.displayName, user?.email, user?.phoneNumber, uid]);
  useEffect(() => {
    const element = viewportRef.current,
      parent = element?.parentElement;
    let frame = 0;
    const measure = () => {
      if (!element) return;
      const viewport = window.visualViewport;
      const bottom = viewport
        ? viewport.height + viewport.offsetTop
        : window.innerHeight;
      const top = Math.max(0, element.getBoundingClientRect().top);
      const padding = parent
        ? parseFloat(window.getComputedStyle(parent).paddingBottom) || 0
        : 0;
      element.style.setProperty(
        "--ds-available-height",
        `${Math.max(240, Math.floor(bottom - top - padding))}px`
      );
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : null;
    if (parent) observer?.observe(parent);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const change = () => {
    revisionRef.current += 1;
    setRevision(revisionRef.current);
    mutationRef.current = null;
    setReceipt(null);
    setError("");
  };
  const closeCamera = () => {
    setCameraSide(null);
    setCameraInitialPhoto(null);
    setCameraInitialMetadata(null);
  };
  const closeSheet = () => {
    if (busyRef.current || sendingRef.current) return;
    if (sheet?.returnTo === "final") {
      setSheet({ kind: "final", scrollTop: sheet.returnScroll || 0 });
      return;
    }
    setSheet(null);
  };
  const photoState = (key, value) => {
    if (alive.current)
      setStatuses((previous) => ({ ...previous, [key]: value }));
  };
  const selectCapture = (key) => {
    if (busyRef.current || !PHOTO_KEYS.includes(key)) return;
    setActiveSide(key);
    setError("");
    if (cameraSide) {
      setCameraInitialPhoto(null);
      setCameraInitialMetadata(null);
      setCameraSide(key);
    }
  };
  const openCamera = (key = activeSide, single = tab !== "scan") => {
    if (busyRef.current || !PHOTO_KEYS.includes(key)) return;
    const gear = viewportRef.current?.querySelector(
      ".ds-native-scan .ds-solid-gear"
    );
    const rect = gear?.getBoundingClientRect();
    setCameraEntryRect(
      rect
        ? {
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
          }
        : null
    );
    captureContext.current = { tab, single };
    setSheet(null);
    setActiveSide(key);
    setCameraInitialPhoto(null);
    setCameraInitialMetadata(null);
    setCameraSide(key);
    setError("");
  };
  const openDeviceUpload = (key = activeSide) => {
    if (busyRef.current || !PHOTO_KEYS.includes(key)) return;
    captureContext.current = { tab, single: tab !== "scan" };
    setSheet(null);
    pickerSideRef.current = key;
    setActiveSide(key);
    deviceInputRef.current.value = "";
    deviceInputRef.current.click();
  };
  const selectDeviceFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busyRef.current) return;
    const key = pickerSideRef.current,
      request = ++selectionRequest.current;
    busyRef.current = true;
    setBusy(true);
    setStage("Preparing photo");
    setError("");
    try {
      const prepared = await prepareSelectedPhoto(file, key);
      if (!alive.current || request !== selectionRequest.current) return;
      setCameraInitialPhoto(prepared);
      setCameraInitialMetadata({
        source: "device",
        fullFrame: true,
        appCrop: false,
        originalFile: file,
      });
      setCameraSide(key);
    } catch (failure) {
      if (alive.current && request === selectionRequest.current)
        setError(friendlyError(failure));
    } finally {
      if (request === selectionRequest.current) {
        busyRef.current = false;
        if (alive.current) {
          setBusy(false);
          setStage("");
        }
      }
    }
  };
  const acceptPhoto = (file, key, source, metadata) => {
    if (busyRef.current || !PHOTO_KEYS.includes(key)) return;
    const photo = {
      file,
      captureId: newId(),
      capturedAt: Date.now(),
      capture: metadata || { source, fullFrame: true, appCrop: false },
      originalFile: metadata?.originalFile || file,
      analysisStatus: key === "certificate" ? "not-requested" : "pending",
    };
    const nextPhotos = { ...photos, [key]: file };
    const replacingCore = SIDES.includes(key) && Boolean(jobsRef.current[key]);
    jobsRef.current[key] = photo;
    if (replacingCore)
      setObservations((previous) =>
        previous.map((item) =>
          item.type === "checklist-answer" ? { ...item, stale: true } : item
        )
      );
    change();
    setPhotos(nextPhotos);
    setResults((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    photoState(key, { phase: "captured" });
    if (key === "certificate") setCertificateDecision(null);
    const next = nextCaptureKey(
      nextPhotos,
      hingeCount,
      key === "certificate" ? null : certificateDecision
    );
    setActiveSide(next || key);
    setCameraInitialPhoto(null);
    setCameraInitialMetadata(null);
    setCameraSide(
      !captureContext.current.single && source === "camera" ? next : null
    );
    setTab(captureContext.current.single ? captureContext.current.tab : "scan");
  };
  const chooseHingeCount = (value) => {
    if (busyRef.current || ![2, 3].includes(value)) return;
    change();
    setHingeConfiguration({
      reportedCount: value,
      recordedAt: new Date().toISOString(),
    });
    setSheet(null);
    if (value === 2 && activeSide === "hinge_middle")
      setActiveSide("hinge_bottom");
    if (value === 2 && cameraSide === "hinge_middle") {
      setCameraInitialPhoto(null);
      setCameraInitialMetadata(null);
      setCameraSide("hinge_bottom");
    }
  };
  const chooseCertificateReason = (status) => {
    if (busyRef.current || photos.certificate) return;
    change();
    setCertificateDecision({ status, recordedAt: new Date().toISOString() });
    setSheet(null);
    closeCamera();
  };
  const saveCurrent = async ({ forReview = false } = {}) => {
    if (busyRef.current || (sendingRef.current && !forReview)) return null;
    if (!frontBackReady) {
      setError("Take and accept the front and back photos first.");
      return null;
    }
    if (
      currentScan &&
      !dirty &&
      !hasFailedAnalysis &&
      currentScan.checklist?.version === SECTION_CHECKLIST_VERSION &&
      Array.isArray(currentScan.checklist?.sections) &&
      checklistFingerprint(currentScan.checklist.sections) ===
        checklistFingerprint(checklist)
    )
      return currentScan;
    if (!services || authLoading || authorizationMessage) {
      setError(authorizationMessage || "Your Firebase session is not ready.");
      return null;
    }
    const creatingNewScan = !currentScan;
    const controller = new AbortController();
    controllerRef.current = controller;
    busyRef.current = true;
    setBusy(true);
    setError("");
    setProgress(0);
    const report = (text, percent) => {
      if (alive.current && !controller.signal.aborted) {
        setStage(text);
        setProgress(Math.round(percent));
      }
    };
    try {
      report("Preparing your scan", 1);
      const owner = await ensureScanOwner(services.auth, allowGuestScans);
      const valid = () => {
        if (!alive.current || servicesRef.current !== services)
          throw new Error("The session changed.");
        assertOwner(services, owner.uid, controller.signal);
      };
      valid();
      const visitSessionId =
        currentScan?.visitSessionId ||
        (creatingNewScan ? getVisitSessionId(owner.uid) : "");
      if (!sessionIdRef.current) sessionIdRef.current = newId();
      if (!scanDocumentRef.current)
        scanDocumentRef.current = doc(
          collection(services.db, SCAN_COLLECTION, owner.uid, "scans")
        );
      if (currentScan && !dirty) {
        setReceipt(null);
        revisionRef.current += 1;
        setRevision(revisionRef.current);
        mutationRef.current = null;
      }
      if (!mutationRef.current) mutationRef.current = newId();
      const entries = allScanEntries(jobsRef.current, extras),
        predictions = { ...results };
      const textUrl = extractTextEndpoint(predictionUrl);
      let optionalFailures = 0;
      for (let index = 0; index < entries.length; index += 1) {
        const [key, job] = entries[index];
        valid();
        if (job.ownerId && job.ownerId !== owner.uid)
          throw new Error("The scan belongs to a different user.");
        job.ownerId = owner.uid;
        const needsTextExtraction =
          key === "certificate" ||
          HINGES.includes(key) ||
          (job.type === "photo" &&
            (job.group === "hinges" || job.group === "certificate"));
        if (
          needsTextExtraction &&
          !job.extractedText &&
          !job.textAnalysisAttempted
        ) {
          try {
            const textFile = await remoteAnalysisFile(job, controller.signal);
            valid();
            job.extractedText = await extractTextFromPhoto(
              textFile,
              textUrl,
              controller.signal
            );
            job.textAnalysisAttempted = true;
            job.textAnalysisError = "";
            if (HINGES.includes(key) || job.group === "hinges")
              job.hingeTextResult = applyHingeTextResult(job.extractedText);
            if (key === "certificate" || job.group === "certificate")
              job.certTextResult = applyCertTextResult(job.extractedText);
          } catch (failure) {
            valid();
            job.textAnalysisAttempted = true;
            job.textAnalysisError = friendlyError(failure);
            if (HINGES.includes(key) || job.group === "hinges")
              job.hingeTextResult = applyHingeTextResult("");
            if (key === "certificate" || job.group === "certificate")
              job.certTextResult = applyCertTextResult("");
          }
        }
        const endpoint = endpointFor(
          key,
          job,
          predictionUrl,
          hingePredictionUrl
        );
        if (!endpoint) {
          job.analysisStatus = "not-requested";
          job.analysisError = "";
          job.prediction = null;
          job.endpoint = null;
          delete predictions[key];
          continue;
        }
        if (job.analysisStatus === "complete" && (job.prediction || job.stored))
          continue;
        photoState(key, { phase: "analysing" });
        report(
          `Analysing ${PHOTO_LABELS[key] || job.label}`,
          5 + (index / entries.length) * 40
        );
        try {
          const file = await remoteAnalysisFile(job, controller.signal);
          valid();
          job.endpoint = endpoint;
          job.prediction = await predictPhoto(
            file,
            endpoint,
            controller.signal,
            endpoint === hingePredictionUrl ? transformHingeResponse : null,
            { key, kind: endpoint === hingePredictionUrl ? "hinge" : "door" }
          );
          valid();
          job.predictionChanged = true;
          job.analysisStatus = "complete";
          job.analysisError = "";
          predictions[key] = job.prediction;
          setResults({ ...predictions });
          photoState(key, { phase: "captured" });
        } catch (failure) {
          valid();
          job.analysisStatus = "failed";
          job.analysisError = friendlyError(failure);
          photoState(key, { phase: "captured", error: job.analysisError });
          if (SIDES.includes(key))
            throw new Error(`${labelFor(key)}: ${job.analysisError}`);
          optionalFailures += 1;
        }
      }
      valid();
      const textResults = Object.fromEntries(
        hingePhotoKeys(hingeConfiguration.reportedCount || 3)
          .filter((key) => jobsRef.current[key])
          .map((key) => [
            key,
            jobsRef.current[key].hingeTextResult ||
              applyHingeTextResult(jobsRef.current[key].extractedText || ""),
          ])
      );
      const certifiedCount = Object.values(textResults).filter(
        (item) => item.certified
      ).length;
      const nextHingeConfiguration = {
        ...hingeConfiguration,
        textResults,
        certifiedCount,
        overallCertified: certifiedCount >= 1,
      };
      const certificateJob = jobsRef.current.certificate;
      const certResult = certificateJob
        ? certificateJob.certTextResult ||
          applyCertTextResult(certificateJob.extractedText || "")
        : null;
      const nextCertificateDecision = certificateJob
        ? {
            status: "photo_captured",
            analysed: true,
            rating: certResult.rating,
            confidence: certResult.confidence,
            matches: certResult.matches,
            text: certificateJob.extractedText || "",
          }
        : certificateDecision;
      setHingeConfiguration(nextHingeConfiguration);
      if (certificateJob) setCertificateDecision(nextCertificateDecision);
      const saved = await saveScan({
        jobs: jobsRef.current,
        documentRef: scanDocumentRef.current,
        services,
        owner,
        doorId: currentScan?.doorId ?? doorId,
        doorName: scanName || "Door scan",
        sessionId: sessionIdRef.current,
        visitSessionId,
        signal: controller.signal,
        report,
        hingeConfiguration: nextHingeConfiguration,
        certificateDecision: nextCertificateDecision,
        extras,
        observations,
        revision: revisionRef.current,
        baseRevision: baseRevisionRef.current,
        mutationId: mutationRef.current,
        onPhotoState: photoState,
        checklistPolicy,
      });
      valid();
      if (creatingNewScan && saved.visitSessionId) recordVisitScan(saved);
      baseRevisionRef.current = saved.revision;
      setCurrentScan(saved);
      setResults(predictions);
      setScanEpoch((value) => value + 1);
      if (optionalFailures)
        setStage(
          `Evidence uploaded. ${optionalFailures} hinge photo${
            optionalFailures === 1 ? "" : "s"
          } need an analysis retry.`
        );
      if (!forReview) setTab("evidence");
      return saved;
    } catch (failure) {
      if (alive.current && !controller.signal.aborted) {
        setError(friendlyError(failure));
        setStage("Not fully saved. Your accepted evidence is still here.");
        setStatuses((previous) =>
          Object.fromEntries(
            Object.entries(previous).map(([key, value]) => {
              if (!["uploading", "saving", "analysing"].includes(value.phase))
                return [key, value];
              const savedPhoto = storedPhoto(currentScan, key);
              const job =
                jobsRef.current[key] ||
                extras.find((entry) => entry.id === key);
              const uploaded =
                savedPhoto && savedPhoto.captureId === job?.captureId;
              return [
                key,
                {
                  phase: uploaded ? "uploaded" : "captured",
                  percent: uploaded ? 100 : 0,
                  error: value.error || "",
                },
              ];
            })
          )
        );
      }
      return null;
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        busyRef.current = false;
        if (alive.current) setBusy(false);
      }
    }
  };
  const loadScans = useCallback(
    async (append = false) => {
      if (
        !services ||
        !uid ||
        authLoading ||
        (user?.isAnonymous && !allowGuestScans)
      )
        return;
      const request = ++listRequest.current;
      setScans((previous) => ({ ...previous, loading: true, error: "" }));
      try {
        const constraints = [orderBy("createdAt", "desc")];
        if (append && listCursor.current)
          constraints.push(startAfter(listCursor.current));
        constraints.push(limit(6));
        const snapshot = await getDocs(
          query(
            collection(services.db, SCAN_COLLECTION, uid, "scans"),
            ...constraints
          )
        );
        if (
          !alive.current ||
          request !== listRequest.current ||
          services.auth.currentUser?.uid !== uid ||
          servicesRef.current !== services
        )
          return;
        const docs = snapshot.docs.slice(0, 5);
        if (docs.some((item) => item.data().ownerId !== uid))
          throw new Error("A scan could not be verified for this account.");
        listCursor.current = docs[docs.length - 1] || null;
        const rows = docs
          .filter(
            (item) => item.data().photos?.front && item.data().photos?.back
          )
          .map((item) => ({
            ...item.data(),
            id: item.id,
            documentPath: item.ref.path,
          }));
        setScans((previous) => ({
          rows: append
            ? [
                ...previous.rows,
                ...rows.filter(
                  (row) => !previous.rows.some((old) => old.id === row.id)
                ),
              ]
            : rows,
          loading: false,
          more: snapshot.docs.length > 5,
          error: "",
        }));
      } catch (failure) {
        if (alive.current && request === listRequest.current)
          setScans((previous) => ({
            ...previous,
            loading: false,
            error: friendlyError(failure),
          }));
      }
    },
    [services, uid, authLoading, user?.isAnonymous, allowGuestScans]
  );
  useEffect(() => {
    loadScans(false);
  }, [loadScans, scanEpoch]);
  const openSavedScan = async (row) => {
    if (busyRef.current || !services || !uid) return;
    if (
      dirty &&
      !window.confirm(
        "Discard your unsaved changes and open this scan? Uploaded evidence is kept."
      )
    )
      return;
    const controller = new AbortController();
    controllerRef.current = controller;
    busyRef.current = true;
    setBusy(true);
    setError("");
    setStage("Opening saved scan");
    try {
      const expectedPath = `${SCAN_COLLECTION}/${uid}/scans/${row.id}`;
      if (row.documentPath !== expectedPath)
        throw new Error("Invalid scan path.");
      const snapshot = await getDoc(doc(services.db, expectedPath));
      assertOwner(services, uid, controller.signal);
      if (!snapshot.exists() || snapshot.data().ownerId !== uid)
        throw new Error("This scan is not available to your account.");
      const record = {
        ...snapshot.data(),
        id: snapshot.id,
        documentPath: expectedPath,
      };
      let details;
      try {
        details = await loadScanPredictions(
          services,
          record,
          controller.signal
        );
      } catch (failure) {
        details = { predictions: {}, errors: { scan: friendlyError(failure) } };
      }
      if (!alive.current || servicesRef.current !== services) return;
      assertOwner(services, uid, controller.signal);
      const hydrated = (key, stored) => ({
        file: null,
        stored,
        captureId: stored.captureId || stored.imageId || key,
        prediction: details.predictions[key] || null,
        analysisStatus:
          !details.predictions[key] &&
          (details.errors[key] || details.errors.scan) &&
          key !== "certificate" &&
          stored.analysisStatus !== "not-requested"
            ? "failed"
            : stored.analysisStatus || "complete",
        analysisError:
          stored.analysisError ||
          details.errors[key] ||
          details.errors.scan ||
          "",
        endpoint: stored.endpoint || null,
        ownerId: uid,
        type: stored.type,
        group: stored.group,
        checkId: stored.checkId || null,
        questionId: stored.questionId || null,
        id: key,
        label: stored.label,
        note: stored.note || "",
        extractedText: stored.extractedText || "",
        textAnalysisAttempted: Boolean(
          stored.extractedText ||
            stored.textAnalysisError ||
            stored.hingeTextResult ||
            stored.certTextResult
        ),
        textAnalysisError: stored.textAnalysisError || "",
        hingeTextResult: stored.hingeTextResult || null,
        certTextResult: stored.certTextResult || null,
      });
      jobsRef.current = Object.fromEntries(
        Object.entries(record.photos).map(([key, stored]) => [
          key,
          hydrated(key, stored),
        ])
      );
      scanDocumentRef.current = snapshot.ref;
      sessionIdRef.current = record.sessionId || newId();
      baseRevisionRef.current = record.revision || 0;
      revisionRef.current = record.revision || 0;
      mutationRef.current = null;
      setRevision(revisionRef.current);
      setCurrentScan(record);
      setPhotos(
        Object.fromEntries(
          Object.keys(record.photos).map((key) => [key, { saved: true }])
        )
      );
      setExtras(
        (record.supplemental || []).map((entry) => ({
          ...hydrated(entry.id, entry),
          type: entry.type || "photo",
          group: entry.group || "door",
          label: entry.label || entry.fileName || "Evidence",
        }))
      );
      setStatuses(
        Object.fromEntries(
          [
            ...Object.entries(record.photos),
            ...(record.supplemental || []).map((entry) => [entry.id, entry]),
          ].map(([key, entry]) => [
            key,
            {
              phase: "uploaded",
              percent: 100,
              error: entry.analysisError || details.errors[key] || "",
            },
          ])
        )
      );
      setResults(details.predictions);
      setObservations(record.observations || []);
      setHingeConfiguration(
        record.hinges || { reportedCount: 3, recordedAt: null }
      );
      setCertificateDecision(record.certification || null);
      setScanName(record.doorName || "");
      setActiveSide("front");
      setReceipt(null);
      setSheet(null);
      closeCamera();
      setStage("Saved scan opened");
      setProgress(100);
      setTab("evidence");
      if (details.errors.scan)
        setError(
          `Photos are available, but labels could not load: ${details.errors.scan}`
        );
    } catch (failure) {
      if (alive.current && !controller.signal.aborted)
        setError(friendlyError(failure));
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        busyRef.current = false;
        if (alive.current) setBusy(false);
      }
    }
  };
  const addEvidence = async ({
    group,
    type,
    files: incoming = [],
    note = "",
    checkId = null,
    questionId = null,
  }) => {
    if (busyRef.current) return;
    const requirement = mediaRequirementFor(group, checkId || "");
    if (requirement) {
      if (type !== requirement.type || !incoming.length)
        throw new Error(
          requirement.type === "video"
            ? "Attach a closing video; a photo or note cannot replace it."
            : checkId === LOCK_CONFIGURATION_CHECK
            ? "Attach certificate evidence; a note or video cannot replace it."
            : "Attach a photo; a note or video cannot replace it."
        );
      checkId = requirement.checkId;
      questionId = null;
    }
    if (incoming.length + extras.length > MAX_EXTRAS)
      throw new Error(`Use up to ${MAX_EXTRAS} additional files per scan.`);
    if (note && observations.length >= 40)
      throw new Error("This scan already has 40 notes.");
    busyRef.current = true;
    setBusy(true);
    setStage("Preparing additional evidence");
    const request = ++selectionRequest.current;
    try {
      const added = [];
      for (const file of incoming) {
        if (
          !file.size ||
          file.size > (type === "photo" ? MAX_INPUT_BYTES : 50 * 1024 * 1024)
        )
          throw new Error(`${file.name}: file is empty or too large.`);
        if (
          type === "video" &&
          !/^video\/(mp4|quicktime|webm)$/i.test(file.type)
        )
          throw new Error("Choose MP4, MOV or WebM video.");
        if (type === "document" && file.type !== "application/pdf")
          throw new Error("Choose a PDF document.");
        const prepared =
          type === "photo"
            ? await prepareSelectedPhoto(file, "evidence")
            : file;
        if (!alive.current || request !== selectionRequest.current) return;
        const id = `extra-${newId()}`;
        added.push({
          id,
          captureId: id,
          file: prepared,
          originalFile: file,
          type,
          group,
          checkId,
          questionId,
          capturedAt: Date.now(),
          note,
          label: file.name.slice(0, 120) || "Additional evidence",
          capture: { source: "device", fullFrame: true, appCrop: false },
          analysisStatus:
            group === "hinges" && type === "photo"
              ? "pending"
              : "not-requested",
        });
      }
      if (!alive.current || request !== selectionRequest.current) return;
      change();
      setExtras((previous) => [...previous, ...added]);
      added.forEach((entry) => photoState(entry.id, { phase: "captured" }));
      if (note)
        setObservations((previous) => [
          ...previous,
          {
            id: newId(),
            group,
            checkId,
            questionId,
            text: note,
            type: "resident-note",
            recordedAt: new Date().toISOString(),
          },
        ]);
      if (sheet?.returnTo === "final") {
        setSheet({ kind: "final", scrollTop: sheet.returnScroll || 0 });
      } else {
        setSheet(null);
      }
      setStage("Evidence added on this device. Save to upload it.");
    } finally {
      if (request === selectionRequest.current) {
        busyRef.current = false;
        if (alive.current) setBusy(false);
      }
    }
  };
  const removeExtra = (id) => {
    if (
      busyRef.current ||
      !window.confirm(
        "Remove this extra file from the scan? Changes take effect when you save."
      )
    )
      return;
    change();
    setExtras((previous) => previous.filter((entry) => entry.id !== id));
    setResults((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
    setStatuses((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
  };
  const sendReview = async ({
    contact: submittedContact,
    message,
    consent,
    summaryAcknowledged,
    reviewedFingerprint,
  }) => {
    if (!summaryAcknowledged)
      throw new Error("Review the final result before sending.");
    if (busyRef.current || sendingRef.current || !consent) return;
    const details = validateContact(submittedContact);
    sendingRef.current = true;
    setError("");
    let controller;
    try {
      const record = await saveCurrent({ forReview: true });
      if (!record) return;
      if (
        !record.checklist ||
        reviewedFingerprint !== checklistFingerprint(record.checklist.sections)
      ) {
        setSheet({ kind: "final" });
        setError(
          "Analysis updated the checklist. Review the updated final result before sending. Your evidence has been saved."
        );
        return;
      }
      const owner = services.auth.currentUser;
      if (!owner || owner.uid !== record.ownerId)
        throw new Error("Sign in as the owner before submitting.");
      controller = new AbortController();
      controllerRef.current = controller;
      busyRef.current = true;
      setBusy(true);
      setStage("Submitting review request");
      const requestId = `${record.id}-r${record.revision || 0}`;
      const payload = {
        requestId,
        scanId: record.id,
        documentPath: record.documentPath,
        ownerId: owner.uid,
        doorId: record.doorId || null,
        doorName: record.doorName,
        revision: record.revision || 0,
        photos: record.photos,
        supplemental: record.supplemental || [],
        certification: record.certification || { status: "not-provided" },
        evidence: {
          photos: record.photos,
          supplemental: record.supplemental || [],
        },
        hinges: record.hinges,
        observations: record.observations || [],
        checklist: record.checklist,
        summaryAcknowledged: {
          granted: true,
          recordedAt: new Date().toISOString(),
        },
        contact: details,
        name: details.name,
        email: details.email,
        phone: details.phone,
        contactEmail: details.email,
        message: message.slice(0, 2000),
        consent: { granted: true, recordedAt: new Date().toISOString() },
      };
      let confirmation;
      if (typeof submitReview === "function") {
        const response = await submitReview({
          ...payload,
          signal: controller.signal,
        });
        if (
          !response ||
          response.error ||
          !(
            response.id ||
            response.requestId ||
            response.reference ||
            ["submitted", "queued", "received", "accepted"].includes(
              response.status
            )
          )
        )
          throw new Error(
            response?.error ||
              "The review service did not confirm receipt. Retry with the same request reference."
          );
        confirmation = {
          reference: String(
            response.reference || response.id || response.requestId || requestId
          ),
          delivery: "external-service",
        };
      } else {
        const parent = doc(services.db, record.documentPath),
          queueRef = doc(parent, "reviewRequests", requestId);
        await runTransaction(services.db, async (transaction) => {
          assertOwner(services, owner.uid, controller.signal);
          const latest = await transaction.get(parent);
          const queued = await transaction.get(queueRef);
          if (
            !latest.exists() ||
            latest.data().ownerId !== owner.uid ||
            (latest.data().revision || 0) !== record.revision
          )
            throw new Error(
              "The evidence changed. Reopen and review the latest scan."
            );
          if (queued.exists()) {
            if (queued.data().ownerId !== owner.uid)
              throw new Error(
                "Review request ownership could not be verified."
              );
            const prior = queued.data();
            if (
              ["name", "email", "phone"].some(
                (key) => prior.contact?.[key] !== details[key]
              ) ||
              prior.message !== payload.message
            )
              throw new Error(
                "A request for this scan version is already saved with different contact details. Your existing request was not changed."
              );
            return;
          }
          transaction.set(queueRef, {
            requestId,
            scanId: record.id,
            documentPath: record.documentPath,
            ownerId: owner.uid,
            revision: record.revision,
            status: "queued",
            contact: details,
            message: payload.message,
            consent: payload.consent,
            createdAt: serverTimestamp(),
            evidence: [
              ...Object.values(record.photos),
              ...(record.supplemental || []),
            ].map((entry) => ({
              captureId: entry.captureId,
              side: entry.side,
              originalPath:
                entry.originalImage?.path || entry.displayImage?.path || null,
              displayPath:
                entry.displayImage?.path || entry.originalImage?.path || null,
              resultChunkIds: entry.resultChunkIds || [],
            })),
            certification: record.certification || { status: "not-provided" },
            hinges: record.hinges || { reportedCount: null },
            observations: record.observations || [],
            checklist: record.checklist,
            summaryAcknowledged: payload.summaryAcknowledged,
          });
        });
        confirmation = { reference: requestId, delivery: "portal-queue" };
      }
      if (!alive.current || servicesRef.current !== services) return;
      assertOwner(services, owner.uid, controller.signal);
      setReceipt(confirmation);
      setStage("Review request saved");
    } catch (failure) {
      if (alive.current && !controller?.signal.aborted)
        setError(friendlyError(failure));
    } finally {
      sendingRef.current = false;
      if (controller && controllerRef.current === controller) {
        controllerRef.current = null;
        busyRef.current = false;
        if (alive.current) setBusy(false);
      }
    }
  };
  const editContact = (value) => {
    Object.keys(value).forEach((key) => {
      if (value[key] !== contact[key]) contactDirty.current[key] = true;
    });
    setContact(value);
  };
  const changeTab = (next) => {
    if (busyRef.current) return;
    if (next === "users" && !isAdminUser) return;
    setTab(next);
  };
  const answerQuestion = (group, questionId, answer) => {
    if (busyRef.current || sendingRef.current || !QUESTION_IDS.has(questionId))
      return;
    if (
      !(
        questionId === "escapeSide"
          ? ["front", "back", "unknown"]
          : ["yes", "no", "unknown"]
      ).includes(answer)
    )
      return;
    change();
    setObservations((previous) => [
      ...previous.filter(
        (item) =>
          !(item.type === "checklist-answer" && item.questionId === questionId)
      ),
      {
        id: newId(),
        group,
        questionId,
        answer,
        type: "checklist-answer",
        source: "resident",
        recordedAt: new Date().toISOString(),
        evidenceRevision: revisionRef.current,
      },
    ]);
  };
  const currentSheetReturn = () => {
    if (sheet?.returnTo) {
      return {
        returnTo: sheet.returnTo,
        returnScroll: sheet.returnScroll || 0,
      };
    }
    if (sheet?.kind === "final") {
      const scrollTop =
        document.querySelector(".ds-sheet .ds-sheet-scroll")?.scrollTop || 0;
      return { returnTo: "final", returnScroll: scrollTop };
    }
    return {};
  };
  const openSheet = (next) => setSheet({ ...next, ...currentSheetReturn() });
  const evidenceButton = (group = "door") =>
    openSheet({ kind: "evidence-choose", group });
  const chooseEvidence = (group, check = null) => {
    if (busyRef.current || sendingRef.current) return;
    const section = evidenceSectionFor(checklist, group);
    if (group === "glass" && !isEvidenceSectionApplicable(section)) return;
    if (group === "locks" && !check?.id) {
      if (!isEvidenceSectionApplicable(section)) return;
      check =
        section.checks.find(
          (item) =>
            item.id === LOCK_CONFIGURATION_CHECK && item.status === "fail"
        ) ||
        section.checks.find((item) => item.status === "fail") ||
        null;
    }
    const requirement = mediaRequirementFor(group, check?.id || "");
    if (requirement) {
      openSheet({
        kind: "evidence",
        group,
        initialType: requirement.type,
        checkId: requirement.checkId,
      });
      return;
    }
    if (
      !check?.id &&
      canAddSectionEvidence(section) &&
      (group === "closer" || group === "smoke_control")
    ) {
      openSheet({ kind: "evidence-options", group });
      return;
    }
    openSheet({
      kind: "evidence",
      group,
      initialType: "photo",
      checkId: check?.id || null,
    });
  };
  const openEvidenceFiles = (group, initialType = "photo", check = null) => {
    const requirement = mediaRequirementFor(group, check?.id || "");
    openSheet({
      kind: "evidence",
      group,
      initialType: requirement?.type || initialType,
      checkId: requirement?.checkId || check?.id || null,
    });
  };
  const openGallery = () => setSheet({ kind: "gallery" });
  const viewPhoto = (key) => setSheet({ kind: "photo", key });
  const resetForNext = () => {
    if (
      !dirty ||
      window.confirm("Start another scan and discard the unsaved changes?")
    )
      reset();
  };
  return (
    <div
      ref={viewportRef}
      className={`ds-viewport ds-native-viewport ${className || ""}`}
      style={style}
    >
      <div
        className="door-scan ds-native ds-production"
        data-tab={tab}
        data-camera-open={Boolean(cameraSide)}
        aria-busy={busy}
      >
        <main className="ds-main">
          {tab === "scan" ? (
            <section
              className="ds-scan-panel ds-native-scan"
              aria-label="Capture a door"
            >
              <div className="ds-hero">
                <NfcArt />
                <h1>
                  Your door. <br />
                  Clearly captured.
                </h1>
              </div>

              <div className="ds-capture-zone">
                <CaptureWheel
                  activeKey={activeSide}
                  photos={photos}
                  hingeCount={hingeCount}
                  certificateDecision={certificateDecision}
                  disabled={busy}
                  onSelect={selectCapture}
                  onAction={() => openCamera(activeSide)}
                  actionLabel={`Take ${labelFor(
                    activeSide
                  ).toLowerCase()} photo`}
                />
                <h2 className="ds-camera-hint">
                  {/* {photos[activeSide] ? "Retake" : "Take"}{" "}
                  {labelFor(activeSide).toLowerCase()} */}
                </h2>
                <p className="ds-capture-helper">
                  {GUIDE_COPY[activeSide]?.text}
                </p>
                {HINGES.includes(activeSide) && (
                  <>
                    <div
                      className="ds-hinge-count"
                      role="group"
                      aria-label="Number of hinges"
                    >
                      {[2, 3].map((count) => (
                        <button
                          type="button"
                          key={count}
                          disabled={busy}
                          aria-pressed={hingeCount === count}
                          onClick={() => chooseHingeCount(count)}
                        >
                          {count} hinges
                        </button>
                      ))}
                    </div>
                    <div
                      className="ds-hinge-select"
                      role="group"
                      aria-label="Choose hinge photo"
                    >
                      {hingePhotoKeys(hingeCount).map((key) => (
                        <button
                          type="button"
                          key={key}
                          disabled={busy}
                          aria-pressed={activeSide === key}
                          onClick={() => selectCapture(key)}
                        >
                          {labelFor(key)}
                          {photos[key] ? " / captured" : ""}
                        </button>
                      ))}
                    </div>
                    <p className="ds-muted">
                      One photo per hinge. All selected hinge images use the
                      hinge/screw model.
                    </p>
                  </>
                )}
                {activeSide === "certificate" && (
                  <p className="ds-original-note">
                    Original photo only. No AI analysis.
                  </p>
                )}
                <div className="ds-capture-links">
                  <button
                    type="button"
                    className="ds-text-button"
                    disabled={busy}
                    onClick={() => openDeviceUpload(activeSide)}
                  >
                    <FontAwesomeIcon icon={faImage} /> Choose from device
                  </button>
                  <button
                    type="button"
                    className="ds-text-button"
                    disabled={busy}
                    onClick={() => setSheet({ kind: "guide" })}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} /> Photo help
                  </button>
                </div>
              </div>
              {/* <label className="ds-scan-name">
                Door name <span className="ds-optional">Optional</span>
                <input
                  maxLength={120}
                  value={scanName}
                  disabled={busy}
                  placeholder="For example, Flat 12 entrance"
                  onChange={(event) => {
                    setScanName(event.target.value);
                    change();
                  }}
                />
              </label> */}
              <div className="ds-save-actions">
                <button
                  type="button"
                  className="ds-button ds-button-primary"
                  disabled={
                    !frontBackReady || busy || Boolean(authorizationMessage)
                  }
                  onClick={() => {
                    if (currentScan && !dirty && !hasFailedAnalysis)
                      setTab("evidence");
                    else saveCurrent();
                  }}
                >
                  {busy ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />{" "}
                      {stage || "Please wait..."}
                    </>
                  ) : currentScan && !dirty && !hasFailedAnalysis ? (
                    "View this scan"
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCloudUploadAlt} />{" "}
                      {currentScan
                        ? "Process & upload changes"
                        : "Process & upload scan"}
                    </>
                  )}
                </button>
                <p className="ds-tiny">
                  {frontBackReady
                    ? "Front and back are ready. You can save now; other evidence is optional."
                    : "The front and back photos are needed before processing or uploading."}
                </p>
              </div>

              {totalCount > 0 && (
                <button
                  type="button"
                  className="ds-text-button ds-next-scan"
                  disabled={busy}
                  onClick={resetForNext}
                >
                  Scan another door
                </button>
              )}
            </section>
          ) : tab === "users" && isAdminUser ? (
            <AdminUsersPanel
              services={services}
              busy={busy}
              adminRole={currentUserRole}
            />
          ) : tab === "results" ? (
            <HistoryPanel
              scans={scans}
              uid={uid}
              busy={busy}
              onRefresh={() => loadScans(false)}
              onMore={() => loadScans(true)}
              onOpen={openSavedScan}
              onNew={resetForNext}
            />
          ) : totalCount ? (
            <ResultsView
              pdf={pdf}
              key={currentScan?.id || sessionIdRef.current || "draft"}
              sources={sources}
              predictions={results}
              extras={extras}
              statuses={statuses}
              savedRecord={currentScan}
              checklist={checklist}
              observations={observations}
              busy={busy}
              dirty={dirty}
              retryAnalysis={hasFailedAnalysis}
              blocked={Boolean(sheet || cameraSide)}
              onPhotos={openGallery}
              onEvidence={chooseEvidence}
              onReview={() => setSheet({ kind: "final" })}
              onSave={saveCurrent}
              onInfo={() => setSheet({ kind: "result-info" })}
            />
          ) : (
            <div className="ds-empty ds-results-empty">
              <UiIcon name="evidence" />
              <strong>Your scan will appear here</strong>
              <p>
                Take the front and back photos, then process and upload your
                scan.
              </p>
              <button
                type="button"
                className="ds-button ds-button-primary"
                onClick={() => setTab("scan")}
              >
                Take photos
              </button>
            </div>
          )}
        </main>

        {(error || authorizationMessage) && (
          <div className="ds-operation-status is-error" role="alert">
            {error || authorizationMessage}
          </div>
        )}
        {busy && (
          <div className="ds-operation-status" role="status" aria-live="polite">
            <span>{stage || "Working on your scan..."}</span>
            <progress max="100" value={progress} aria-label="Scan progress" />
          </div>
        )}
        <nav
          className="ds-bottom-tabs"
          role="tablist"
          aria-label="DoorScan navigation"
          onKeyDown={(event) => {
            if (
              !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key) ||
              busy
            )
              return;
            event.preventDefault();
            const tabs = navigationTabs.map(([key]) => key);
            const currentIndex = Math.max(0, tabs.indexOf(tab));
            const next =
              event.key === "Home"
                ? tabs[0]
                : event.key === "End"
                ? tabs[tabs.length - 1]
                : tabs[
                    (currentIndex +
                      (event.key === "ArrowRight" ? 1 : tabs.length - 1)) %
                      tabs.length
                  ];
            changeTab(next);
            event.currentTarget.querySelector(`[data-tab="${next}"]`)?.focus();
          }}
          data-tab-count={navigationTabs.length}
        >
          {navigationTabs.map(([key, label, icon]) => (
            <button
              type="button"
              role="tab"
              data-tab={key}
              key={key}
              aria-selected={tab === key}
              tabIndex={tab === key ? 0 : -1}
              disabled={busy}
              onClick={() => changeTab(key)}
            >
              <FontAwesomeIcon icon={icon} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <input
          ref={deviceInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={selectDeviceFile}
        />
        {cameraSide && (
          <CameraWindow
            side={cameraSide}
            entryRect={cameraEntryRect}
            getReturnRect={() =>
              viewportRef.current
                ?.querySelector(".ds-native-scan .ds-solid-gear")
                ?.getBoundingClientRect()
            }
            nextSide={nextCaptureKey(
              { ...photos, [cameraSide]: true },
              hingeCount,
              certificateDecision
            )}
            initialPhoto={cameraInitialPhoto}
            initialMetadata={cameraInitialMetadata}
            photos={photos}
            hingeCount={hingeCount}
            certificateDecision={certificateDecision}
            onAccept={acceptPhoto}
            onClose={closeCamera}
            onSelectSide={selectCapture}
            onHingeCount={() => setSheet({ kind: "hinges" })}
            onCertificateChoice={() => setSheet({ kind: "certificate" })}
          />
        )}
        {sheet?.kind === "photo" && (
          <PhotoDetail
            side={sheet.key}
            sources={sources}
            originalSources={originalSources}
            predictions={results}
            statuses={statuses}
            extras={extras}
            busy={busy}
            onRetake={openCamera}
            onUpload={openDeviceUpload}
            onClose={closeSheet}
          />
        )}
        {sheet?.kind === "guide" && (
          <Sheet title="Photo help" onClose={closeSheet}>
            <CaptureGuide
              side={activeSide}
              onContinue={() => openCamera(activeSide)}
            />
          </Sheet>
        )}
        {sheet?.kind === "hinges" && (
          <Sheet title="How many hinges?" onClose={closeSheet}>
            <div className="ds-info-body">
              <GuideArt side="hinge_top" />
              <p>
                Count the fitted hinges, not the screws. Take one clear photo of
                each hinge.
              </p>
              {[2, 3].map((count) => (
                <button
                  type="button"
                  className="ds-button ds-button-secondary"
                  key={count}
                  onClick={() => chooseHingeCount(count)}
                >
                  {count} hinges
                </button>
              ))}
              <p className="ds-muted">
                Missing photos do not mean missing hinges. Only collect evidence
                you can reach safely.
              </p>
            </div>
          </Sheet>
        )}
        {sheet?.kind === "certificate" && (
          <Sheet title="Certification photo" onClose={closeSheet}>
            <div className="ds-info-body">
              <GuideArt side="certificate" />
              <p>
                Keep the label readable. The original is saved for review, not
                sent to AI. This photo is optional for the initial scan.
              </p>
              {Object.entries(CERTIFICATE_REASONS).map(([key, label]) => (
                <button
                  type="button"
                  className="ds-button ds-button-secondary"
                  disabled={Boolean(photos.certificate)}
                  key={key}
                  onClick={() => chooseCertificateReason(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </Sheet>
        )}
        {sheet?.kind === "evidence-choose" && (
          <Sheet title="What would you like to show?" onClose={closeSheet}>
            <EvidenceChooser
              selected={sheet.group}
              busy={busy}
              checklist={checklist}
              onChoose={chooseEvidence}
              onBack={() => setSheet({ kind: "final" })}
            />
          </Sheet>
        )}
        {sheet?.kind === "identify" && (
          <Sheet title="Identify the door" onClose={closeSheet}>
            <div className="ds-info-body ds-identify-door">
              <EvidenceGuideArt group="certificate" />
              {sheet.group === "door" && (
                <SectionQuestions
                  group="door"
                  observations={observations}
                  busy={busy}
                  onAnswer={answerQuestion}
                />
              )}
              <p>
                Start with information that already exists. No tools or
                dismantling.
              </p>
              <button
                type="button"
                className="ds-button ds-button-secondary"
                disabled={busy}
                onClick={() => openCamera("certificate", true)}
              >
                Add a label photo
              </button>
              <button
                type="button"
                className="ds-button ds-button-secondary"
                disabled={busy}
                onClick={() => openEvidenceFiles("certificate", "document")}
              >
                Add an existing document
              </button>
              {sheet.group === "door" && (
                <button
                  type="button"
                  className="ds-button ds-button-secondary"
                  disabled={busy}
                  onClick={() => openEvidenceFiles("door")}
                >
                  Add another door photo
                </button>
              )}
              <button
                type="button"
                className="ds-button ds-button-secondary"
                disabled={busy}
                onClick={closeSheet}
              >
                Back to my door
              </button>
            </div>
          </Sheet>
        )}
        {sheet?.kind === "evidence-options" && (
          <Sheet
            title={
              EVIDENCE_CHOICES.find(([key]) => key === sheet.group)?.[1] ||
              "Add evidence"
            }
            onClose={closeSheet}
          >
            <SectionEvidencePanel
              group={sheet.group}
              observations={observations}
              extras={extras}
              statuses={statuses}
              busy={busy}
              onAnswer={answerQuestion}
              onFiles={openEvidenceFiles}
              onRecord={(group, check) =>
                openSheet({
                  kind: "record-video",
                  group,
                  checkId:
                    check?.id || mediaRequirementFor(group)?.checkId || null,
                })
              }
              onHinge={() =>
                openCamera(
                  hingePhotoKeys(hingeCount).find((key) => !photos[key]) ||
                    "hinge_top",
                  true
                )
              }
              onDifferent={() => evidenceButton(sheet.group)}
              checklist={checklist}
              sources={sources}
            />
          </Sheet>
        )}
        {sheet?.kind === "evidence" && (
          <Sheet
            title={
              mediaRequirementFor(sheet.group, sheet.checkId || "")?.label ||
              (photoEvidenceKeysFor(
                sheet.group,
                { sources, predictions: results, extras, observations },
                sheet.checkId || ""
              ).length
                ? QUESTION_IDS.has(sheet.checkId)
                  ? "Review answer"
                  : "Evidence provided"
                : "Add evidence")
            }
            onClose={closeSheet}
          >
            <EvidenceForm
              key={`${sheet.group}-${sheet.initialType}-${
                sheet.checkId || "group"
              }`}
              group={sheet.group}
              initialType={sheet.initialType || "photo"}
              check={
                checklist
                  .find((section) => section.id === sheet.group)
                  ?.checks.find((check) => check.id === sheet.checkId) || null
              }
              observations={observations}
              onAnswer={answerQuestion}
              extras={extras}
              statuses={statuses}
              sources={sources}
              predictions={results}
              busy={busy}
              onSave={addEvidence}
              onRecord={(group, checkId) =>
                openSheet({ kind: "record-video", group, checkId })
              }
              onClose={closeSheet}
            />
          </Sheet>
        )}
        {sheet?.kind === "gallery" && (
          <Sheet title="Your photos & evidence" onClose={closeSheet}>
            <EvidenceGallery
              sources={sources}
              photos={photos}
              extras={extras}
              statuses={statuses}
              savedRecord={currentScan}
              hingeCount={hingeCount}
              busy={busy}
              onView={viewPhoto}
              onCapture={openCamera}
              onAdd={() => evidenceButton()}
              onRemove={removeExtra}
            />
            {dirty && (
              <button
                type="button"
                className="ds-button ds-button-primary"
                disabled={busy || !frontBackReady}
                onClick={async () => {
                  const saved = await saveCurrent();
                  if (saved) setSheet(null);
                }}
              >
                Process &amp; upload changes
              </button>
            )}
          </Sheet>
        )}
        {sheet?.kind === "result-info" && (
          <Sheet title="About your scan" onClose={closeSheet}>
            <div className="ds-info-body">
              <p>
                Labels show the parts returned by the model. No confidence
                percentages or automatic fire-safety decisions are displayed.
              </p>
              <p>
                Tap Tour to walk through every available annotation, including
                hinge photos. Tour zooms into each part and highlights it
                without enlarging the text. Pinch, drag, use the zoom buttons or
                Fit to explore; manual interaction pauses the tour. Front, Back
                and Photos let you switch evidence.
              </p>
              <p>
                Use Add evidence to attach photos, a closing video or an
                existing record. Certification is stored as original evidence
                and is not sent to the model.
              </p>
            </div>
          </Sheet>
        )}
        {sheet?.kind === "final" && (
          <Sheet
            title="Final results"
            onClose={closeSheet}
            initialScrollTop={sheet.scrollTop || 0}
          >
            {error && (
              <p className="ds-inline-error" role="alert">
                {error}
              </p>
            )}
            <FinalResults
              sections={checklist}
              dirty={dirty}
              busy={busy}
              receipt={receipt}
              onEvidence={chooseEvidence}
              onContinue={() => setSheet({ kind: "review" })}
            />
          </Sheet>
        )}
        {sheet?.kind === "record-video" && (
          <Sheet
            title={
              sheet.group === "closer"
                ? "Record closing video"
                : "Record evidence video"
            }
            onClose={closeSheet}
          >
            <VideoEvidenceRecorder
              key={sheet.group}
              group={sheet.group}
              checkId={
                sheet.checkId ||
                (sheet.group === "closer" ? CLOSING_VIDEO_CHECK : null)
              }
              busy={busy}
              onSave={addEvidence}
              onCancel={() =>
                chooseEvidence(sheet.group, {
                  id: sheet.checkId || CLOSING_VIDEO_CHECK,
                })
              }
              onUpload={() =>
                openEvidenceFiles(sheet.group, "video", {
                  id: sheet.checkId || CLOSING_VIDEO_CHECK,
                })
              }
            />
          </Sheet>
        )}
        {sheet?.kind === "review" && (
          <Sheet title="Submit for review" onClose={closeSheet}>
            <button
              type="button"
              className="ds-button ds-button-secondary ds-review-add-evidence"
              disabled={busy}
              onClick={() => evidenceButton()}
            >
              Add evidence
            </button>
            <ReviewForm
              user={user}
              contact={contact}
              onContactChange={editContact}
              record={currentScan || { doorName: scanName }}
              uploadedCount={uploadedCount}
              totalCount={totalCount}
              busy={busy}
              canSave={frontBackReady}
              dirty={dirty}
              receipt={receipt}
              error={error}
              onSend={sendReview}
              externalReview={typeof submitReview === "function"}
              checklist={checklist}
              onSummary={() => setSheet({ kind: "final" })}
            />
          </Sheet>
        )}
      </div>
    </div>
  );
}
const DoorScan = ({
  doorId,
  doorName,
  predictionUrl = PREDICT_URL,
  hingePredictionUrl,
  transformHingeResponse,
  firebaseApp,
  firestoreInstance,
  storageInstance,
  authInstance,
  className = "",
  style,
  allowGuestScans = true,
  submitReview,
  checklistPolicy = DEFAULT_CHECKLIST_POLICY,
  pdfOptions,
}) => {
  const connection = useMemo(() => {
    try {
      const app = firebaseApp || getApp();
      return {
        services: {
          app,
          auth: authInstance || getAuth(app),
          db: firestoreInstance || getFirestore(app),
          storage: storageInstance || getStorage(app),
        },
        error: "",
      };
    } catch (_) {
      return {
        services: null,
        error:
          "Initialize your existing Firebase app before rendering DoorScan.",
      };
    }
  }, [firebaseApp, firestoreInstance, storageInstance, authInstance]);
  const [session, setSession] = useState({
    user: null,
    loading: true,
    error: "",
  });
  useEffect(() => {
    if (!connection.services) {
      setSession({ user: null, loading: false, error: "" });
      return undefined;
    }
    setSession({ user: null, loading: true, error: "" });
    return onAuthStateChanged(
      connection.services.auth,
      (user) => setSession({ user, loading: false, error: "" }),
      () =>
        setSession({
          user: null,
          loading: false,
          error: "Unable to read your sign-in session.",
        })
    );
  }, [connection]);
  const hingeUrl = hingePredictionUrl || hingeEndpoint(predictionUrl);
  return (
    <DoorScanSession
      key={`${doorId ?? "new"}:${predictionUrl}:${hingeUrl}`}
      doorId={doorId}
      doorName={doorName}
      predictionUrl={predictionUrl}
      hingePredictionUrl={hingeUrl}
      transformHingeResponse={transformHingeResponse}
      services={connection.services}
      user={session.user}
      authLoading={session.loading}
      setupError={connection.error || session.error}
      allowGuestScans={allowGuestScans}
      className={className}
      style={style}
      submitReview={submitReview}
      checklistPolicy={checklistPolicy}
      pdfOptions={pdfOptions}
    />
  );
};
export default DoorScan;
